import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { SUPPORTED_CURRENCIES, setStoreCurrency, formatMinor, normalizeCurrency, currencySymbol } from '../utils/money';
import { DEFAULT_LANGUAGE, LANGUAGE_CODES, LANGUAGE_REGISTRY } from '../utils/i18nRegistry';
// The ticket-number reset zone is its own field, but it is pre-filled from the zone the
// tenant has already established elsewhere so an India store does not land on the Eastern
// day boundary by default. Same resolver the queue and the print builders use.
import { resolveTenantTimezone } from '../utils/tenantTime';
// POS print paper sizes. The allowlist is shared with the print builder and both backend
// handlers, so the option offered here is exactly the set that will be honoured.
import { DEFAULT_PAPER_SIZE, PAPER_SIZES, isRollPaper, normalizePaperSize } from '../utils/paperSize';
// Network printer registry (pos-network-printing Layer 2). The admin defines the
// restaurant's printers here; the mobile app routes each document to one by role.
import {
  DEFAULT_PRINTER_PORT,
  MAX_PRINTERS,
  PRINTER_ROLES,
  isValidIpv4,
  normalizePrinters,
} from '../utils/printerRegistry';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// PriceInput component for better UX when entering prices
const PriceInput = ({ value, onChange, disabled = false, placeholder = '0.00' }) => {
  const [localValue, setLocalValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  // Initialize local value from cents prop
  React.useEffect(() => {
    if (!isFocused) {
      const cents = Number(value) || 0;
      const dollars = (cents / 100).toFixed(2);
      setLocalValue(dollars);
    }
  }, [value, isFocused]);

  const handleChange = (e) => {
    const text = e.target.value;
    // Just allow the user to type - no restrictions while typing
    setLocalValue(text);
    
    // Also try to save immediately if it's a valid number
    // This ensures the value is saved even if blur doesn't fire
    const cleaned = text.replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned);
    if (!isNaN(dollars) && isFinite(dollars)) {
      const cents = Math.round(dollars * 100);
      onChange(cents);
    } else if (cleaned === '' || cleaned === '0') {
      onChange(0);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    
    // Convert to cents and update parent (final cleanup)
    const cleaned = localValue.replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned || '0');
    
    if (isNaN(dollars) || !isFinite(dollars)) {
      onChange(0);
      return;
    }
    
    const cents = Math.round(dollars * 100);
    onChange(cents);
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    // Select all text on focus for easy editing
    e.target.select();
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
};

// Timezone choices offered by this screen, lifted verbatim from the two existing inline
// lists (scheduled closures and service windows) so a tenant is never offered a
// different set of zones depending on which control they are looking at.
//
// Those two sections still hold their own copies. Left alone deliberately: they are
// working, customer-visible scheduling controls, and rewriting them to consume this
// constant would put a live feature at risk for a tidiness gain unrelated to ticket
// numbering. Worth collapsing the three into one the next time that area is touched.
const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
  { value: 'Asia/Kolkata', label: 'India (Asia/Kolkata, IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia/Sydney, AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (Australia/Melbourne)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (Australia/Brisbane, no DST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (Australia/Adelaide)' },
  { value: 'Australia/Perth', label: 'Perth (Australia/Perth, AWST)' },
  { value: 'Etc/GMT', label: 'GMT (Etc/GMT)' },
];

const centsToDollars = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return (n / 100).toFixed(2);
};

const dollarsToCents = (dollars) => {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

const fallbackDefaults = {
  storeMode: '',
  posProvider: 'SQUARE',
  currency: 'USD',
  // India GST (india-globalization). Defaults keep every store on US_SALES.
  taxRegime: 'US_SALES',
  gst: {
    gstin: '',
    legalName: '',
    storeState: '',
    defaultRatePct: 5,
    pricingMode: 'TAX_EXCLUSIVE',
    fssai: '',
  },
  reports: {
    enabledReportTypes: ['orders', 'labels', 'users', 'fulfillment'],
  },
  posPayments: {
    online_enabled: true,
    in_store_enabled: true,
  },
  posPrinting: {
    autoPrintNewOrders: false,
    autoPrintReceiptOnPayment: false,
    // Paper size for POS prints. Allowed values live in utils/paperSize.js:
    // 'half_letter' (5.5x8.5), 'a4', or 'thermal_80mm' (80 mm receipt roll). Replaces the
    // old orderDetailsOnly boolean, which conflated content-type with paper size.
    paperSize: DEFAULT_PAPER_SIZE,
    // Network printer registry (pos-network-printing Layer 2). Empty by default; an empty
    // list is the "no network printing configured" state that every consumer treats as
    // "behave exactly as before". Each entry: { id, name, ip, port, role }.
    printers: [],
  },
  posSla: {
    enabled: false,
    greenMinutes: 5,
    orangeMinutes: 10,
    redMinutes: 15,
  },
  // In-store ticket/token number for staff Take Order tickets. OFF by default: while it
  // is off, in-store orders keep showing the existing order number everywhere, which is
  // the whole non-regression contract. `timezone` is the daily-reset boundary only and
  // is deliberately separate from the serviceWindows / orderingClosedHours zones.
  storeTicketNumber: {
    enabled: false,
    startNumber: 1,
    dailyReset: false,
    timezone: '',
  },
  orderMessages: {
    pickupMessage: '',
    deliveryMessage: '',
  },
  posTips: {
    enabled: false,
    presetPercentages: [15, 18, 20, 25],
    allowCustom: true,
    allowNoTip: true,
    calculateOn: 'subtotal',
    defaultPercentage: null,
    displayMode: 'inline_collapsed',
  },
  // Scheduled pickup/delivery (scheduled-orders spec). Default OFF: an untouched tenant
  // keeps today's ASAP-only ordering. No timezone field — scheduling uses the resolved
  // tenant zone, shown read-only below.
  scheduledOrders: {
    enabled: false,
    maxCutoffHours: 72,
    minLeadMinutes: 30,
    posReleaseMinutes: 15,
    slotIntervalMinutes: 15,
    allowPickup: true,
    allowDelivery: true,
  },
  posDeliveryLinks: {
    enabled: false,
    vendors: [],
  },
  delivery: {
    enabled: false,
    fee_cents: 0,
    minimum_order_cents: 0,
    radius_restriction: {
      enabled: false,
      base_zip_code: '',
      max_radius_miles: 10.0,
      max_radius_km: 5.0,
    },
    uber_direct: {
      enabled: false,
      mock: false,
      environment: 'sandbox',
      client_id: '',
      customer_id: '',
      has_secret: false,
      pricing_rule: {
        mode: 'pass_through',
        flat_cents: 0,
        markup_cents: 0,
        markup_percent: 0,
        subsidized_customer_cents: 0,
      },
    },
    // Borzo India courier (mirrors the backend `delivery.borzo` default shape).
    // Off by default; only surfaced for INR tenants. The auth token and webhook
    // signing secret are write-only — the API returns a mask plus the
    // `has_token` / `has_webhook_secret` flags instead of the stored values.
    borzo: {
      enabled: false,
      mock: false,
      environment: 'sandbox',
      auth_token: '',
      has_token: false,
      webhook_signing_secret: '',
      has_webhook_secret: false,
      pickup_address: {
        name: '',
        phone: '',
        street: '',
        apt_unit: '',
        city: '',
        // Full Indian state name (e.g., "Tamil Nadu"), not a 2-char code.
        state: '',
        // 6-digit PIN code.
        zip: '',
      },
      pricing_rule: {
        mode: 'pass_through',
        flat_cents: 0,
        markup_cents: 0,
        markup_percent: 0,
        subsidized_customer_cents: 0,
      },
    },
  },
  addressAutocomplete: {
    provider: 'geoapify',
    enabled: false,
    apiKey: '',
  },
  otter: {
    enabled: false,
    api_key: '',
    api_secret: '',
    store_id: '',
    webhook_url: '',
    menu_sync_enabled: false,
    menu_sync_mode: 'manual',
    last_menu_sync_timestamp: null,
  },
  // Delivery Integrations enablement section (additive). Groups one toggle per
  // delivery provider (Uber Eats, Otter, ...). Mirrors the backend
  // `delivery_integrations` shape. Every toggle defaults to OFF.
  delivery_integrations: {
    ubereats: {
      enabled: false,
      environment: 'sandbox',
      client_id: '',
      // Write-only: the stored secret is never returned by the API. The GET
      // response sends a mask sentinel + `has_secret` flag instead.
      client_secret: '',
      has_secret: false,
      store_id: '',
      last_menu_sync_timestamp: null,
    },
    doordash: {
      enabled: false,
      environment: 'sandbox',
      developer_id: '',
      key_id: '',
      // Write-only: the stored signing secret is never returned by the API. The
      // GET response sends a mask sentinel + `has_secret` flag instead.
      signing_secret: '',
      has_secret: false,
      store_id: '',
      last_menu_sync_timestamp: null,
    },
    otter: {
      enabled: false,
    },
  },
  orderingClosedHours: {
    enabled: false,
    timezone: 'America/New_York',
    message: '',
    oneOff: [],
    weekly: [],
  },
  serviceWindows: {
    enabled: false,
    timezone: 'America/New_York',
    windows: [],
  },
  shipping: {
    standard: { enabled: true, label: 'Standard Shipping', amount_cents: 499, min_days: 3, max_days: 5, currency: 'usd' },
    express: { enabled: false, label: 'Express Shipping', amount_cents: 999, min_days: 1, max_days: 2, currency: 'usd' },
    free: { enabled: true, label: 'Free Shipping', threshold_cents: 2000, amount_cents: 0, min_days: 5, max_days: 8, currency: 'usd' },
    allowed_countries: ['US'],
  },
  tax: { stripe_automatic_tax_enabled: false },
  features: {
    mock_online_payment_enabled: false,
    mock_upi_qr_enabled: false,
    pos_discount_codes_enabled: false,
    chatbot_enabled: false,
  },
  optional_sections: {
    crochet_features: {
      enabled: false,
      title: 'Menu features',
      html:
        '<div class="crochet-features">' +
        '<ul class="feature-bullets">' +
        '<li><strong>Freshly prepared:</strong> made to order with quality ingredients.</li>' +
        '<li><strong>Fast ordering:</strong> browse the menu, search items, and order in seconds.</li>' +
        '<li><strong>Pickup-friendly:</strong> ideal for takeout and busy schedules.</li>' +
        '<li><strong>Seasonal specials:</strong> rotating favorites and limited-time dishes.</li>' +
        '</ul>' +
        '<p><a class="feature-cta" href="/pos">View the menu</a></p>' +
        '</div>',
    },
    events: { enabled: false, title: 'Events', html: '' },
    reviews: { enabled: false, title: 'Reviews', auto_approve: false, double_opt_in: true },
  },
};

// Otter Analytics Section Component
const OtterAnalyticsSection = ({ tenantId }) => {
  const [analytics, setAnalytics] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [dateRange, setDateRange] = React.useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    end_date: new Date().toISOString().split('T')[0] // today
  });

  const fetchAnalytics = async () => {
    if (!tenantId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      
      const params = new URLSearchParams({
        tenant_id: tenantId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });
      
      const response = await fetch(`${API_BASE_URL}/admin/otter/analytics?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.statusText}`);
      }
      
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching Otter analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    if (!tenantId) return;
    
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      
      const params = new URLSearchParams({
        tenant_id: tenantId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        export: 'csv'
      });
      
      const response = await fetch(`${API_BASE_URL}/admin/otter/analytics?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to export analytics');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `otter_analytics_${dateRange.start_date}_to_${dateRange.end_date}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting analytics:', err);
      setError(err.message || 'Failed to export analytics');
    }
  };

  React.useEffect(() => {
    fetchAnalytics();
  }, [tenantId]);

  const formatCurrency = (cents) => {
    return formatMinor(Number(cents) || 0);
  };

  return (
    <div style={{ marginTop: 24, padding: 16, background: 'rgba(249, 250, 251, 1)', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#4c1d95' }}>📊 Otter Analytics</h3>
        <button
          type="button"
          onClick={exportCSV}
          disabled={loading || !analytics}
          style={{
            padding: '6px 12px',
            background: '#4c1d95',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: loading || !analytics ? 'not-allowed' : 'pointer',
            opacity: loading || !analytics ? 0.5 : 1,
            fontSize: '0.9rem'
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Date Range Selector */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9rem', fontWeight: 600 }}>Start Date</label>
          <input
            type="date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))}
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9rem', fontWeight: 600 }}>End Date</label>
          <input
            type="date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))}
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
          />
        </div>
        <button
          type="button"
          onClick={fetchAnalytics}
          disabled={loading}
          style={{
            marginTop: 24,
            padding: '8px 16px',
            background: '#059669',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1
          }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.25)', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: '#dc2626' }}>Error: {error}</div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 24, color: 'rgba(15, 23, 42, 0.6)' }}>
          Loading analytics...
        </div>
      )}

      {!loading && analytics && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ padding: 16, background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)', marginBottom: 4 }}>Total Orders</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#4c1d95' }}>{analytics.total_orders}</div>
            </div>
            <div style={{ padding: 16, background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)', marginBottom: 4 }}>Total Revenue</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#059669' }}>{formatCurrency(analytics.total_revenue)}</div>
            </div>
            <div style={{ padding: 16, background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)', marginBottom: 4 }}>Avg Order Value</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#0284c7' }}>{formatCurrency(analytics.average_order_value)}</div>
            </div>
          </div>

          {/* Orders by Channel */}
          {Object.keys(analytics.orders_by_channel || {}).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>Orders by Delivery Channel</h4>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'rgba(249, 250, 251, 1)', borderBottom: '1px solid rgba(148, 163, 184, 0.25)' }}>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: '0.9rem', fontWeight: 600 }}>Channel</th>
                      <th style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem', fontWeight: 600 }}>Orders</th>
                      <th style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem', fontWeight: 600 }}>Revenue</th>
                      <th style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem', fontWeight: 600 }}>Avg Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(analytics.orders_by_channel).map(([channel, data]) => (
                      <tr key={channel} style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.15)' }}>
                        <td style={{ padding: 12, fontSize: '0.9rem' }}>{channel}</td>
                        <td style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem' }}>{data.count}</td>
                        <td style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(data.revenue)}</td>
                        <td style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(data.average_order_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Orders by Status */}
          {Object.keys(analytics.orders_by_status || {}).length > 0 && (
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>Orders by Status</h4>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)', padding: 12 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(analytics.orders_by_status).map(([status, count]) => (
                    <div key={status} style={{ padding: '6px 12px', background: 'rgba(249, 250, 251, 1)', borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)' }}>{status}: </span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default function GeneralSettings() {
  const navigate = useNavigate();

  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [source, setSource] = useState('legacy');

  const [form, setForm] = useState(() => fallbackDefaults);

  // Write-only Uber Eats client secret. We NEVER prefill the stored secret; the
  // admin only types here to set/replace it. An empty string on save means
  // "leave the stored secret unchanged".
  const [ubereatsSecretInput, setUbereatsSecretInput] = useState('');

  // Inline Uber Eats Test Connection result rendered next to the card buttons
  // (in addition to the page-level banner) so the outcome is visible without
  // scrolling. Shape: { ok: boolean, message: string } | null.
  const [ueTestResult, setUeTestResult] = useState(null);

  // Optional selective Uber Eats menu sync: a comma-separated list of Product
  // IDs. When empty, "Sync Menu Now" syncs the full catalog (existing default).
  const [ueSyncProductIds, setUeSyncProductIds] = useState('');

  // Write-only DoorDash signing secret. We NEVER prefill the stored secret; the
  // admin only types here to set/replace it. An empty string on save means
  // "leave the stored secret unchanged".
  const [doordashSecretInput, setDoordashSecretInput] = useState('');

  // Write-only Uber Direct client secret. We NEVER prefill the stored secret;
  // the admin only types here to set/replace it. An empty string on save means
  // "leave the stored secret unchanged".
  const [uberDirectSecretInput, setUberDirectSecretInput] = useState('');
  // Write-only Uber Direct webhook signing secret (same handling as above).
  const [uberDirectWebhookSecretInput, setUberDirectWebhookSecretInput] = useState('');

  // Write-only Borzo auth token. We NEVER prefill the stored token; the admin
  // only types here to set/replace it. An empty string on save means "leave the
  // stored token unchanged" (the field is omitted from the write request).
  const [borzoTokenInput, setBorzoTokenInput] = useState('');
  // Write-only Borzo webhook signing secret (same handling as above).
  const [borzoWebhookSecretInput, setBorzoWebhookSecretInput] = useState('');
  // Inline Borzo connection-test result. Shape: { ok: boolean|null, message: string } | null.
  // Never holds a credential value — only the provider's success/failure text.
  const [borzoTestResult, setBorzoTestResult] = useState(null);

  // Shift4 (shift4-payment-integration) connect form state. Typed credentials live ONLY in
  // local state and are sent to /admin/pos/connect/shift4 (stored in Secrets Manager by the
  // backend); they are NEVER written into `form`/settings. `shift4ConnectResult` holds only
  // success/failure text, never a credential. All isolated — rendered only for a SHIFT4 tenant.
  const [shift4ClientGuidInput, setShift4ClientGuidInput] = useState('');
  const [shift4AuthTokenInput, setShift4AuthTokenInput] = useState('');
  const [shift4TerminalIdInput, setShift4TerminalIdInput] = useState('');
  const [shift4ConnectResult, setShift4ConnectResult] = useState(null);

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const makeId = () => {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/-/g, '');
      }
    } catch {
      // ignore
    }
    return `${Date.now()}${Math.floor(Math.random() * 1e9)}`;
  };

  const updateOrderingClosedHours = (patch) => {
    setForm((prev) => ({
      ...prev,
      orderingClosedHours: {
        ...(fallbackDefaults.orderingClosedHours || {}),
        ...((prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {}),
        ...(patch || {}),
      },
    }));
  };

  const updateServiceWindows = (patch) => {
    setForm((prev) => ({
      ...prev,
      serviceWindows: {
        ...(fallbackDefaults.serviceWindows || {}),
        ...((prev.serviceWindows && typeof prev.serviceWindows === 'object') ? prev.serviceWindows : {}),
        ...(patch || {}),
      },
    }));
  };

  const addServiceWindowRule = () => {
    const id = makeId();
    setForm((prev) => {
      const base = (prev.serviceWindows && typeof prev.serviceWindows === 'object') ? prev.serviceWindows : {};
      const list = Array.isArray(base.windows) ? base.windows : [];
      const next = [...list, { id, enabled: true, name: '', start_hhmm: '09:00', end_hhmm: '17:00', days: ['ALL'] }];
      return {
        ...prev,
        serviceWindows: {
          ...(fallbackDefaults.serviceWindows || {}),
          ...base,
          windows: next,
        },
      };
    });
  };

  const updateServiceWindowRule = (id, patch) => {
    setForm((prev) => {
      const base = (prev.serviceWindows && typeof prev.serviceWindows === 'object') ? prev.serviceWindows : {};
      const list = Array.isArray(base.windows) ? base.windows : [];
      return {
        ...prev,
        serviceWindows: {
          ...(fallbackDefaults.serviceWindows || {}),
          ...base,
          windows: list.map((it) => (String(it?.id || '') === String(id) ? { ...it, ...patch } : it)),
        },
      };
    });
  };

  const deleteServiceWindowRule = (id) => {
    setForm((prev) => {
      const base = (prev.serviceWindows && typeof prev.serviceWindows === 'object') ? prev.serviceWindows : {};
      const list = Array.isArray(base.windows) ? base.windows : [];
      return {
        ...prev,
        serviceWindows: {
          ...(fallbackDefaults.serviceWindows || {}),
          ...base,
          windows: list.filter((it) => String(it?.id || '') !== String(id)),
        },
      };
    });
  };

  const updatePosDeliveryLinks = (patch) => {
    setForm((prev) => ({
      ...prev,
      posDeliveryLinks: {
        ...(fallbackDefaults.posDeliveryLinks || {}),
        ...((prev.posDeliveryLinks && typeof prev.posDeliveryLinks === 'object') ? prev.posDeliveryLinks : {}),
        ...(patch || {}),
      },
    }));
  };

  const addDeliveryVendor = () => {
    const id = makeId();
    setForm((prev) => {
      const base = (prev.posDeliveryLinks && typeof prev.posDeliveryLinks === 'object') ? prev.posDeliveryLinks : {};
      const vendors = Array.isArray(base.vendors) ? base.vendors : [];
      const next = [...vendors, { id, name: '', url: '', imageUrl: '' }];
      return {
        ...prev,
        posDeliveryLinks: {
          ...(fallbackDefaults.posDeliveryLinks || {}),
          ...base,
          vendors: next,
        },
      };
    });
  };

  const updateDeliveryVendor = (id, patch) => {
    setForm((prev) => {
      const base = (prev.posDeliveryLinks && typeof prev.posDeliveryLinks === 'object') ? prev.posDeliveryLinks : {};
      const vendors = Array.isArray(base.vendors) ? base.vendors : [];
      return {
        ...prev,
        posDeliveryLinks: {
          ...(fallbackDefaults.posDeliveryLinks || {}),
          ...base,
          vendors: vendors.map((it) => (String(it?.id || '') === String(id) ? { ...it, ...patch } : it)),
        },
      };
    });
  };

  const deleteDeliveryVendor = (id) => {
    setForm((prev) => {
      const base = (prev.posDeliveryLinks && typeof prev.posDeliveryLinks === 'object') ? prev.posDeliveryLinks : {};
      const vendors = Array.isArray(base.vendors) ? base.vendors : [];
      return {
        ...prev,
        posDeliveryLinks: {
          ...(fallbackDefaults.posDeliveryLinks || {}),
          ...base,
          vendors: vendors.filter((it) => String(it?.id || '') !== String(id)),
        },
      };
    });
  };

  const handleDeliveryVendorImageUpload = async (vendorId, file) => {
    setError('');
    setStatusMessage('');

    if (!file) {
      updateDeliveryVendor(vendorId, { imageUpload: undefined });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      const maxMb = (MAX_IMAGE_BYTES / (1024 * 1024)).toFixed(1);
      setError(`Image is too large. Please choose a file under ${maxMb} MB.`);
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Unsupported file type. Please upload PNG, JPG, WEBP, or SVG images.');
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      const uploadPayload = {
        data: dataUrl,
        filename: file.name,
        mimeType: file.type,
      };
      updateDeliveryVendor(vendorId, { imageUpload: uploadPayload });
    } catch (fileError) {
      console.error('Image read error:', fileError);
      setError('Failed to read selected image. Please try again.');
    }
  };

  const addWeeklyClosedRule = () => {
    const id = makeId();
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const weekly = Array.isArray(base.weekly) ? base.weekly : [];
      const next = [...weekly, { id, enabled: true, weekday: 'MON', start_hhmm: '09:00', end_hhmm: '17:00', label: '' }];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          weekly: next,
        },
      };
    });
  };

  const updateWeeklyClosedRule = (id, patch) => {
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const weekly = Array.isArray(base.weekly) ? base.weekly : [];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          weekly: weekly.map((it) => (String(it?.id || '') === String(id) ? { ...it, ...patch } : it)),
        },
      };
    });
  };

  const deleteWeeklyClosedRule = (id) => {
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const weekly = Array.isArray(base.weekly) ? base.weekly : [];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          weekly: weekly.filter((it) => String(it?.id || '') !== String(id)),
        },
      };
    });
  };

  const addOneOffClosedRule = () => {
    const id = makeId();
    const now = new Date();
    const start = new Date(now.getTime() + 60 * 60 * 1000);
    const end = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    const toLocalInput = (dt) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    };

    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const oneOff = Array.isArray(base.oneOff) ? base.oneOff : [];
      const next = [...oneOff, { id, enabled: true, start_iso: toLocalInput(start), end_iso: toLocalInput(end), label: '' }];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          oneOff: next,
        },
      };
    });
  };

  const updateOneOffClosedRule = (id, patch) => {
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const oneOff = Array.isArray(base.oneOff) ? base.oneOff : [];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          oneOff: oneOff.map((it) => (String(it?.id || '') === String(id) ? { ...it, ...patch } : it)),
        },
      };
    });
  };

  const deleteOneOffClosedRule = (id) => {
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const oneOff = Array.isArray(base.oneOff) ? base.oneOff : [];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          oneOff: oneOff.filter((it) => String(it?.id || '') !== String(id)),
        },
      };
    });
  };

  const legacyBanner = useMemo(() => source === 'legacy', [source]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'General Settings');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await fetchSettings();
      } catch (e) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchSettings = async () => {
    if (!API_BASE_URL) return;
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load general settings');

      const incoming = data?.settings && typeof data.settings === 'object' ? data.settings : null;
      if (incoming) {
        setForm({ ...fallbackDefaults, ...incoming });
      } else {
        setForm(fallbackDefaults);
      }
      // Reset the write-only secret input on every (re)load — the stored secret
      // is never echoed back, so the field always starts empty.
      setUbereatsSecretInput('');
      setDoordashSecretInput('');
      setUberDirectSecretInput('');
      setUberDirectWebhookSecretInput('');
      setBorzoTokenInput('');
      setBorzoWebhookSecretInput('');
      setSource(data?.source || 'legacy');
    } catch (e) {
      setError(e?.message || 'Failed to load settings');
      setForm(fallbackDefaults);
      setSource('legacy');
    }
  };

  const updateShipping = (segment, patch) => {
    setForm((prev) => {
      const shipping = { ...(prev.shipping || {}) };
      const seg = { ...(shipping[segment] || {}) };
      shipping[segment] = { ...seg, ...patch };
      return { ...prev, shipping };
    });
  };

  const updateTax = (patch) => {
    setForm((prev) => ({ ...prev, tax: { ...(prev.tax || {}), ...patch } }));
  };

  const updateFeatures = (patch) => {
    setForm((prev) => ({ ...prev, features: { ...(prev.features || {}), ...patch } }));
  };

  const updateReports = (patch) => {
    setForm((prev) => ({
      ...prev,
      reports: {
        ...((fallbackDefaults.reports && typeof fallbackDefaults.reports === 'object') ? fallbackDefaults.reports : {}),
        ...((prev.reports && typeof prev.reports === 'object') ? prev.reports : {}),
        ...(patch || {}),
      },
    }));
  };

  const updatePosPayments = (patch) => {
    setForm((prev) => ({
      ...prev,
      posPayments: {
        ...(fallbackDefaults.posPayments || {}),
        ...((prev.posPayments && typeof prev.posPayments === 'object') ? prev.posPayments : {}),
        ...(patch || {}),
      },
    }));
  };

  const updatePosPrinting = (patch) => {
    setForm((prev) => ({
      ...prev,
      posPrinting: {
        ...(fallbackDefaults.posPrinting || {}),
        ...((prev.posPrinting && typeof prev.posPrinting === 'object') ? prev.posPrinting : {}),
        ...(patch || {}),
      },
    }));
  };

  const updatePosSla = (patch) => {
    setForm((prev) => ({
      ...prev,
      posSla: {
        ...(fallbackDefaults.posSla || {}),
        ...((prev.posSla && typeof prev.posSla === 'object') ? prev.posSla : {}),
        ...(patch || {}),
      },
    }));
  };

  const updateStoreTicketNumber = (patch) => {
    setForm((prev) => ({
      ...prev,
      storeTicketNumber: {
        ...(fallbackDefaults.storeTicketNumber || {}),
        ...((prev.storeTicketNumber && typeof prev.storeTicketNumber === 'object') ? prev.storeTicketNumber : {}),
        ...(patch || {}),
      },
    }));
  };

  const updatePosTips = (patch) => {
    setForm((prev) => ({
      ...prev,
      posTips: {
        ...(fallbackDefaults.posTips || {}),
        ...((prev.posTips && typeof prev.posTips === 'object') ? prev.posTips : {}),
        ...(patch || {}),
      },
    }));
  };

  const updateScheduledOrders = (patch) => {
    setForm((prev) => ({
      ...prev,
      scheduledOrders: {
        ...(fallbackDefaults.scheduledOrders || {}),
        ...((prev.scheduledOrders && typeof prev.scheduledOrders === 'object') ? prev.scheduledOrders : {}),
        ...(patch || {}),
      },
    }));
  };

  // Staff-interface languages (take-order-i18n spec R2.8).
  //
  // NOTE the deliberate absence of an `i18n` entry in `fallbackDefaults`. The form
  // is seeded with `{ ...fallbackDefaults, ...incoming }`, so a default here would
  // make `form.i18n` defined for EVERY tenant, and the save payload would then
  // synthesize an `i18n` block on every unrelated settings save — breaking the
  // inertness guarantee (R1.5). Instead `form.i18n` stays `undefined` until either
  // the API returned a stored block or the admin touches this control, and the
  // payload omits the key while it is undefined.
  //
  // `enabled` is DERIVED rather than exposed as its own checkbox: it is true
  // exactly when a second language is selected. That matches the backend
  // coercion (one language is never multilingual) and gives the admin one thing
  // to reason about instead of two that can contradict each other.
  const i18nView = (form?.i18n && typeof form.i18n === 'object')
    ? form.i18n
    : { enabled: false, availableLanguages: [DEFAULT_LANGUAGE], defaultLanguage: DEFAULT_LANGUAGE };

  const commitI18n = (availableLanguages, defaultLanguage) => {
    const langs = [];
    // Preserve registry order for stability, and always retain English: it is the
    // guaranteed fallback for every translation key.
    LANGUAGE_CODES.forEach((code) => {
      if (code === DEFAULT_LANGUAGE || availableLanguages.includes(code)) langs.push(code);
    });
    const nextDefault = langs.includes(defaultLanguage) ? defaultLanguage : DEFAULT_LANGUAGE;
    setForm((prev) => ({
      ...prev,
      i18n: {
        enabled: langs.length >= 2,
        availableLanguages: langs,
        defaultLanguage: nextDefault,
      },
    }));
  };

  const toggleI18nLanguage = (code, checked) => {
    if (code === DEFAULT_LANGUAGE) return; // English is always retained
    const current = Array.isArray(i18nView.availableLanguages) ? i18nView.availableLanguages : [];
    const next = checked ? [...current, code] : current.filter((c) => c !== code);
    commitI18n(next, i18nView.defaultLanguage);
  };

  // Table subdivisions (table-party-subdivision spec R2.1, R2.2).
  //
  // SAME `fallbackDefaults` TRAP AS `i18n` ABOVE, and it bites harder here. The form
  // is seeded with `{ ...fallbackDefaults, ...incoming }`, so a `tableParties` default
  // would make `form.tableParties` defined for EVERY tenant and every unrelated save
  // would write the block — silently opting every tenant in. That matters more for
  // this flag than for languages because this one gates queue behaviour that fires on
  // data existing tenants already produce (a stored `guestCount`, two dine-in tickets
  // on one table), so a leaked opt-in changes the American and pizza demos without
  // anyone asking. `form.tableParties` therefore stays `undefined` until the API
  // returned a stored block or the admin touches this control.
  //
  // NOT gated on currency, payment provider, tax regime, or configured languages
  // (R2.7): communal seating is a floor-plan fact, so an INR store must not imply it
  // and a USD Stripe store must not be denied it.
  const tablePartiesEnabledView = form?.tableParties?.enabled === true;

  const setTablePartiesEnabled = (enabled) => {
    setForm((prev) => ({ ...prev, tableParties: { enabled: Boolean(enabled) } }));
  };

  // In-app card payment (in-app-card-payment spec, Phase 1). SAME omit-when-absent
  // discipline as tableParties above and for the same reason: deliberately NOT in
  // fallbackDefaults, so `form.inAppCardPaymentEnabled` stays undefined until the API
  // returned a stored value or the admin touches this control. That keeps every
  // unrelated save from synthesizing the key and silently opting a tenant in. The flag
  // is inert until the Stripe PaymentSheet phase; it applies only to Stripe tenants
  // (the UI below is shown only for STRIPE), so a non-Stripe tenant never sees it.
  const inAppCardPaymentEnabledView = form?.inAppCardPaymentEnabled === true;

  const setInAppCardPaymentEnabled = (enabled) => {
    setForm((prev) => ({ ...prev, inAppCardPaymentEnabled: Boolean(enabled) }));
  };

  // Razorpay UPI at-store QR toggle (razorpay-upi-dinein-qr spec). SEPARATE from the Stripe
  // in-app-card / reader toggles -- this is for an INR Razorpay tenant. Same omit-when-
  // absent discipline (NOT in fallbackDefaults, so `form.razorpayUpiAtStoreEnabled` stays
  // undefined until the API returned a stored value or the admin touches the control).
  // admin-web/admin-mobile parity -- the mobile General Settings carries the same control.
  const razorpayUpiAtStoreEnabledView = form?.razorpayUpiAtStoreEnabled === true;

  const setRazorpayUpiAtStoreEnabled = (enabled) => {
    setForm((prev) => ({ ...prev, razorpayUpiAtStoreEnabled: Boolean(enabled) }));
  };

  // Stripe Terminal (external card reader) toggle (pos-terminal-reader spec). SEPARATE
  // from inAppCardPaymentEnabled — a tenant may enable in-app card, the reader, both, or
  // neither. Same omit-when-absent discipline (NOT in fallbackDefaults, shown only for
  // STRIPE). admin-web/admin-mobile parity — the mobile General Settings carries it too.
  const terminalReaderEnabledView = form?.terminalReaderEnabled === true;

  const setTerminalReaderEnabled = (enabled) => {
    setForm((prev) => ({ ...prev, terminalReaderEnabled: Boolean(enabled) }));
  };

  // Simulated-reader mode (pos-terminal-reader spec). DEFAULTS TO SIMULATED: the view is
  // true unless an explicit stored `false` is present, so a tenant that just turned the
  // reader on is in the safe test-mode until they pair real hardware and flip this off.
  // Separate stored key `terminalReaderSimulated`; same omit-when-absent save discipline.
  const terminalReaderSimulatedView = form?.terminalReaderSimulated !== false;

  const setTerminalReaderSimulated = (simulated) => {
    setForm((prev) => ({ ...prev, terminalReaderSimulated: Boolean(simulated) }));
  };

  // Stripe Terminal TIPS (pos-terminal-tips spec). Three independent controls, same
  // omit-when-absent discipline. terminalTipsEnabled is the master switch; the three
  // independent method flags (terminalTipOnBill / terminalTipOnReader /
  // terminalTipBeforePayment) each surface as a charge-time button (on_bill = dine-in
  // written tip; on_reader = customer taps on a smart reader; before_payment = staff enter
  // before charging); terminalReaderType selects discovery (bluetooth M2 vs internet smart
  // readers). Defaults chosen so an untouched tenant is unchanged: tips off, bluetooth.
  const terminalTipsEnabledView = form?.terminalTipsEnabled === true;
  // Three INDEPENDENT tip-method toggles (pos-terminal-tips). A tenant may enable any
  // combination; each enabled method surfaces as its OWN charge-time button on the POS
  // queue (the staff pick per sale). The server still honors the explicit choice and only
  // falls back to fixed priority (on_bill > on_reader > before_payment) if no button-chosen
  // mode is sent. Each fail-closed.
  const terminalTipOnBillView = form?.terminalTipOnBill === true;
  const terminalTipOnReaderView = form?.terminalTipOnReader === true;
  const terminalTipBeforePaymentView = form?.terminalTipBeforePayment === true;
  const terminalReaderTypeView = String(form?.terminalReaderType || '').trim().toLowerCase() === 'internet'
    ? 'internet'
    : 'bluetooth';

  const setTerminalTipsEnabled = (enabled) => {
    setForm((prev) => ({ ...prev, terminalTipsEnabled: Boolean(enabled) }));
  };
  const setTerminalTipOnBill = (v) => {
    setForm((prev) => ({ ...prev, terminalTipOnBill: Boolean(v) }));
  };
  const setTerminalTipOnReader = (v) => {
    setForm((prev) => ({ ...prev, terminalTipOnReader: Boolean(v) }));
  };
  const setTerminalTipBeforePayment = (v) => {
    setForm((prev) => ({ ...prev, terminalTipBeforePayment: Boolean(v) }));
  };
  const setTerminalReaderType = (rtype) => {
    const t = String(rtype || '').trim().toLowerCase();
    setForm((prev) => ({ ...prev, terminalReaderType: t === 'internet' ? 'internet' : 'bluetooth' }));
  };

  const updateOptionalCrochetFeatures = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const crochet_features = { ...(optional_sections.crochet_features || fallbackDefaults.optional_sections.crochet_features) };
      optional_sections.crochet_features = { ...crochet_features, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const updateOptionalEvents = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const events = { ...(optional_sections.events || fallbackDefaults.optional_sections.events) };
      optional_sections.events = { ...events, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const updateOptionalReviews = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const reviews = { ...(optional_sections.reviews || fallbackDefaults.optional_sections.reviews) };
      optional_sections.reviews = { ...reviews, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const save = async () => {
    if (!API_BASE_URL) return;
    try {
      setSaving(true);
      setError('');
      setStatusMessage('');

      const enabledReportTypesRaw = Array.isArray(form?.reports?.enabledReportTypes) ? form.reports.enabledReportTypes : [];
      const allowedReportTypes = new Set(['orders', 'labels', 'users', 'fulfillment', 'online_sales']);
      const enabledReportTypes = [];
      const seen = new Set();
      for (const t of enabledReportTypesRaw) {
        const v = String(t || '').trim().toLowerCase();
        if (allowedReportTypes.has(v) && !seen.has(v)) {
          enabledReportTypes.push(v);
          seen.add(v);
        }
      }
      if (!enabledReportTypes.length) {
        throw new Error('At least one report type must be enabled');
      }

      const posOnlineEnabled = Boolean(form?.posPayments?.online_enabled ?? true);
      const posInStoreEnabled = Boolean(form?.posPayments?.in_store_enabled ?? true);
      if (!posOnlineEnabled && !posInStoreEnabled) {
        throw new Error('Cannot disable both online and in-store POS payments');
      }

      const posSlaEnabled = Boolean(form?.posSla?.enabled);
      const posSlaGreen = Number(form?.posSla?.greenMinutes);
      const posSlaOrange = Number(form?.posSla?.orangeMinutes);
      const posSlaRed = Number(form?.posSla?.redMinutes);
      if (posSlaEnabled) {
        if (
          !Number.isFinite(posSlaGreen) ||
          !Number.isFinite(posSlaOrange) ||
          !Number.isFinite(posSlaRed) ||
          posSlaGreen <= 0 ||
          posSlaOrange <= 0 ||
          posSlaRed <= 0
        ) {
          throw new Error('SLA green, orange, and red minutes are all required (and must be positive) when SLA indicators are enabled');
        }
        if (!(posSlaGreen < posSlaOrange && posSlaOrange < posSlaRed)) {
          throw new Error('SLA thresholds must satisfy green < orange < red minutes');
        }
      }

      // In-store ticket number. Validated only when enabled -- a disabled feature must
      // never block an unrelated save, and the fields are disabled in the UI while off so
      // a stale value behind the toggle is not the admin's fault.
      //
      // The backend clamps rather than rejects (it runs on the order path, where refusing
      // is the worse failure). Rejecting HERE is still right: at save time there is a
      // human to tell, and silently storing 1 when they typed 0 would be a surprise the
      // next morning.
      const ticketEnabled = form?.storeTicketNumber?.enabled === true;
      const ticketStart = Number(form?.storeTicketNumber?.startNumber);
      const ticketDailyReset = form?.storeTicketNumber?.dailyReset === true;
      if (ticketEnabled) {
        if (!Number.isFinite(ticketStart) || !Number.isInteger(ticketStart) || ticketStart < 1 || ticketStart > 999999) {
          throw new Error('In-store ticket starting number must be a whole number between 1 and 999999');
        }
      }

      const posDeliveryEnabled = Boolean(form?.posDeliveryLinks?.enabled);
      const posDeliveryVendors = Array.isArray(form?.posDeliveryLinks?.vendors) ? form.posDeliveryLinks.vendors : [];
      if (posDeliveryEnabled) {
        for (const vendor of posDeliveryVendors) {
          const name = String(vendor?.name || '').trim();
          const url = String(vendor?.url || '').trim();
          if (!name) throw new Error('Delivery provider name is required');
          if (!url) throw new Error('Delivery provider URL is required');
          if (!/^https?:\/\//i.test(url)) throw new Error('Delivery provider URL must start with http:// or https://');
        }
      }

      // Provider/currency compatibility. Stripe product Price objects (used by
      // both online e-commerce checkout and POS-Stripe checkout) are created in
      // USD and a Checkout Session can't mix currencies, so any Stripe-settled
      // store must stay on USD. PhonePe processes only INR and can't service the
      // online (Stripe) storefront. Square/Clover settle in their own account
      // currency, so any currency is allowed there.
      const cMode = String(form?.storeMode || '').trim().toUpperCase();
      const cProvider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
      const cCurrency = String(form?.currency || 'USD').trim().toUpperCase();
      const hasOnlineStripe = cMode === '' || cMode === 'ECOMM' || cMode === 'HYBRID';
      const usesStripe = hasOnlineStripe || (cMode === 'POS' && cProvider === 'STRIPE');
      const indiaProviders = ['PHONEPE', 'RAZORPAY'];
      if (indiaProviders.includes(cProvider)) {
        const label = cProvider === 'RAZORPAY' ? 'Razorpay' : 'PhonePe';
        if (hasOnlineStripe) {
          throw new Error(`${label} can only be used in POS storefront mode. Online e-commerce checkout settles through Stripe (USD), which ${label} cannot service.`);
        }
        if (cCurrency !== 'INR') {
          throw new Error(`${label} only processes payments in INR. Set Store currency to INR (₹) to use ${label}.`);
        }
      } else if (usesStripe && cCurrency !== 'USD' && cCurrency !== 'AUD') {
        // Stripe-settled stores support USD (default) or AUD (australia-globalization).
        // A Checkout Session cannot mix currencies, so it must be exactly one of these.
        throw new Error('Stripe checkout supports USD or AUD only. For another currency, use POS mode with Square/Clover (any currency) or PhonePe/Razorpay (INR).');
      }

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payload = {
        settings: {
          storeMode: String(form?.storeMode || ''),
          posProvider: String(form?.posProvider || 'SQUARE'),
          // POS output routing (square-pos-output-routing spec, Phase 1). Sent ONLY when
          // the admin actually has a value in the form, so a tenant that never touched the
          // control never grows a `posOutput` key by saving unrelated settings. The
          // backend additionally ignores it unless the provider is SQUARE, so this can
          // never affect a non-Square tenant even if sent.
          ...(form?.posOutput ? { posOutput: String(form.posOutput) } : {}),
          // India multi-provider payment options. Only sent when the admin has
          // configured >1 India method; backend validates (INR India store only),
          // sets the first as the primary posProvider, and ignores it otherwise.
          ...(Array.isArray(form?.paymentProviders) && form.paymentProviders.length > 0
            ? { paymentProviders: form.paymentProviders }
            : {}),
          currency: String(form?.currency || 'USD'),
          // Tax regime + config. Backend re-validates the guardrail (IN_GST only
          // for INR + PhonePe/Razorpay; AU_GST only for AUD) and coerces to
          // US_SALES otherwise.
          taxRegime: String(form?.taxRegime || 'US_SALES'),
          gst: {
            gstin: String(form?.gst?.gstin || '').toUpperCase(),
            legalName: String(form?.gst?.legalName || ''),
            storeState: String(form?.gst?.storeState || ''),
            defaultRatePct: Number.isFinite(Number(form?.gst?.defaultRatePct)) ? Number(form.gst.defaultRatePct) : 5,
            pricingMode: String(form?.gst?.pricingMode || 'TAX_EXCLUSIVE'),
            fssai: String(form?.gst?.fssai || '').trim(),
          },
          // Australia GST config (australia-globalization). Only sent when the
          // admin has an auGst block in the form; backend ignores it unless the
          // store is AUD + AU_GST, so US/India stores are unaffected.
          ...(form?.auGst && typeof form.auGst === 'object'
            ? {
                auGst: {
                  abn: String(form?.auGst?.abn || '').trim(),
                  legalName: String(form?.auGst?.legalName || ''),
                  defaultRatePct: Number.isFinite(Number(form?.auGst?.defaultRatePct)) ? Number(form.auGst.defaultRatePct) : 10,
                  pricingMode: String(form?.auGst?.pricingMode || 'TAX_INCLUSIVE'),
                },
              }
            : {}),
          // Staff-interface languages (take-order-i18n). Sent ONLY when a block
          // actually exists — either the tenant already had one or the admin
          // touched the control this session. Omitting the key is what tells the
          // backend to leave the stored value alone and never synthesize one, so a
          // tenant that does not use multilingual mode never grows an `i18n` block
          // by saving unrelated settings (spec R1.5). Backend re-validates.
          ...(form?.i18n && typeof form.i18n === 'object'
            ? {
                i18n: {
                  enabled: Boolean(form.i18n.enabled),
                  availableLanguages: Array.isArray(form.i18n.availableLanguages)
                    ? form.i18n.availableLanguages
                    : [DEFAULT_LANGUAGE],
                  defaultLanguage: String(form.i18n.defaultLanguage || DEFAULT_LANGUAGE),
                },
              }
            : {}),
          // Table subdivisions (table-party-subdivision R1.5, R2.2). Same
          // conditional-spread discipline as `i18n` immediately above: the key is
          // sent only when a block exists, so a tenant that never opted in cannot
          // grow one by saving unrelated settings. Backend re-validates and coerces
          // a non-boolean `enabled` to false.
          ...(form?.tableParties && typeof form.tableParties === 'object'
            ? { tableParties: { enabled: form.tableParties.enabled === true } }
            : {}),
          // In-app card payment (in-app-card-payment Phase 1). CONDITIONAL like
          // tableParties: the key is sent only when the admin has actually set it this
          // session (or the API returned it), so a tenant that never opted in cannot grow
          // the flag by saving unrelated settings. Sent as a strict boolean.
          ...(typeof form?.inAppCardPaymentEnabled === 'boolean'
            ? { inAppCardPaymentEnabled: form.inAppCardPaymentEnabled === true }
            : {}),
          // Razorpay UPI at-store QR toggle (razorpay-upi-dinein-qr spec). Same
          // conditional-when-boolean spread as in-app card above, independent of it: sent
          // only once an admin has touched it, so an untouched tenant keeps a byte-identical
          // body. The backend allowlist merge (admin-product-handler) persists it.
          ...(typeof form?.razorpayUpiAtStoreEnabled === 'boolean'
            ? { razorpayUpiAtStoreEnabled: form.razorpayUpiAtStoreEnabled === true }
            : {}),
          // Stripe Terminal (external reader) toggle (pos-terminal-reader spec). Same
          // conditional-when-boolean spread as in-app card above, independent of it.
          ...(typeof form?.terminalReaderEnabled === 'boolean'
            ? { terminalReaderEnabled: form.terminalReaderEnabled === true }
            : {}),
          // Simulated-reader mode. Same conditional-when-boolean spread, independent of the
          // enable flag: only persisted once an admin has touched it, so a tenant that never
          // changed it keeps a byte-identical body and the clients apply the simulated default.
          ...(typeof form?.terminalReaderSimulated === 'boolean'
            ? { terminalReaderSimulated: form.terminalReaderSimulated === true }
            : {}),
          // Terminal TIPS (pos-terminal-tips). Each key persisted only once an admin has
          // touched it, so an untouched tenant keeps a byte-identical settings body and the
          // backend allowlist applies the safe defaults.
          ...(typeof form?.terminalTipsEnabled === 'boolean'
            ? { terminalTipsEnabled: form.terminalTipsEnabled === true }
            : {}),
          // Square Terminal device tips — independent of the Stripe reader flags. Same
          // omit-when-absent, strict-bool discipline so an untouched tenant is unchanged.
          ...(typeof form?.squareTerminalTipsEnabled === 'boolean'
            ? { squareTerminalTipsEnabled: form.squareTerminalTipsEnabled === true }
            : {}),
          // Shift4 terminal tips (shift4-payment-integration). Same omit-when-absent / strict-bool
          // discipline as squareTerminalTipsEnabled so an untouched tenant is byte-identical.
          ...(typeof form?.shift4TerminalTipsEnabled === 'boolean'
            ? { shift4TerminalTipsEnabled: form.shift4TerminalTipsEnabled === true }
            : {}),
          // Scan-to-Pay QR (take-order-scan-to-pay). Same omit-when-absent / strict-bool
          // discipline so an untouched tenant stays byte-identical; the backend allowlist
          // merge (admin-product-handler) persists it.
          ...(typeof form?.scanToPayEnabled === 'boolean'
            ? { scanToPayEnabled: form.scanToPayEnabled === true }
            : {}),
          // "Square App" (Take Payment) button visibility. Default off; omit-when-absent.
          ...(typeof form?.squareAppButtonEnabled === 'boolean'
            ? { squareAppButtonEnabled: form.squareAppButtonEnabled === true }
            : {}),
          ...(typeof form?.terminalTipOnBill === 'boolean'
            ? { terminalTipOnBill: form.terminalTipOnBill === true }
            : {}),
          ...(typeof form?.terminalTipOnReader === 'boolean'
            ? { terminalTipOnReader: form.terminalTipOnReader === true }
            : {}),
          ...(typeof form?.terminalTipBeforePayment === 'boolean'
            ? { terminalTipBeforePayment: form.terminalTipBeforePayment === true }
            : {}),
          ...(typeof form?.terminalReaderType === 'string' && form.terminalReaderType
            ? { terminalReaderType: form.terminalReaderType }
            : {}),
          reports: {
            enabledReportTypes,
          },
          posPayments: {
            online_enabled: Boolean(form?.posPayments?.online_enabled ?? true),
            in_store_enabled: Boolean(form?.posPayments?.in_store_enabled ?? true),
          },
          posPrinting: {
            autoPrintNewOrders: Boolean(form?.posPrinting?.autoPrintNewOrders),
            autoPrintReceiptOnPayment: Boolean(form?.posPrinting?.autoPrintReceiptOnPayment),
            paperSize: normalizePaperSize(form?.posPrinting?.paperSize),
            // Normalized on save: drops malformed rows, de-dupes, caps. An admin cannot
            // store a printer with a bad IP or unknown role.
            printers: normalizePrinters(form?.posPrinting?.printers),
          },
          posSla: {
            enabled: posSlaEnabled,
            greenMinutes: Number.isFinite(posSlaGreen) ? posSlaGreen : 5,
            orangeMinutes: Number.isFinite(posSlaOrange) ? posSlaOrange : 10,
            redMinutes: Number.isFinite(posSlaRed) ? posSlaRed : 15,
          },
          // Sent unconditionally, like posSla and unlike tableParties: the block has a
          // default for every tenant, so there is no "never opted in" state to protect.
          // The start number and reset zone are sent even while disabled so switching the
          // feature off and back on does not lose them.
          storeTicketNumber: {
            enabled: ticketEnabled,
            startNumber: Number.isFinite(ticketStart) && ticketStart >= 1 ? Math.trunc(ticketStart) : 1,
            dailyReset: ticketDailyReset,
            timezone: String(form?.storeTicketNumber?.timezone || '').trim(),
          },
          orderMessages: {
            pickupMessage: String(form?.orderMessages?.pickupMessage || ''),
            deliveryMessage: String(form?.orderMessages?.deliveryMessage || ''),
          },
          posTips: {
            enabled: Boolean(form?.posTips?.enabled),
            presetPercentages: Array.isArray(form?.posTips?.presetPercentages) 
              ? form.posTips.presetPercentages.filter(p => Number.isFinite(Number(p)) && Number(p) > 0)
              : [15, 18, 20, 25],
            allowCustom: Boolean(form?.posTips?.allowCustom ?? true),
            allowNoTip: Boolean(form?.posTips?.allowNoTip ?? true),
            calculateOn: String(form?.posTips?.calculateOn || 'subtotal'),
            defaultPercentage: Number.isFinite(Number(form?.posTips?.defaultPercentage)) ? Number(form.posTips.defaultPercentage) : null,
            displayMode: String(form?.posTips?.displayMode || 'inline_collapsed'),
          },
          // Scheduled pickup/delivery (scheduled-orders spec). The backend clamps/snaps
          // every numeric to range and coerces the booleans, so this sends the current
          // form values and lets the server be authoritative about bounds.
          scheduledOrders: {
            enabled: Boolean(form?.scheduledOrders?.enabled),
            maxCutoffHours: Number(form?.scheduledOrders?.maxCutoffHours) || 72,
            minLeadMinutes: Number.isFinite(Number(form?.scheduledOrders?.minLeadMinutes)) ? Number(form.scheduledOrders.minLeadMinutes) : 30,
            posReleaseMinutes: Number.isFinite(Number(form?.scheduledOrders?.posReleaseMinutes)) ? Number(form.scheduledOrders.posReleaseMinutes) : 15,
            slotIntervalMinutes: Number(form?.scheduledOrders?.slotIntervalMinutes) || 15,
            allowPickup: Boolean(form?.scheduledOrders?.allowPickup ?? true),
            allowDelivery: Boolean(form?.scheduledOrders?.allowDelivery ?? true),
          },
          posDeliveryLinks: {
            enabled: posDeliveryEnabled,
            vendors: posDeliveryVendors.map((vendor) => ({
              id: vendor?.id,
              name: vendor?.name,
              url: vendor?.url,
              imageUrl: vendor?.imageUrl,
              imageUpload: vendor?.imageUpload,
            })),
          },
          delivery: {
            enabled: Boolean(form?.delivery?.enabled),
            fee_cents: Number.isFinite(Number(form?.delivery?.fee_cents)) ? Number(form.delivery.fee_cents) : 0,
            minimum_order_cents: Number.isFinite(Number(form?.delivery?.minimum_order_cents)) ? Number(form.delivery.minimum_order_cents) : 0,
            radius_restriction: {
              enabled: Boolean(form?.delivery?.radius_restriction?.enabled),
              base_zip_code: String(form?.delivery?.radius_restriction?.base_zip_code || '').trim(),
              max_radius_miles: Number.isFinite(Number(form?.delivery?.radius_restriction?.max_radius_miles)) ? Number(form.delivery.radius_restriction.max_radius_miles) : 10.0,
              max_radius_km: Number.isFinite(Number(form?.delivery?.radius_restriction?.max_radius_km)) ? Number(form.delivery.radius_restriction.max_radius_km) : 5.0,
            },
            uber_direct: (() => {
              const ud = (form?.delivery?.uber_direct && typeof form.delivery.uber_direct === 'object') ? form.delivery.uber_direct : {};
              const pr = (ud.pricing_rule && typeof ud.pricing_rule === 'object') ? ud.pricing_rule : {};
              const pa = (ud.pickup_address && typeof ud.pickup_address === 'object') ? ud.pickup_address : {};
              const payload = {
                enabled: Boolean(ud.enabled),
                mock: Boolean(ud.mock),
                environment: ud.environment === 'production' ? 'production' : 'sandbox',
                client_id: String(ud.client_id || '').trim(),
                customer_id: String(ud.customer_id || '').trim(),
                pickup_address: {
                  name: String(pa.name || '').trim(),
                  phone: String(pa.phone || '').trim(),
                  street: String(pa.street || '').trim(),
                  apt_unit: String(pa.apt_unit || '').trim(),
                  city: String(pa.city || '').trim(),
                  state: String(pa.state || '').trim().toUpperCase(),
                  zip: String(pa.zip || '').trim(),
                },
                pricing_rule: {
                  mode: ['pass_through', 'flat', 'markup', 'subsidized'].includes(pr.mode) ? pr.mode : 'pass_through',
                  flat_cents: Number.isFinite(Number(pr.flat_cents)) ? Math.max(0, Number(pr.flat_cents)) : 0,
                  markup_cents: Number.isFinite(Number(pr.markup_cents)) ? Math.max(0, Number(pr.markup_cents)) : 0,
                  markup_percent: Number.isFinite(Number(pr.markup_percent)) ? Math.max(0, Number(pr.markup_percent)) : 0,
                  subsidized_customer_cents: Number.isFinite(Number(pr.subsidized_customer_cents)) ? Math.max(0, Number(pr.subsidized_customer_cents)) : 0,
                },
              };
              // Write-only secret: only send when the admin typed a new one.
              const typedUberDirectSecret = String(uberDirectSecretInput || '').trim();
              if (typedUberDirectSecret) {
                payload.client_secret = typedUberDirectSecret;
              }
              const typedUberDirectWebhookSecret = String(uberDirectWebhookSecretInput || '').trim();
              if (typedUberDirectWebhookSecret) {
                payload.webhook_signing_secret = typedUberDirectWebhookSecret;
              }
              return payload;
            })(),
            borzo: (() => {
              const bz = (form?.delivery?.borzo && typeof form.delivery.borzo === 'object') ? form.delivery.borzo : {};
              const pr = (bz.pricing_rule && typeof bz.pricing_rule === 'object') ? bz.pricing_rule : {};
              const pa = (bz.pickup_address && typeof bz.pickup_address === 'object') ? bz.pickup_address : {};
              const payload = {
                enabled: Boolean(bz.enabled),
                mock: Boolean(bz.mock),
                environment: bz.environment === 'production' ? 'production' : 'sandbox',
                pickup_address: {
                  name: String(pa.name || '').trim(),
                  phone: String(pa.phone || '').trim(),
                  street: String(pa.street || '').trim(),
                  apt_unit: String(pa.apt_unit || '').trim(),
                  city: String(pa.city || '').trim(),
                  // Free text: Indian state names are full words ("Tamil Nadu"),
                  // so this is never uppercased or truncated.
                  state: String(pa.state || '').trim(),
                  zip: String(pa.zip || '').trim(),
                },
                pricing_rule: {
                  mode: ['pass_through', 'flat', 'markup', 'subsidized'].includes(pr.mode) ? pr.mode : 'pass_through',
                  flat_cents: Number.isFinite(Number(pr.flat_cents)) ? Math.max(0, Number(pr.flat_cents)) : 0,
                  markup_cents: Number.isFinite(Number(pr.markup_cents)) ? Math.max(0, Number(pr.markup_cents)) : 0,
                  markup_percent: Number.isFinite(Number(pr.markup_percent)) ? Math.max(0, Number(pr.markup_percent)) : 0,
                  subsidized_customer_cents: Number.isFinite(Number(pr.subsidized_customer_cents)) ? Math.max(0, Number(pr.subsidized_customer_cents)) : 0,
                },
              };
              // Write-only credentials: only send when the admin typed a new
              // value. Omitting the field tells the backend to keep the stored
              // one.
              const typedBorzoToken = String(borzoTokenInput || '').trim();
              if (typedBorzoToken) {
                payload.auth_token = typedBorzoToken;
              }
              const typedBorzoWebhookSecret = String(borzoWebhookSecretInput || '').trim();
              if (typedBorzoWebhookSecret) {
                payload.webhook_signing_secret = typedBorzoWebhookSecret;
              }
              return payload;
            })(),
          },
          addressAutocomplete: {
            provider: String(form?.addressAutocomplete?.provider || 'geoapify'),
            enabled: Boolean(form?.addressAutocomplete?.enabled),
          },
          delivery_integrations: (() => {
            const di = (form?.delivery_integrations && typeof form.delivery_integrations === 'object')
              ? form.delivery_integrations
              : {};
            const ue = (di.ubereats && typeof di.ubereats === 'object') ? di.ubereats : {};
            const dd = (di.doordash && typeof di.doordash === 'object') ? di.doordash : {};
            const ot = (di.otter && typeof di.otter === 'object') ? di.otter : {};

            const ubereats = {
              enabled: Boolean(ue.enabled),
              environment: ue.environment === 'production' ? 'production' : 'sandbox',
              client_id: String(ue.client_id || '').trim(),
              store_id: String(ue.store_id || '').trim(),
              last_menu_sync_timestamp: ue.last_menu_sync_timestamp ?? null,
            };
            // Write-only secret: only send a value when the admin typed a new
            // one. Omitting the field tells the backend to preserve the stored
            // secret (it never round-trips through the UI).
            const typedSecret = String(ubereatsSecretInput || '').trim();
            if (typedSecret) {
              ubereats.client_secret = typedSecret;
            }

            const doordash = {
              enabled: Boolean(dd.enabled),
              environment: dd.environment === 'production' ? 'production' : 'sandbox',
              developer_id: String(dd.developer_id || '').trim(),
              key_id: String(dd.key_id || '').trim(),
              store_id: String(dd.store_id || '').trim(),
              last_menu_sync_timestamp: dd.last_menu_sync_timestamp ?? null,
            };
            // Write-only signing secret: only send a value when the admin typed
            // a new one. Omitting the field tells the backend to preserve the
            // stored secret (it never round-trips through the UI).
            const typedDoordashSecret = String(doordashSecretInput || '').trim();
            if (typedDoordashSecret) {
              doordash.signing_secret = typedDoordashSecret;
            }

            return {
              ubereats,
              doordash,
              otter: {
                // The relocated Otter toggle is authoritative; the backend
                // propagates it to the top-level Otter block. `form.otter.enabled`
                // is the source of truth shared with the detailed Otter section.
                enabled: Boolean(form?.otter?.enabled ?? ot.enabled),
              },
            };
          })(),
          orderingClosedHours: {
            enabled: Boolean(form?.orderingClosedHours?.enabled),
            timezone: String(form?.orderingClosedHours?.timezone || 'America/New_York'),
            message: String(form?.orderingClosedHours?.message || ''),
            oneOff: Array.isArray(form?.orderingClosedHours?.oneOff) ? form.orderingClosedHours.oneOff : [],
            weekly: Array.isArray(form?.orderingClosedHours?.weekly) ? form.orderingClosedHours.weekly : [],
          },
          serviceWindows: {
            enabled: Boolean(form?.serviceWindows?.enabled),
            timezone: String(form?.serviceWindows?.timezone || 'America/New_York'),
            windows: Array.isArray(form?.serviceWindows?.windows) ? form.serviceWindows.windows : [],
          },
          features: {
            mock_online_payment_enabled: Boolean(form?.features?.mock_online_payment_enabled),
            mock_upi_qr_enabled: Boolean(form?.features?.mock_upi_qr_enabled),
            pos_discount_codes_enabled: Boolean(form?.features?.pos_discount_codes_enabled),
            chatbot_enabled: Boolean(form?.features?.chatbot_enabled),
          },
          shipping: {
            allowed_countries: form?.shipping?.allowed_countries || ['US'],
            standard: {
              enabled: Boolean(form?.shipping?.standard?.enabled),
              label: String(form?.shipping?.standard?.label || ''),
              amount_cents: dollarsToCents(form?.shipping?.standard?.amount_dollars ?? centsToDollars(form?.shipping?.standard?.amount_cents)) ?? 499,
              min_days: Number(form?.shipping?.standard?.min_days ?? 3),
              max_days: Number(form?.shipping?.standard?.max_days ?? 5),
            },
            express: {
              enabled: Boolean(form?.shipping?.express?.enabled),
              label: String(form?.shipping?.express?.label || ''),
              amount_cents: dollarsToCents(form?.shipping?.express?.amount_dollars ?? centsToDollars(form?.shipping?.express?.amount_cents)) ?? 999,
              min_days: Number(form?.shipping?.express?.min_days ?? 1),
              max_days: Number(form?.shipping?.express?.max_days ?? 2),
            },
            free: {
              enabled: Boolean(form?.shipping?.free?.enabled),
              label: String(form?.shipping?.free?.label || ''),
              threshold_cents: dollarsToCents(form?.shipping?.free?.threshold_dollars ?? centsToDollars(form?.shipping?.free?.threshold_cents)) ?? 2000,
              min_days: Number(form?.shipping?.free?.min_days ?? 5),
              max_days: Number(form?.shipping?.free?.max_days ?? 8),
            },
          },
          tax: {
            stripe_automatic_tax_enabled: Boolean(form?.tax?.stripe_automatic_tax_enabled),
          },
          optional_sections: {
            crochet_features: {
              enabled: Boolean(form?.optional_sections?.crochet_features?.enabled),
              title: String(form?.optional_sections?.crochet_features?.title || 'Crochet features'),
              html: String(form?.optional_sections?.crochet_features?.html || ''),
            },
            events: {
              enabled: Boolean(form?.optional_sections?.events?.enabled),
              title: String(form?.optional_sections?.events?.title || 'Events'),
              html: String(form?.optional_sections?.events?.html || ''),
            },
            reviews: {
              enabled: Boolean(form?.optional_sections?.reviews?.enabled),
              title: String(form?.optional_sections?.reviews?.title || 'Reviews'),
              auto_approve: Boolean(form?.optional_sections?.reviews?.auto_approve),
              double_opt_in: true,
            },
          },
        },
      };

      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Save failed'));

      setForm({ ...fallbackDefaults, ...(data?.settings || form) });
      // The just-saved secret (if any) is now stored server-side; clear the
      // write-only input so it never lingers in the UI.
      setUbereatsSecretInput('');
      setDoordashSecretInput('');
      setUberDirectSecretInput('');
      setUberDirectWebhookSecretInput('');
      setBorzoTokenInput('');
      setBorzoWebhookSecretInput('');
      setSource(data?.source || 'stored');
      const stripeSyncOk = Boolean(data?.stripeSync?.ok);
      const stripeSyncErr = data?.stripeSync?.error;
      if (stripeSyncOk) {
        setStatusMessage('Saved.');
      } else if (stripeSyncErr) {
        setStatusMessage(`Saved. Shipping rates could not be synced: ${stripeSyncErr}`);
      } else {
        setStatusMessage('Saved. Shipping rates could not be synced.');
      }
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
      setTimeout(() => {
        try {
          statusRef.current?.focus?.();
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        } catch {
          // ignore
        }
      }, 50);
      setSaving(false);
    }
  };

  const reports = (form?.reports && typeof form.reports === 'object')
    ? form.reports
    : fallbackDefaults.reports;

  const reportTypeOptions = useMemo(() => {
    return [
      { k: 'orders', label: 'Orders' },
      { k: 'labels', label: 'Labels' },
      { k: 'users', label: 'Users' },
      { k: 'fulfillment', label: 'Fulfillment' },
      // LABEL ONLY. The key `online_sales` is persisted in tenant config and validated
      // against an allow-list in two places in this file; renaming it would orphan
      // every tenant's saved report-type selection (R2.2).
      { k: 'online_sales', label: 'Sales' },
    ];
  }, []);

  const enabledReportTypes = useMemo(() => {
    const raw = Array.isArray(reports?.enabledReportTypes) ? reports.enabledReportTypes : fallbackDefaults.reports.enabledReportTypes;
    const allowed = new Set(['orders', 'labels', 'users', 'fulfillment', 'online_sales']);
    const out = [];
    const seen = new Set();
    for (const t of raw || []) {
      const v = String(t || '').trim().toLowerCase();
      if (allowed.has(v) && !seen.has(v)) {
        out.push(v);
        seen.add(v);
      }
    }
    return out.length ? out : ['orders', 'labels', 'users', 'fulfillment', 'online_sales'];
  }, [reports?.enabledReportTypes]);

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading settings...</p>
      </div>
    );
  }

  const standard = form?.shipping?.standard || fallbackDefaults.shipping.standard;

  const express = form?.shipping?.express || fallbackDefaults.shipping.express;
  const free = form?.shipping?.free || fallbackDefaults.shipping.free;
  const orderingClosedHours = (form?.orderingClosedHours && typeof form.orderingClosedHours === 'object')
    ? form.orderingClosedHours
    : fallbackDefaults.orderingClosedHours;
  const serviceWindows = (form?.serviceWindows && typeof form.serviceWindows === 'object')
    ? form.serviceWindows
    : fallbackDefaults.serviceWindows;
  const posDeliveryLinks = (form?.posDeliveryLinks && typeof form.posDeliveryLinks === 'object')
    ? form.posDeliveryLinks
    : fallbackDefaults.posDeliveryLinks;

  const toggleReportType = (key) => {
    const k = String(key || '').trim().toLowerCase();
    if (!k) return;
    const next = enabledReportTypes.includes(k) ? enabledReportTypes.filter((x) => x !== k) : [...enabledReportTypes, k];
    if (!next.length) {
      setError('At least one report type must be enabled');
      return;
    }
    setError('');
    updateReports({ enabledReportTypes: next });
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div>
          <h1>General</h1>
          <p>Shipping & taxes</p>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {legacyBanner ? (
          <div className="alert" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>
            <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
            Currently using legacy defaults: Standard $4.99, Free over $20. Save to start managing shipping here.
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        <div className="products-list-container">
          <details open>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Storefront mode</summary>
            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
              <div className="form-row" style={{ gap: 14, alignItems: 'end' }}>
                <div className="form-group" style={{ minWidth: 280 }}>
                  <label>Mode</label>
                  <select value={String(form?.storeMode ?? '')} onChange={(e) => setForm((prev) => ({ ...prev, storeMode: e.target.value }))}>
                    <option value="">Legacy (no mode set)</option>
                    <option value="ECOMM">ECOMM (eCommerce)</option>
                    <option value="POS">POS (In-store ordering)</option>
                    <option value="HYBRID">HYBRID (demo only)</option>
                  </select>
                  <div className="field-hint">This controls which parts of the public app are enabled for this domain.</div>
                </div>

                <div className="form-group" style={{ minWidth: 240 }}>
                  <label>Store currency</label>
                  <select
                    value={String(form?.currency ?? 'USD')}
                    onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))}
                  >
                    {Object.entries(SUPPORTED_CURRENCIES).map(([code, meta]) => (
                      <option key={code} value={code}>
                        {`${meta.label} (${meta.symbol}) — ${code}`}
                      </option>
                    ))}
                  </select>
                  <div className="field-hint">
                    Applies to all prices across the storefront, POS, and admin.
                    Changing currency <strong>relabels</strong> amounts — it does not convert existing prices.
                  </div>
                  {(() => {
                    const cMode = String(form?.storeMode || '').trim().toUpperCase();
                    const cProvider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                    const hasOnlineStripe = cMode === '' || cMode === 'ECOMM' || cMode === 'HYBRID';
                    const usesStripe = hasOnlineStripe || (cMode === 'POS' && cProvider === 'STRIPE');
                    let note = '';
                    if (cProvider === 'PHONEPE' || cProvider === 'RAZORPAY') {
                      const label = cProvider === 'RAZORPAY' ? 'Razorpay' : 'PhonePe';
                      note = `${label} settles in INR (₹) only, and requires POS storefront mode.`;
                    } else if (usesStripe) {
                      // Stripe-settled stores support USD (default) or AUD
                      // (australia-globalization). A Checkout Session cannot mix
                      // currencies, so it must be exactly one of those two. Keep
                      // this in sync with the save-time guard above and the
                      // backend _STRIPE_CURRENCIES allowlist.
                      note = 'This store settles through Stripe, which supports USD or AUD. For another currency, use POS mode with Square/Clover.';
                    }
                    if (!note) return null;
                    return (
                      <div className="field-hint" style={{ color: '#b45309', marginTop: 4 }}>
                        {note}
                      </div>
                    );
                  })()}
                </div>

                {/* Staff interface languages (take-order-i18n).
                    Rendered UNCONDITIONALLY on purpose. The India GST block
                    immediately below is gated on INR + PhonePe/Razorpay, and it
                    would be an easy mistake to copy that gating here — but
                    language must NOT depend on currency, payment provider, or tax
                    regime (spec R2.10). A US restaurant may want Tamil, and an INR
                    store must never imply it. */}
                <div className="form-group" style={{ minWidth: 260 }}>
                  <label>Staff interface languages</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '6px 0' }}>
                    {LANGUAGE_CODES.map((code) => {
                      const isDefaultLang = code === DEFAULT_LANGUAGE;
                      const checked = isDefaultLang
                        || (Array.isArray(i18nView.availableLanguages)
                          && i18nView.availableLanguages.includes(code));
                      return (
                        <label
                          key={code}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontWeight: 500,
                            cursor: isDefaultLang ? 'not-allowed' : 'pointer',
                            opacity: isDefaultLang ? 0.7 : 1,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isDefaultLang}
                            onChange={(e) => toggleI18nLanguage(code, e.target.checked)}
                          />
                          <span>{LANGUAGE_REGISTRY[code].label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="field-hint">
                    This setting chooses which languages are <strong>offered</strong>.
                    Each staff member picks their own display language on their own
                    device, so one server can work in Tamil while another works in English.
                    English is always available as the fallback.
                  </div>

                  {Array.isArray(i18nView.availableLanguages) && i18nView.availableLanguages.length >= 2 ? (
                    <div style={{ marginTop: 8 }}>
                      <label>Default language for new devices</label>
                      <select
                        value={String(i18nView.defaultLanguage || DEFAULT_LANGUAGE)}
                        onChange={(e) => commitI18n(i18nView.availableLanguages, e.target.value)}
                      >
                        {i18nView.availableLanguages.map((code) => (
                          <option key={code} value={code}>
                            {LANGUAGE_REGISTRY[code] ? LANGUAGE_REGISTRY[code].label : code}
                          </option>
                        ))}
                      </select>
                      <div className="field-hint">
                        Used until a staff member chooses a language on their device.
                      </div>
                    </div>
                  ) : null}

                  <div className="field-hint" style={{ marginTop: 4 }}>
                    Applies to the staff <strong>Take Order</strong> screen. Other admin
                    screens, the customer storefront, and kitchen ticket headings remain
                    in English.
                  </div>
                </div>

                {/* Table subdivisions (table-party-subdivision R2.1, R2.7).
                    Rendered UNCONDITIONALLY, for the same reason the language block
                    above is: the India GST block immediately below is gated on
                    INR + PhonePe/Razorpay and it would be an easy mistake to copy
                    that gating here. Communal seating is a floor-plan fact — a US
                    diner with one long shared table needs this, and an INR store
                    must not imply it. */}
                <div className="form-group" style={{ minWidth: 260 }}>
                  <label>Table subdivisions</label>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: '6px 0',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={tablePartiesEnabledView}
                      onChange={(e) => setTablePartiesEnabled(e.target.checked)}
                      aria-describedby="tableParties-hint"
                    />
                    <span>Allow two or more separate groups at one table</span>
                  </label>
                  <div className="field-hint" id="tableParties-hint">
                    For communal or large tables where unrelated groups sit together and
                    pay separately. Servers can mark each ticket with a party letter
                    (<strong>A</strong>, <strong>B</strong>), the letter prints on the
                    kitchen chit and the guest bill, and the POS queue groups a table's
                    open tickets together so a cashier cannot settle the wrong one.
                  </div>
                  <div className="field-hint" style={{ marginTop: 4 }}>
                    Leave this off if every table is one group. With it off, Take Order,
                    the POS queue, and printed tickets behave exactly as they do today.
                    Change it between services rather than during one: turning it off
                    also removes the party from a <em>reprinted</em> bill, while the copy
                    the guest is holding still shows it.
                  </div>
                </div>

                {/* India GST — shown only for an INR store on an India provider
                    (PhonePe or Razorpay). Additive and fully gated; US stores never
                    see this and default to US_SALES. */}
                {(() => {
                  const prov = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                  const isIndia = String(form?.currency || 'USD').trim().toUpperCase() === 'INR'
                    && (prov === 'PHONEPE' || prov === 'RAZORPAY');
                  if (!isIndia) return null;
                  const gst = (form?.gst && typeof form.gst === 'object') ? form.gst : {};
                  const setGst = (patch) => setForm((prev) => ({ ...prev, gst: { ...(prev.gst || {}), ...patch } }));
                  const regime = String(form?.taxRegime || 'US_SALES').toUpperCase();
                  return (
                    <div className="form-group" style={{ minWidth: 240 }}>
                      <label>Tax regime</label>
                      <select
                        value={regime === 'IN_GST' ? 'IN_GST' : 'US_SALES'}
                        onChange={(e) => setForm((prev) => ({ ...prev, taxRegime: e.target.value }))}
                      >
                        <option value="US_SALES">US Sales Tax (default)</option>
                        <option value="IN_GST">India GST</option>
                      </select>
                      <div className="field-hint">India GST applies only to INR stores on an India provider (PhonePe or Razorpay). Confirm rates with your accountant.</div>
                      {regime === 'IN_GST' && (
                        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                          <div>
                            <label>GSTIN</label>
                            <input
                              value={String(gst.gstin || '')}
                              onChange={(e) => setGst({ gstin: e.target.value.toUpperCase() })}
                              placeholder="29ABCDE1234F1Z5"
                              maxLength={15}
                            />
                          </div>
                          <div>
                            <label>Legal business name</label>
                            <input value={String(gst.legalName || '')} onChange={(e) => setGst({ legalName: e.target.value })} />
                          </div>
                          <div>
                            <label>FSSAI license number</label>
                            <input
                              value={String(gst.fssai || '')}
                              onChange={(e) => setGst({ fssai: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                              placeholder="14-digit number"
                              inputMode="numeric"
                              maxLength={14}
                            />
                            <div className="field-hint">Food businesses must print their 14-digit FSSAI license on bills/invoices. Collected at onboarding.</div>
                          </div>
                          <div>
                            <label>Store state (place of supply)</label>
                            <input value={String(gst.storeState || '')} onChange={(e) => setGst({ storeState: e.target.value })} placeholder="e.g. Karnataka" />
                          </div>
                          <div>
                            <label>Default GST rate (%)</label>
                            <input
                              type="number" min="0" max="100" step="0.5"
                              value={gst.defaultRatePct ?? 5}
                              onChange={(e) => setGst({ defaultRatePct: Number(e.target.value) })}
                            />
                            <div className="field-hint">Restaurants are commonly 5%.</div>
                          </div>
                          <div>
                            <label>Pricing</label>
                            <select
                              value={String(gst.pricingMode || 'TAX_EXCLUSIVE')}
                              onChange={(e) => setGst({ pricingMode: e.target.value })}
                            >
                              <option value="TAX_EXCLUSIVE">Tax exclusive (add GST at checkout)</option>
                              <option value="TAX_INCLUSIVE">Tax inclusive (prices include GST)</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  return (
                    <div className="form-group" style={{ minWidth: 240 }}>
                      <label>POS provider</label>
                      <select
                        value={String(form?.posProvider ?? 'SQUARE')}
                        onChange={(e) => setForm((prev) => ({ ...prev, posProvider: e.target.value }))}
                      >
                        <option value="SQUARE">Square</option>
                        <option value="CLOVER">Clover</option>
                        <option value="STRIPE">Stripe</option>
                        <option value="SHIFT4">Shift4</option>
                        <option value="PHONEPE">PhonePe (India)</option>
                        <option value="RAZORPAY">Razorpay (India)</option>
                      </select>
                      <div className="field-hint">Used for POS ordering + payments when Storefront mode is POS/HYBRID.</div>
                    </div>
                  );
                })()}

                {/* Australia GST (australia-globalization) — shown only for an AUD
                    store (any card provider: Stripe/Square/Clover). Additive and fully
                    gated; US/India stores never see this and default to their own
                    regime. Separate block from the India GST above so neither affects
                    the other. */}
                {(() => {
                  const isAu = String(form?.currency || 'USD').trim().toUpperCase() === 'AUD';
                  if (!isAu) return null;
                  const au = (form?.auGst && typeof form.auGst === 'object') ? form.auGst : {};
                  const setAu = (patch) => setForm((prev) => ({ ...prev, auGst: { ...(prev.auGst || {}), ...patch } }));
                  const regime = String(form?.taxRegime || 'US_SALES').toUpperCase();
                  return (
                    <div className="form-group" style={{ minWidth: 240 }}>
                      <label>Tax regime</label>
                      <select
                        value={regime === 'AU_GST' ? 'AU_GST' : 'US_SALES'}
                        onChange={(e) => setForm((prev) => ({ ...prev, taxRegime: e.target.value }))}
                      >
                        <option value="US_SALES">US Sales Tax (default)</option>
                        <option value="AU_GST">Australia GST</option>
                      </select>
                      <div className="field-hint">Australian GST (10%, GST-inclusive) applies only to AUD stores. Confirm your registration and rate with your accountant.</div>
                      {regime === 'AU_GST' && (
                        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                          <div>
                            <label>ABN</label>
                            <input
                              value={String(au.abn || '')}
                              onChange={(e) => setAu({ abn: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                              placeholder="11-digit ABN"
                              inputMode="numeric"
                              maxLength={11}
                            />
                          </div>
                          <div>
                            <label>Legal business name</label>
                            <input value={String(au.legalName || '')} onChange={(e) => setAu({ legalName: e.target.value })} />
                          </div>
                          <div>
                            <label>Default GST rate (%)</label>
                            <input
                              type="number" min="0" max="100" step="0.5"
                              value={au.defaultRatePct ?? 10}
                              onChange={(e) => setAu({ defaultRatePct: Number(e.target.value) })}
                            />
                          </div>
                          <div>
                            <label>Pricing mode</label>
                            <select
                              value={String(au.pricingMode || 'TAX_INCLUSIVE')}
                              onChange={(e) => setAu({ pricingMode: e.target.value })}
                            >
                              <option value="TAX_INCLUSIVE">Tax-inclusive (prices include GST)</option>
                              <option value="TAX_EXCLUSIVE">Tax-exclusive (GST added at checkout)</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* POS output routing (square-pos-output-routing spec, Phase 1).
                    SQUARE-ONLY, mirroring how the in-app card toggle below is gated to
                    STRIPE. Chooses whether in-store (and storefront) orders land in our
                    own POS queue ("Internal POS", today's behaviour) or on the merchant's
                    Square terminal ("Square terminal"). Defaults to Internal POS, so a
                    tenant that never touches it is unchanged. Inert until Phase 2 wires the
                    Take Order -> terminal path. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const provider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                  // Terminal output is supported on the terminal-capable providers
                  // (Square terminal / Clover device). Non-terminal providers never see it.
                  const show = (mode === 'POS' || mode === 'HYBRID') && (provider === 'SQUARE' || provider === 'CLOVER');
                  if (!show) return null;
                  const deviceLabel = provider === 'CLOVER' ? 'Clover device' : 'Square terminal';
                  return (
                    <div className="form-group" style={{ minWidth: 240 }}>
                      <label>POS output</label>
                      <select
                        value={String(form?.posOutput ?? 'INTERNAL')}
                        onChange={(e) => setForm((prev) => ({ ...prev, posOutput: e.target.value }))}
                      >
                        <option value="INTERNAL">Internal POS (our order queue)</option>
                        <option value="TERMINAL">{deviceLabel}</option>
                      </select>
                      <div className="field-hint">
                        Where in-store and storefront orders appear. Internal POS keeps them in
                        this app&apos;s order queue. {deviceLabel} sends them to your {provider === 'CLOVER' ? 'Clover' : 'Square'} device.
                      </div>
                    </div>
                  );
                })()}

                {/* India multi-provider payment options. For an INR store the admin
                    can offer more than one India payment method at checkout. When
                    2+ are selected, the storefront shows a chooser; the first
                    selected becomes the primary provider. Gated to INR + POS/HYBRID
                    so US/other stores never see this. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const isPos = mode === 'POS' || mode === 'HYBRID';
                  const isInr = String(form?.currency || 'USD').trim().toUpperCase() === 'INR';
                  if (!isPos || !isInr) return null;

                  const INDIA_METHODS = [
                    { key: 'PHONEPE', label: 'PhonePe' },
                    { key: 'RAZORPAY', label: 'Razorpay' },
                  ];
                  const current = Array.isArray(form?.paymentProviders)
                    ? form.paymentProviders.map((p) => String(p || '').trim().toUpperCase()).filter((p) => p)
                    : [];
                  // Seed from the single posProvider when no explicit list exists yet.
                  const primary = String(form?.posProvider || '').trim().toUpperCase();
                  const selected = current.length > 0
                    ? current
                    : (primary === 'PHONEPE' || primary === 'RAZORPAY' ? [primary] : []);

                  const toggle = (key) => {
                    setForm((prev) => {
                      const list = Array.isArray(prev.paymentProviders)
                        ? prev.paymentProviders.map((p) => String(p || '').trim().toUpperCase()).filter((p) => p)
                        : (['PHONEPE', 'RAZORPAY'].includes(primary) ? [primary] : []);
                      const has = list.includes(key);
                      const next = has ? list.filter((p) => p !== key) : [...list, key];
                      // Keep the primary posProvider in sync with the first choice.
                      const nextPrimary = next[0] || prev.posProvider;
                      return { ...prev, paymentProviders: next, posProvider: nextPrimary };
                    });
                  };

                  return (
                    <div className="form-group" style={{ minWidth: 240 }}>
                      <label>India payment methods</label>
                      <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
                        {INDIA_METHODS.map((m) => (
                          <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                            <input
                              type="checkbox"
                              checked={selected.includes(m.key)}
                              onChange={() => toggle(m.key)}
                            />
                            {m.label}
                          </label>
                        ))}
                      </div>
                      <div className="field-hint">
                        Select one or more. With 2+ selected, the storefront lets the
                        customer choose at checkout. The first selected is the primary
                        provider. Each method needs its own credentials secret.
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const onlineEnabled = Boolean(form?.posPayments?.online_enabled ?? true);
                  const inStoreEnabled = Boolean(form?.posPayments?.in_store_enabled ?? true);
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>POS payments</label>
                      <div style={{ display: 'grid', gap: 10 }}>
                        <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="checkbox"
                            checked={onlineEnabled}
                            onChange={(e) => {
                              const next = Boolean(e.target.checked);
                              if (!next && !inStoreEnabled) {
                                setError('Cannot disable both online and in-store POS payments');
                                return;
                              }
                              setError('');
                              updatePosPayments({ online_enabled: next });
                            }}
                          />
                          Online payments (Pay now)
                        </label>
                        <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="checkbox"
                            checked={inStoreEnabled}
                            onChange={(e) => {
                              const next = Boolean(e.target.checked);
                              if (!next && !onlineEnabled) {
                                setError('Cannot disable both online and in-store POS payments');
                                return;
                              }
                              setError('');
                              updatePosPayments({ in_store_enabled: next });
                            }}
                          />
                          In-store payments (Pay at restaurant)
                        </label>
                      </div>
                      <div className="field-hint">At least one option must remain enabled.</div>
                    </div>
                  );
                })()}

                {/* In-app card payment (in-app-card-payment spec, Phase 1). Shown ONLY
                    for a POS/HYBRID Stripe tenant, because in-app card entry is a
                    Stripe-only capability (Square/Clover are external-app hand-offs;
                    Razorpay/PhonePe are web/redirect). Default OFF; a tenant that never
                    turns it on has no stored key and the mobile settle flow is unchanged.
                    The flag is inert until the Stripe PaymentSheet phase ships. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const provider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                  const show = (mode === 'POS' || mode === 'HYBRID') && provider === 'STRIPE';
                  if (!show) return null;
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>In-app card payment</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={inAppCardPaymentEnabledView}
                          onChange={(e) => {
                            setError('');
                            setInAppCardPaymentEnabled(e.target.checked);
                          }}
                          aria-describedby="inAppCardPayment-hint"
                        />
                        <span>Accept card, Apple Pay &amp; Google Pay in the app</span>
                      </label>
                      <div className="field-hint" id="inAppCardPayment-hint">
                        Lets staff charge a card (or Apple/Google Pay) directly in the mobile
                        POS app via Stripe, instead of the hosted checkout redirect. Requires
                        the updated app build. When off, the existing payment flow is used.
                      </div>
                    </div>
                  );
                })()}

                {/* UPI at store (razorpay-upi-dinein-qr spec). Lets staff collect a dine-in
                    / counter payment by showing a Razorpay dynamic UPI QR the customer scans
                    with any UPI app. Shown ONLY for a POS/HYBRID tenant whose effective India
                    provider is Razorpay (UPI QR is a Razorpay+INR capability), mirroring the
                    backend guard and the mobile General Settings control. Independent of the
                    Stripe toggles. Default OFF; omit-when-absent. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const primary = String(form?.posProvider || '').trim().toUpperCase();
                  const providers = Array.isArray(form?.paymentProviders)
                    ? form.paymentProviders.map((p) => String(p || '').trim().toUpperCase())
                    : [];
                  const usesRazorpay = primary === 'RAZORPAY' || providers.includes('RAZORPAY');
                  const isInr = String(form?.currency || '').trim().toUpperCase() === 'INR';
                  const show = (mode === 'POS' || mode === 'HYBRID') && usesRazorpay && isInr;
                  if (!show) return null;
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>UPI at store</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={razorpayUpiAtStoreEnabledView}
                          onChange={(e) => {
                            setError('');
                            setRazorpayUpiAtStoreEnabled(e.target.checked);
                          }}
                          aria-describedby="razorpayUpi-hint"
                        />
                        <span>Accept UPI at the counter via a Razorpay QR</span>
                      </label>
                      <div className="field-hint" id="razorpayUpi-hint">
                        Shows a "Collect UPI (QR)" button on unpaid dine-in / counter orders.
                        The customer scans a Razorpay dynamic QR with any UPI app (PhonePe,
                        Google Pay, Paytm, …); the order is marked paid when Razorpay confirms.
                        When off, the existing payment flow is used.
                      </div>
                    </div>
                  );
                })()}

                {/* Mock UPI QR (razorpay-upi-dinein-qr R8). Its OWN top-level block beside
                    "UPI at store" with the IDENTICAL gate, so it renders exactly where that
                    toggle does. (It previously lived inside the "Test payment mode" group,
                    which is gated/positioned differently and did not render for this tenant.)
                    Dev/demo stand-in while real UPI isn't enabled on the Razorpay account:
                    the "Collect UPI (QR)" button produces a SIMULATED QR the customer still
                    scans, settling via our mock-pay page — no money moves. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const primary = String(form?.posProvider || '').trim().toUpperCase();
                  const providers = Array.isArray(form?.paymentProviders)
                    ? form.paymentProviders.map((p) => String(p || '').trim().toUpperCase())
                    : [];
                  const usesRazorpay = primary === 'RAZORPAY' || providers.includes('RAZORPAY');
                  const isInr = String(form?.currency || '').trim().toUpperCase() === 'INR';
                  const show = (mode === 'POS' || mode === 'HYBRID') && usesRazorpay && isInr;
                  if (!show) return null;
                  const mockUpi = Boolean(form?.features?.mock_upi_qr_enabled);
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Mock UPI QR (test)</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={mockUpi}
                          onChange={(e) => {
                            setError('');
                            updateFeatures({ mock_upi_qr_enabled: e.target.checked });
                          }}
                          aria-describedby="mockUpi-hint"
                        />
                        <span>Simulated scan — no money moves</span>
                      </label>
                      <div className="field-hint" id="mockUpi-hint">
                        Dev/demo only. The POS "Collect UPI (QR)" button shows a SIMULATED QR
                        the customer scans with any phone; tapping Pay on the opened page marks
                        the order paid. Turn OFF to use the real Razorpay UPI QR (requires UPI
                        enabled on the store's Razorpay account).
                      </div>
                    </div>
                  );
                })()}

                {/* Stripe Terminal (external card reader) — pos-terminal-reader spec.
                    SEPARATE toggle from in-app card; shown ONLY for a POS/HYBRID Stripe
                    tenant (Terminal is Stripe-only). Default OFF; omit-when-absent so a
                    tenant that never turns it on has no stored key. admin-web/admin-mobile
                    parity — the mobile General Settings carries the same control. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const provider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                  const show = (mode === 'POS' || mode === 'HYBRID') && provider === 'STRIPE';
                  if (!show) return null;
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Card reader (Stripe Terminal)</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={terminalReaderEnabledView}
                          onChange={(e) => {
                            setError('');
                            setTerminalReaderEnabled(e.target.checked);
                          }}
                          aria-describedby="terminalReader-hint"
                        />
                        <span>Accept card-present payments on a Stripe reader</span>
                      </label>
                      <div className="field-hint" id="terminalReader-hint">
                        Lets staff take a card the customer taps or inserts on a paired Stripe
                        reader (BBPOS WisePad 3 / Stripe Reader M2) via Stripe Terminal, in the
                        mobile POS app. Requires a paired reader and the updated app build.
                        When off, the reader option is hidden.
                      </div>

                      {/* Simulated-reader (test-mode) sub-toggle — only meaningful when the
                          reader is on. Default ON so testing works with no hardware; the
                          owner turns it OFF once a real reader is paired. */}
                      {terminalReaderEnabledView ? (
                        <label
                          className="toggle-chip"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 10 }}
                        >
                          <input
                            type="checkbox"
                            checked={terminalReaderSimulatedView}
                            onChange={(e) => {
                              setError('');
                              setTerminalReaderSimulated(e.target.checked);
                            }}
                            aria-describedby="terminalReaderSimulated-hint"
                          />
                          <span>Use a simulated reader (test mode — no hardware)</span>
                        </label>
                      ) : null}
                      {terminalReaderEnabledView ? (
                        <div className="field-hint" id="terminalReaderSimulated-hint">
                          Keep this ON to test the full payment flow against Stripe test mode
                          with no physical reader. Turn it OFF once a real Stripe reader is
                          paired to the POS device to take live card-present payments.
                        </div>
                      ) : null}

                      {/* Reader TYPE — which hardware discovery to use. Only meaningful when
                          the reader is on. bluetooth = M2 / WisePad 3; internet = S700 / S710
                          / WisePOS E smart readers. Default bluetooth (the demo M2). */}
                      {terminalReaderEnabledView ? (
                        <div style={{ marginTop: 12 }}>
                          <label htmlFor="terminalReaderType">Reader type</label>
                          <select
                            id="terminalReaderType"
                            value={terminalReaderTypeView}
                            onChange={(e) => {
                              setError('');
                              setTerminalReaderType(e.target.value);
                            }}
                          >
                            <option value="bluetooth">Bluetooth (Stripe Reader M2 / WisePad 3)</option>
                            <option value="internet">Smart reader (S700 / S710 / WisePOS E)</option>
                          </select>
                          <div className="field-hint">
                            Bluetooth readers pair to the POS device; smart readers connect
                            over the network. Only the smart readers show a tip screen to the
                            customer (see Tips below).
                          </div>
                        </div>
                      ) : null}

                      {/* TIPS (pos-terminal-tips). Master switch + mode. Only shown when the
                          reader is on. Default off; independent of the reader/simulated flags. */}
                      {terminalReaderEnabledView ? (
                        <label
                          className="toggle-chip"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 12 }}
                        >
                          <input
                            type="checkbox"
                            checked={terminalTipsEnabledView}
                            onChange={(e) => {
                              setError('');
                              setTerminalTipsEnabled(e.target.checked);
                            }}
                            aria-describedby="terminalTips-hint"
                          />
                          <span>Collect tips on the reader</span>
                        </label>
                      ) : null}
                      {terminalReaderEnabledView && terminalTipsEnabledView ? (
                        <div style={{ marginTop: 10 }}>
                          <label>Tip methods</label>
                          {/* THREE INDEPENDENT toggles — enable any combination. Each enabled
                              method becomes its OWN button on the POS queue at charge time, so
                              staff pick the right one per sale (dine-in = on the bill; counter
                              = on the reader). No priority — the button the staff tap decides. */}
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                            <input
                              type="checkbox"
                              checked={terminalTipOnBillView}
                              onChange={(e) => { setError(''); setTerminalTipOnBill(e.target.checked); }}
                            />
                            <span>On the bill — staff enter a written tip after the card is authorized (dine-in)</span>
                          </label>
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                            <input
                              type="checkbox"
                              checked={terminalTipOnReaderView}
                              onChange={(e) => { setError(''); setTerminalTipOnReader(e.target.checked); }}
                            />
                            <span>On the reader — customer taps a tip on the reader (smart readers only)</span>
                          </label>
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                            <input
                              type="checkbox"
                              checked={terminalTipBeforePaymentView}
                              onChange={(e) => { setError(''); setTerminalTipBeforePayment(e.target.checked); }}
                            />
                            <span>Before payment — staff enter a tip on the iPad before charging (any reader)</span>
                          </label>
                          <div className="field-hint" id="terminalTips-hint">
                            Enable any combination. Each method you turn on appears as its own
                            button on the POS queue when charging on a reader, so staff choose
                            per sale: “Charge on Reader + Bill Tip” for dine-in (staff enter the
                            written tip) and “Charge on Reader” for a customer tapping the tip on
                            the reader. On-the-reader tipping needs a smart reader (S700 / S710 /
                            WisePOS E); a Bluetooth M2 or the simulator has no tip screen, so it
                            charges without a reader tip.
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const enabled = Boolean(form?.posPrinting?.autoPrintNewOrders);
                  // Paper size, migrated from the old orderDetailsOnly boolean. A stored
                  // record that predates this change has no paperSize; the old toggle only
                  // ever produced half-letter, which is what the shared normalizer defaults
                  // to. Allowed values live in utils/paperSize.js so this screen, the print
                  // builder and both backend handlers cannot disagree.
                  const paperSize = normalizePaperSize(form?.posPrinting?.paperSize);
                  const pickupMessage = String(form?.orderMessages?.pickupMessage || '');
                  const deliveryMessage = String(form?.orderMessages?.deliveryMessage || '');
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>POS printing</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => {
                            setError('');
                            updatePosPrinting({ autoPrintNewOrders: Boolean(e.target.checked) });
                          }}
                        />
        &nbsp;Auto-print kitchen ticket on new order
                      </label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                        <input
                          type="checkbox"
                          checked={Boolean(form?.posPrinting?.autoPrintReceiptOnPayment)}
                          onChange={(e) => {
                            setError('');
                            updatePosPrinting({ autoPrintReceiptOnPayment: Boolean(e.target.checked) });
                          }}
                        />
                        &nbsp;Auto-print customer receipt on payment
                      </label>
                      <div className="field-hint">
                        Kitchen ticket prints when an order arrives; the customer receipt
                        prints when an order is paid (pay-at-store / counter). Dine-in bills
                        are printed on demand, so they are not auto-printed.
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <label htmlFor="pos-print-paper-size" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                          Print paper size
                        </label>
                        <select
                          id="pos-print-paper-size"
                          value={paperSize}
                          onChange={(e) => {
                            setError('');
                            // Normalized rather than passed through: the select can only
                            // emit known values today, but this is the value that ends up
                            // in an @page CSS rule, so it is validated at the boundary.
                            updatePosPrinting({ paperSize: normalizePaperSize(e.target.value) });
                          }}
                          style={{ minWidth: 260 }}
                        >
                          {/* Driven from the shared list so a size added there appears on
                              both surfaces without editing this markup. */}
                          {PAPER_SIZES.map((p) => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="field-hint">
                        When Auto-print is enabled, the POS Queue page prints a kitchen ticket for each new incoming order.
                      </div>
                      <div className="field-hint">
                        Paper size applies to both the kitchen ticket (Print Ticket) and the itemized bill (Print Receipt), on manual and auto-print alike.
                      </div>
                      {isRollPaper(paperSize) ? (
                        <div className="field-hint">
                          80 mm is a continuous receipt roll: the ticket prints in a single narrow column
                          and runs to whatever length it needs, rather than being laid out for a fixed sheet.
                          Your printer must be loaded with 80 mm paper and reporting that size.
                        </div>
                      ) : null}

                      {/* NETWORK PRINTERS (pos-network-printing Layer 2).
                          The restaurant's printers, defined once here. The mobile app routes
                          each document to one by role: the kitchen ticket to a kitchen
                          printer, the itemized receipt to a cashier printer. Leaving this
                          empty keeps today's behaviour (the app prints via its own
                          selected/system printer), so this is purely opt-in.

                          Editing works on a local draft array through updatePosPrinting;
                          the SAVE path re-normalizes (drops bad IPs/roles), so the on-screen
                          validation here is guidance, not the enforcement point. */}
                      {(() => {
                        const printers = Array.isArray(form?.posPrinting?.printers)
                          ? form.posPrinting.printers
                          : [];
                        const setPrinters = (next) => updatePosPrinting({ printers: next });
                        const patchRow = (idx, patch) =>
                          setPrinters(printers.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
                        const addRow = () => {
                          if (printers.length >= MAX_PRINTERS) return;
                          setError('');
                          setPrinters([
                            ...printers,
                            // A fresh row is deliberately incomplete (blank IP). The save
                            // normalizer drops it until it has a valid IP + role, so an
                            // empty row the admin never fills in cannot persist.
                            { id: `printer-${Date.now()}`, name: '', ip: '', port: DEFAULT_PRINTER_PORT, role: 'kitchen', language: 'en' },
                          ]);
                        };
                        const removeRow = (idx) => {
                          setError('');
                          setPrinters(printers.filter((_, i) => i !== idx));
                        };
                        return (
                          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(148,163,184,0.25)' }}>
                            <label>Network printers</label>
                            <div className="field-hint">
                              For direct network (ESC/POS) printing. Enter each printer's IP address and role.
                              The kitchen ticket goes to a kitchen printer; the itemized receipt to a cashier
                              printer. Leave empty to keep using this device's selected/system printer.
                            </div>
                            {printers.length === 0 ? (
                              <div className="field-hint" style={{ marginTop: 8, fontStyle: 'italic' }}>
                                No network printers configured.
                              </div>
                            ) : null}
                            {printers.map((row, idx) => {
                              const ip = String(row?.ip || '');
                              const ipValid = isValidIpv4(ip);
                              const role = PRINTER_ROLES.includes(row?.role) ? row.role : 'kitchen';
                              const language = LANGUAGE_CODES.includes(row?.language) ? row.language : DEFAULT_LANGUAGE;
                              return (
                                <div
                                  key={row?.id || idx}
                                  style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}
                                >
                                  <div className="form-group" style={{ minWidth: 160 }}>
                                    <label style={{ fontSize: 12 }}>Name (optional)</label>
                                    <input
                                      type="text"
                                      value={String(row?.name || '')}
                                      maxLength={40}
                                      placeholder="Kitchen"
                                      onChange={(e) => { setError(''); patchRow(idx, { name: e.target.value }); }}
                                    />
                                  </div>
                                  <div className="form-group" style={{ minWidth: 150 }}>
                                    <label style={{ fontSize: 12 }}>IP address</label>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={ip}
                                      placeholder="192.168.1.50"
                                      aria-invalid={ip !== '' && !ipValid}
                                      style={ip !== '' && !ipValid ? { borderColor: '#dc2626' } : undefined}
                                      onChange={(e) => { setError(''); patchRow(idx, { ip: e.target.value.trim() }); }}
                                    />
                                  </div>
                                  <div className="form-group" style={{ minWidth: 90 }}>
                                    <label style={{ fontSize: 12 }}>Port</label>
                                    <input
                                      type="number"
                                      min="1"
                                      max="65535"
                                      value={row?.port ?? DEFAULT_PRINTER_PORT}
                                      onChange={(e) => {
                                        setError('');
                                        const raw = e.target.value;
                                        patchRow(idx, { port: raw === '' ? '' : Number(raw) });
                                      }}
                                      style={{ width: 90 }}
                                    />
                                  </div>
                                  <div className="form-group" style={{ minWidth: 130 }}>
                                    <label style={{ fontSize: 12 }}>Role</label>
                                    <select
                                      value={role}
                                      onChange={(e) => { setError(''); patchRow(idx, { role: e.target.value }); }}
                                    >
                                      {PRINTER_ROLES.map((r) => (
                                        <option key={r} value={r}>{r === 'kitchen' ? 'Kitchen ticket' : 'Cashier receipt'}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="form-group" style={{ minWidth: 130 }}>
                                    {/* Ticket print language (per-printer). English is the default and
                                        prints English only; another language prints that language with
                                        English beneath. Options come from LANGUAGE_CODES so adding a
                                        language is one edit in i18nRegistry. */}
                                    <label style={{ fontSize: 12 }}>Print language</label>
                                    <select
                                      value={language}
                                      onChange={(e) => { setError(''); patchRow(idx, { language: e.target.value }); }}
                                    >
                                      {LANGUAGE_CODES.map((code) => (
                                        <option key={code} value={code}>
                                          {LANGUAGE_REGISTRY[code] ? LANGUAGE_REGISTRY[code].label : code}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeRow(idx)}
                                    style={{
                                      padding: '8px 12px', borderRadius: 8, border: '1px solid #fca5a5',
                                      background: '#fff', color: '#b91c1c', fontWeight: 700, cursor: 'pointer',
                                    }}
                                  >
                                    Remove
                                  </button>
                                  {ip !== '' && !ipValid ? (
                                    <div className="field-hint" style={{ color: '#dc2626', width: '100%' }}>
                                      Enter a valid IPv4 address (e.g. 192.168.1.50). This row will not be saved until it is valid.
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                            {printers.length < MAX_PRINTERS ? (
                              <button
                                type="button"
                                onClick={addRow}
                                style={{
                                  marginTop: 12, padding: '8px 14px', borderRadius: 8,
                                  border: '1px solid #cbd5e1', background: '#fff', color: '#334155',
                                  fontWeight: 700, cursor: 'pointer',
                                }}
                              >
                                + Add printer
                              </button>
                            ) : (
                              <div className="field-hint" style={{ marginTop: 10 }}>
                                Maximum of {MAX_PRINTERS} printers.
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {(() => {
                        const slaEnabled = Boolean(form?.posSla?.enabled);
                        const green = form?.posSla?.greenMinutes;
                        const orange = form?.posSla?.orangeMinutes;
                        const red = form?.posSla?.redMinutes;
                        const greenNum = Number(green);
                        const orangeNum = Number(orange);
                        const redNum = Number(red);
                        const ordersValid =
                          Number.isFinite(greenNum) && Number.isFinite(orangeNum) && Number.isFinite(redNum) &&
                          greenNum > 0 && orangeNum > 0 && redNum > 0 &&
                          greenNum < orangeNum && orangeNum < redNum;
                        const showError = slaEnabled && !ordersValid;
                        const onMinChange = (key) => (e) => {
                          setError('');
                          const raw = e.target.value;
                          updatePosSla({ [key]: raw === '' ? '' : Number(raw) });
                        };
                        return (
                          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(148,163,184,0.25)' }}>
                            <label>Order SLA indicators</label>
                            <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                              <input
                                type="checkbox"
                                checked={slaEnabled}
                                onChange={(e) => {
                                  setError('');
                                  updatePosSla({ enabled: Boolean(e.target.checked) });
                                }}
                              />
                              Show SLA blinker lights on OPEN orders
                            </label>
                            <div className="field-hint">
                              For orders still in OPEN/ACTIVE state, the POS Queue shows a blinking light based on
                              minutes elapsed since the order was received: green under the green threshold, orange
                              between green and orange, red at/after the orange threshold.
                            </div>
                            {slaEnabled ? (
                              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>🟢 Green under (min)</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={green ?? ''}
                                    onChange={onMinChange('greenMinutes')}
                                    style={{ width: 120 }}
                                  />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: '#ea580c', fontWeight: 600 }}>🟠 Orange under (min)</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={orange ?? ''}
                                    onChange={onMinChange('orangeMinutes')}
                                    style={{ width: 120 }}
                                  />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>🔴 Red at/after (min)</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={red ?? ''}
                                    onChange={onMinChange('redMinutes')}
                                    style={{ width: 120 }}
                                  />
                                </div>
                              </div>
                            ) : null}
                            {showError ? (
                              <div className="field-hint" style={{ color: '#dc2626', marginTop: 8 }}>
                                Green, orange, and red minutes are all required and must satisfy green &lt; orange &lt; red.
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}

                      {/* IN-STORE TICKET / TOKEN NUMBER.
                          The order number is a 12-character UUID slice -- correct as an
                          internal identifier, useless to call across a counter. With this
                          on, staff Take Order tickets also carry a short consecutive
                          token ("001") and that is what the queue, the chit and the send
                          confirmation show.

                          The order number itself is NOT replaced: it is the orders
                          table's partition key and is embedded in payment identifiers.

                          Every dependent field is DISABLED rather than hidden while the
                          toggle is off, so an admin can still see the configuration that
                          will apply once they switch it on. */}
                      {(() => {
                        const stn = form?.storeTicketNumber;
                        const ticketEnabled = stn?.enabled === true;
                        const startRaw = stn?.startNumber;
                        const dailyReset = stn?.dailyReset === true;
                        const startNum = Number(startRaw);
                        const startInvalid =
                          ticketEnabled && (!Number.isFinite(startNum) || startNum < 1 || startNum > 999999);
                        // Pre-filled with the zone the tenant has ALREADY established
                        // elsewhere, so an India store does not silently reset its tokens
                        // on the Eastern day boundary just because this field was left
                        // untouched. Stored as '' until the admin picks something, which
                        // the backend reads as "fall back to the tenant zone".
                        const effectiveTz = resolveTenantTimezone(form);
                        const tzValue = String(stn?.timezone || '').trim() || effectiveTz;
                        return (
                          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(148,163,184,0.25)' }}>
                            <label>In-store ticket number</label>
                            <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                              <input
                                type="checkbox"
                                checked={ticketEnabled}
                                aria-describedby="storeTicketNumber-hint"
                                onChange={(e) => {
                                  setError('');
                                  updateStoreTicketNumber({ enabled: Boolean(e.target.checked) });
                                }}
                              />
                              Use a ticket/token number for in-store orders
                            </label>
                            <div className="field-hint" id="storeTicketNumber-hint">
                              Applies to in-store orders taken by staff on the Take Order screen. Online and
                              customer self-order numbering is unchanged, and the underlying order number is
                              kept as-is for lookups and payments.
                            </div>

                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-start' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <label htmlFor="store-ticket-start" style={{ fontSize: 12, fontWeight: 600 }}>
                                  Starting number
                                </label>
                                <input
                                  id="store-ticket-start"
                                  type="number"
                                  min="1"
                                  max="999999"
                                  step="1"
                                  disabled={!ticketEnabled}
                                  value={startRaw ?? ''}
                                  onChange={(e) => {
                                    setError('');
                                    const raw = e.target.value;
                                    updateStoreTicketNumber({ startNumber: raw === '' ? '' : Number(raw) });
                                  }}
                                  style={{ width: 140 }}
                                />
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 280 }}>
                                <label htmlFor="store-ticket-tz" style={{ fontSize: 12, fontWeight: 600 }}>
                                  Daily reset timezone
                                </label>
                                <select
                                  id="store-ticket-tz"
                                  value={tzValue}
                                  disabled={!ticketEnabled || !dailyReset}
                                  onChange={(e) => {
                                    setError('');
                                    updateStoreTicketNumber({ timezone: e.target.value });
                                  }}
                                >
                                  {TIMEZONE_OPTIONS.map((tz) => (
                                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <label
                              className="toggle-chip"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 12, opacity: ticketEnabled ? 1 : 0.5 }}
                            >
                              <input
                                type="checkbox"
                                checked={dailyReset}
                                disabled={!ticketEnabled}
                                onChange={(e) => {
                                  setError('');
                                  const next = Boolean(e.target.checked);
                                  // Pin the zone the admin is looking at the moment they
                                  // turn the reset on. Otherwise the field shows the
                                  // inherited zone but stores '', and a later change to
                                  // the service-hours zone would move the reset boundary
                                  // without anyone touching this screen.
                                  updateStoreTicketNumber(
                                    next && !String(stn?.timezone || '').trim()
                                      ? { dailyReset: true, timezone: tzValue }
                                      : { dailyReset: next }
                                  );
                                }}
                              />
                              Reset the number daily
                            </label>
                            <div className="field-hint">
                              {dailyReset
                                ? `Numbering restarts from the starting number at midnight ${tzValue}.`
                                : 'Numbering runs continuously and never restarts.'}
                            </div>
                            {startInvalid ? (
                              <div className="field-hint" style={{ color: '#dc2626', marginTop: 8 }}>
                                Starting number is required and must be between 1 and 999999.
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}

                      <div style={{ marginTop: 20 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                          Pickup Order Message (Optional)
                        </label>
                        <input
                          type="text"
                          value={pickupMessage}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({
                              ...prev,
                              orderMessages: {
                                ...(prev.orderMessages || {}),
                                pickupMessage: e.target.value,
                              },
                            }));
                          }}
                          placeholder="e.g., Thank you for your order!"
                          style={{ width: '100%', padding: '8px 12px' }}
                        />
                        <div className="field-hint">
                          Optional message displayed on storefront order details page for pickup orders
                        </div>
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                          Delivery Order Message (Optional)
                        </label>
                        <input
                          type="text"
                          value={deliveryMessage}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({
                              ...prev,
                              orderMessages: {
                                ...(prev.orderMessages || {}),
                                deliveryMessage: e.target.value,
                              },
                            }));
                          }}
                          placeholder="e.g., Your order will be delivered soon!"
                          style={{ width: '100%', padding: '8px 12px' }}
                        />
                        <div className="field-hint">
                          Optional message displayed on storefront order details page for delivery orders
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const tipsEnabled = Boolean(form?.posTips?.enabled);
                  const presetPercentages = Array.isArray(form?.posTips?.presetPercentages) 
                    ? form.posTips.presetPercentages 
                    : [15, 18, 20, 25];
                  const allowCustom = Boolean(form?.posTips?.allowCustom ?? true);
                  const allowNoTip = Boolean(form?.posTips?.allowNoTip ?? true);
                  const calculateOn = String(form?.posTips?.calculateOn || 'subtotal');
                  
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>POS Tips</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={tipsEnabled}
                          onChange={(e) => {
                            setError('');
                            updatePosTips({ enabled: Boolean(e.target.checked) });
                          }}
                        />
                        Enable tips for POS orders
                      </label>
                      
                      {tipsEnabled && (
                        <>
                          <div style={{ marginTop: 12, marginLeft: 24 }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 6, display: 'block' }}>
                              Preset tip percentages (comma-separated):
                            </label>
                            <input
                              type="text"
                              value={presetPercentages.join(', ')}
                              onChange={(e) => {
                                const values = e.target.value.split(',').map(v => {
                                  const num = Number(v.trim());
                                  return Number.isFinite(num) && num > 0 ? num : null;
                                }).filter(v => v !== null);
                                updatePosTips({ presetPercentages: values.length ? values : [15, 18, 20, 25] });
                              }}
                              placeholder="15, 18, 20, 25"
                              style={{ width: '100%', maxWidth: 300 }}
                            />
                            <div className="field-hint">Example: 15, 18, 20, 25</div>
                          </div>
                          
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 10, marginLeft: 24 }}>
                            <input
                              type="checkbox"
                              checked={allowCustom}
                              onChange={(e) => {
                                setError('');
                                updatePosTips({ allowCustom: Boolean(e.target.checked) });
                              }}
                            />
                            Allow custom tip amounts
                          </label>
                          
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 10, marginLeft: 24 }}>
                            <input
                              type="checkbox"
                              checked={allowNoTip}
                              onChange={(e) => {
                                setError('');
                                updatePosTips({ allowNoTip: Boolean(e.target.checked) });
                              }}
                            />
                            Allow "No Tip" option
                          </label>
                          
                          <div style={{ marginTop: 12, marginLeft: 24 }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 6, display: 'block' }}>
                              Calculate tip on:
                            </label>
                            <select
                              value={calculateOn}
                              onChange={(e) => updatePosTips({ calculateOn: e.target.value })}
                              style={{ width: '100%', maxWidth: 300 }}
                            >
                              <option value="subtotal">Subtotal (before tax)</option>
                              <option value="total_with_tax">Total (after tax)</option>
                            </select>
                          </div>
                        </>
                      )}
                      
                      <div className="field-hint">
                        When enabled, customers will see tip options during POS checkout.
                      </div>
                    </div>
                  );
                })()}

                {/* Scheduled pickup/delivery (scheduled-orders spec). Lets customers order
                    for a future time. Default off; times are interpreted in the store
                    timezone (shown read-only). The numeric limits are also enforced
                    server-side, so these inputs are a convenience, not the source of truth. */}
                {(() => {
                  const so = (form?.scheduledOrders && typeof form.scheduledOrders === 'object')
                    ? form.scheduledOrders : {};
                  const enabled = Boolean(so.enabled);
                  const tz = resolveTenantTimezone(form);
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Scheduled ordering</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => {
                            setError('');
                            updateScheduledOrders({ enabled: Boolean(e.target.checked) });
                          }}
                        />
                        Let customers schedule pickup/delivery for later
                      </label>

                      {enabled && (
                        <div style={{ marginTop: 12, marginLeft: 24 }}>
                          <div className="field-hint" style={{ marginBottom: 10 }}>
                            Times are interpreted in the store timezone: <strong>{tz}</strong>
                          </div>

                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                              How far ahead can a customer schedule? (hours)
                            </label>
                            <input
                              type="number"
                              min={1}
                              max={720}
                              value={so.maxCutoffHours ?? 72}
                              onChange={(e) => updateScheduledOrders({ maxCutoffHours: Math.max(1, Math.min(720, Number(e.target.value) || 72)) })}
                              style={{ width: 120 }}
                            />
                            <div className="field-hint">Orders beyond this are rejected. 1–720 hours.</div>
                          </div>

                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                              Minimum notice (minutes)
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={1440}
                              value={so.minLeadMinutes ?? 30}
                              onChange={(e) => updateScheduledOrders({ minLeadMinutes: Math.max(0, Math.min(1440, Number(e.target.value) || 0)) })}
                              style={{ width: 120 }}
                            />
                            <div className="field-hint">Soonest a customer may schedule from now. 0–1440.</div>
                          </div>

                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                              Show in POS queue this many minutes before the slot
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={1440}
                              value={so.posReleaseMinutes ?? 15}
                              onChange={(e) => updateScheduledOrders({ posReleaseMinutes: Math.max(0, Math.min(1440, Number(e.target.value) || 0)) })}
                              style={{ width: 120 }}
                            />
                            <div className="field-hint">
                              A scheduled order stays under "Scheduled / Upcoming" until this many
                              minutes before its time, then enters the live queue. 0–1440.
                            </div>
                          </div>

                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                              Slot interval (minutes)
                            </label>
                            <select
                              value={so.slotIntervalMinutes ?? 15}
                              onChange={(e) => updateScheduledOrders({ slotIntervalMinutes: Number(e.target.value) || 15 })}
                              style={{ width: 120 }}
                            >
                              {[5, 10, 15, 20, 30, 60].map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                            <div className="field-hint">Granularity of the times offered to customers.</div>
                          </div>

                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(so.allowPickup ?? true)}
                              onChange={(e) => updateScheduledOrders({ allowPickup: Boolean(e.target.checked) })}
                            />
                            Allow scheduled pickup
                          </label>
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(so.allowDelivery ?? true)}
                              onChange={(e) => updateScheduledOrders({ allowDelivery: Boolean(e.target.checked) })}
                            />
                            Allow scheduled delivery
                          </label>
                        </div>
                      )}

                      <div className="field-hint">
                        When off, ordering works exactly as today (ASAP only).
                      </div>
                    </div>
                  );
                })()}

                {/* Square Terminal device tips (square-terminal-checkout). Distinct from
                    "POS Tips" above (which is the ONLINE storefront tip) and from the Stripe
                    reader tip controls (which live in the Stripe reader section and never
                    show for a Square-only tenant). This is the toggle that makes the physical
                    Square Terminal prompt the customer for a tip on the device. Shown for any
                    POS/HYBRID tenant, ungated by the Stripe reader. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const squareTipsEnabled = form?.squareTerminalTipsEnabled === true;
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Square Terminal Tips</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={squareTipsEnabled}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({ ...prev, squareTerminalTipsEnabled: Boolean(e.target.checked) }));
                          }}
                          aria-describedby="squareTerminalTips-hint"
                        />
                        <span>Collect tips on the Square Terminal</span>
                      </label>
                      <div className="field-hint" id="squareTerminalTips-hint">
                        When on, the paired Square Terminal shows a tip screen to the customer
                        during a card charge. Tip amounts/percentages come from your Square
                        Dashboard settings. A guest who already tipped online is not asked again.
                      </div>
                    </div>
                  );
                })()}

                {/* CLOVER Terminal Tips (clover-terminal-charge). Shown for a Clover POS/HYBRID
                    tenant — the Clover parallel to the Square Terminal Tips block above. Writes
                    the shared terminalTipsEnabled + terminalTip* method flags that the Clover
                    charge worker reads: on-reader => the Flex prompts the customer (read-tip
                    before the charge); on-bill => staff enter the tip on the ticket before
                    charging. Default off; omit-when-absent so an untouched tenant is unchanged. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const provider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                  const show = (mode === 'POS' || mode === 'HYBRID') && provider === 'CLOVER';
                  if (!show) return null;
                  const cloverTipsEnabled = form?.terminalTipsEnabled === true;
                  const onReader = form?.terminalTipOnReader === true;
                  const onBill = form?.terminalTipOnBill === true;
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Clover Terminal Tips</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={cloverTipsEnabled}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({ ...prev, terminalTipsEnabled: Boolean(e.target.checked) }));
                          }}
                          aria-describedby="cloverTerminalTips-hint"
                        />
                        <span>Collect tips on the Clover device</span>
                      </label>
                      {cloverTipsEnabled ? (
                        <div style={{ marginTop: 10 }}>
                          <label>Tip methods</label>
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                            <input
                              type="checkbox"
                              checked={onReader}
                              onChange={(e) => { setError(''); setForm((prev) => ({ ...prev, terminalTipOnReader: Boolean(e.target.checked) })); }}
                            />
                            <span>On the reader — the Clover device asks the customer for a tip before the charge</span>
                          </label>
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                            <input
                              type="checkbox"
                              checked={onBill}
                              onChange={(e) => { setError(''); setForm((prev) => ({ ...prev, terminalTipOnBill: Boolean(e.target.checked) })); }}
                            />
                            <span>On the bill — staff enter the tip on the ticket before charging (dine-in)</span>
                          </label>
                        </div>
                      ) : null}
                      <div className="field-hint" id="cloverTerminalTips-hint">
                        When on-reader is enabled and the order has no tip yet, the Clover device
                        shows its tip screen to the customer before the card is charged, then the
                        charge includes the chosen tip. On-bill applies a tip staff entered on the
                        ticket. Tip suggestions come from the merchant&apos;s Clover settings.
                      </div>
                    </div>
                  );
                })()}

                {/* SHIFT4 Terminal Tips (shift4-payment-integration). The Shift4 parallel to the
                    Square Terminal Tips block above, and intentionally the SAME single-toggle
                    shape as Square (not Clover's on-bill/on-reader split): when on, the paired
                    Shift4 terminal prompts the customer for a tip on the device during a card
                    charge; a guest who already tipped online is not asked again. Shown ONLY for a
                    POS/HYBRID tenant whose provider is SHIFT4, so no other tenant sees it. Writes
                    shift4TerminalTipsEnabled with the same strict-bool / omit-when-absent discipline
                    as squareTerminalTipsEnabled, so an untouched tenant's settings are byte-identical. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const provider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                  const show = (mode === 'POS' || mode === 'HYBRID') && provider === 'SHIFT4';
                  if (!show) return null;
                  const shift4TipsEnabled = form?.shift4TerminalTipsEnabled === true;
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Shift4 Terminal Tips</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={shift4TipsEnabled}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({ ...prev, shift4TerminalTipsEnabled: Boolean(e.target.checked) }));
                          }}
                          aria-describedby="shift4TerminalTips-hint"
                        />
                        <span>Collect tips on the Shift4 terminal</span>
                      </label>
                      <div className="field-hint" id="shift4TerminalTips-hint">
                        When on, the paired Shift4 terminal shows a tip screen to the customer
                        during a card charge. A guest who already tipped online is not asked again.
                      </div>
                    </div>
                  );
                })()}

                {/* Scan-to-Pay QR (take-order-scan-to-pay). Terminal-free collect: staff show a
                    QR of the provider hosted-checkout URL that an in-store customer scans and pays
                    on their own phone. Shown ONLY for a POS/HYBRID tenant whose provider is one of
                    STRIPE / SQUARE / CLOVER (the US card providers with a hosted checkout), so no
                    other tenant sees it. Writes scanToPayEnabled with the same strict-bool /
                    omit-when-absent discipline as shift4TerminalTipsEnabled, so an untouched tenant's
                    settings stay byte-identical. Pay-optional and gated OFF by default. NOTE: only
                    STRIPE is wired end-to-end today; SQUARE/CLOVER are shown for parity but the
                    backend returns 503 until their adapters exist. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const provider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                  const show = (mode === 'POS' || mode === 'HYBRID') && ['STRIPE', 'SQUARE', 'CLOVER'].includes(provider);
                  if (!show) return null;
                  const scanToPayEnabled = form?.scanToPayEnabled === true;
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Scan-to-Pay (QR)</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={scanToPayEnabled}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({ ...prev, scanToPayEnabled: Boolean(e.target.checked) }));
                          }}
                          aria-describedby="scanToPay-hint"
                        />
                        <span>Show a "Collect Payment by QR" button on unpaid in-store orders</span>
                      </label>
                      <div className="field-hint" id="scanToPay-hint">
                        When on, Take Order shows a "Collect Payment by QR" button on an unpaid
                        pay-at-store order. The customer scans the QR and pays on their own phone,
                        terminal-free; the order is marked paid when the payment is confirmed.
                      </div>
                    </div>
                  );
                })()}

                {/* SHIFT4 Connect (shift4-payment-integration). Shift4 uses CREDENTIALS
                    (Client GUID + Auth Token from a Shift4 API Analyst), not an OAuth redirect,
                    so this is a small inline form rather than a "Connect" bounce. Shown ONLY for
                    a POS/HYBRID tenant whose provider is SHIFT4. Credentials are posted to
                    /admin/pos/connect/shift4 and stored server-side in Secrets Manager; they are
                    never written into settings. Fully isolated — no other tenant renders this. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const provider = String(form?.posProvider || 'SQUARE').trim().toUpperCase();
                  const show = (mode === 'POS' || mode === 'HYBRID') && provider === 'SHIFT4';
                  if (!show) return null;
                  const connectShift4 = async () => {
                    if (!API_BASE_URL) { setError('Missing API base URL.'); return; }
                    const guid = String(shift4ClientGuidInput || '').trim();
                    const tok = String(shift4AuthTokenInput || '').trim();
                    const term = String(shift4TerminalIdInput || '').trim();
                    if (!guid || !tok) {
                      setShift4ConnectResult({ ok: false, message: 'Enter both Client GUID and Auth Token.' });
                      return;
                    }
                    setShift4ConnectResult({ ok: null, message: 'Connecting to Shift4…' });
                    try {
                      const session = await Auth.currentSession();
                      const token = session.getIdToken().getJwtToken();
                      const res = await fetch(`${API_BASE_URL}/admin/pos/connect/shift4`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          clientGuid: guid,
                          authToken: tok,
                          ...(term ? { terminalDeviceId: term } : {}),
                        }),
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(data?.error || 'Shift4 connect failed');
                      setShift4ConnectResult({ ok: true, message: 'Shift4 connected.' });
                      // Clear the typed secrets from memory once stored.
                      setShift4ClientGuidInput('');
                      setShift4AuthTokenInput('');
                    } catch (err) {
                      setShift4ConnectResult({ ok: false, message: err?.message || 'Shift4 connect failed' });
                    }
                  };
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Shift4 Connection</label>
                      <input
                        type="text"
                        placeholder="Client GUID"
                        value={shift4ClientGuidInput}
                        onChange={(e) => setShift4ClientGuidInput(e.target.value)}
                        style={{ width: '100%', maxWidth: 360, marginBottom: 8 }}
                      />
                      <input
                        type="password"
                        placeholder="Auth Token"
                        value={shift4AuthTokenInput}
                        onChange={(e) => setShift4AuthTokenInput(e.target.value)}
                        style={{ width: '100%', maxWidth: 360, marginBottom: 8 }}
                      />
                      <input
                        type="text"
                        placeholder="Terminal device id (optional, for card-present)"
                        value={shift4TerminalIdInput}
                        onChange={(e) => setShift4TerminalIdInput(e.target.value)}
                        style={{ width: '100%', maxWidth: 360, marginBottom: 8 }}
                      />
                      <button type="button" className="btn-secondary" onClick={connectShift4} style={{ marginTop: 4 }}>
                        Connect Shift4
                      </button>
                      {shift4ConnectResult ? (
                        <div className="field-hint" style={{ color: shift4ConnectResult.ok === false ? '#b00020' : undefined }}>
                          {shift4ConnectResult.message}
                        </div>
                      ) : (
                        <div className="field-hint">
                          Enter the Client GUID and Auth Token from your Shift4 API Analyst. The
                          terminal id is only needed for card-present "charge on terminal".
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* "Square App" button visibility (square-terminal-checkout). The POS queue's
                    Take-Payment action opens the Square POS app / a payment link — redundant
                    for a tenant that collects via the Square Terminal directly. Default OFF;
                    turn on only if you actually ring up in the Square app. Shown for POS/HYBRID. */}
                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const squareAppEnabled = form?.squareAppButtonEnabled === true;
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Square App Payment</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={squareAppEnabled}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({ ...prev, squareAppButtonEnabled: Boolean(e.target.checked) }));
                          }}
                          aria-describedby="squareAppButton-hint"
                        />
                        <span>Show the "Square App" button on the POS queue</span>
                      </label>
                      <div className="field-hint" id="squareAppButton-hint">
                        Off by default. Turn on only if you collect payment in the Square POS app.
                        Most setups pay on the Square Terminal directly, which does not need this.
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const enabled = Boolean(form?.features?.mock_online_payment_enabled);
                  const posDiscountsEnabled = Boolean(form?.features?.pos_discount_codes_enabled);
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Test payment mode</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => updateFeatures({ mock_online_payment_enabled: e.target.checked })}
                        />
                        Enable mock online payment (storefront)
                      </label>
                      <div className="field-hint">
                        When enabled, the storefront will show Pay now as a test payment option.
                      </div>

                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                        <input
                          type="checkbox"
                          checked={posDiscountsEnabled}
                          onChange={(e) => updateFeatures({ pos_discount_codes_enabled: e.target.checked })}
                        />
                        Enable POS discount codes
                      </label>
                      <div className="field-hint">
                        When enabled, the POS cart will show a discount code field and apply it during Stripe checkout.
                      </div>
                    </div>
                  );
                })()}

                {/* Storefront AI Chatbot — visible for all store modes */}
                <div className="form-group" style={{ minWidth: 280 }}>
                  <label>AI Chatbot</label>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form?.features?.chatbot_enabled)}
                      onChange={(e) => updateFeatures({ chatbot_enabled: e.target.checked })}
                    />
                    Enable storefront AI chatbot
                  </label>
                  <div className="field-hint">
                    When enabled, a chat widget appears on the storefront. Requires the provider key to be set in SSM.
                  </div>
                </div>

              </div>
            </section>
          </details>

          <details open style={{ marginTop: 22 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Reports</summary>
            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
              <div className="form-row" style={{ gap: 14, alignItems: 'end' }}>
                <div className="form-group" style={{ minWidth: 280 }}>
                  <label>Enabled report types</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                    {reportTypeOptions.map((opt) => (
                      <label
                        key={opt.k}
                        className="toggle-chip"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={enabledReportTypes.includes(opt.k)}
                          onChange={() => toggleReportType(opt.k)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  <div className="field-hint">Controls which report types appear in the Reports dropdown.</div>
                </div>
              </div>
            </section>
          </details>

          {(() => {
            const mode = String(form?.storeMode ?? '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const enabled = Boolean(orderingClosedHours?.enabled);
            const tzValue = String(orderingClosedHours?.timezone || 'America/New_York');
            const messageValue = String(orderingClosedHours?.message || '');
            const weekly = Array.isArray(orderingClosedHours?.weekly) ? orderingClosedHours.weekly : [];
            const oneOff = Array.isArray(orderingClosedHours?.oneOff) ? orderingClosedHours.oneOff : [];

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Ordering availability (POS)</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updateOrderingClosedHours({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable scheduled closed hours
                  </label>

                  <div className="form-row" style={{ gap: 14, alignItems: 'end', marginTop: 6 }}>
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Timezone</label>
                      <select
                        value={tzValue}
                        onChange={(e) => updateOrderingClosedHours({ timezone: e.target.value })}
                        disabled={!enabled}
                      >
                        <option value="America/New_York">Eastern (America/New_York)</option>
                        <option value="America/Chicago">Central (America/Chicago)</option>
                        <option value="America/Denver">Mountain (America/Denver)</option>
                        <option value="America/Phoenix">Arizona (America/Phoenix)</option>
                        <option value="America/Los_Angeles">Pacific (America/Los_Angeles)</option>
                        <option value="America/Anchorage">Alaska (America/Anchorage)</option>
                        <option value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</option>
                        <option value="Asia/Kolkata">India (Asia/Kolkata, IST)</option>
                        <option value="Australia/Sydney">Sydney (Australia/Sydney, AEST/AEDT)</option>
                        <option value="Australia/Melbourne">Melbourne (Australia/Melbourne)</option>
                        <option value="Australia/Brisbane">Brisbane (Australia/Brisbane, no DST)</option>
                        <option value="Australia/Adelaide">Adelaide (Australia/Adelaide)</option>
                        <option value="Australia/Perth">Perth (Australia/Perth, AWST)</option>
                        <option value="Etc/GMT">GMT (Etc/GMT)</option>
                      </select>
                      <div className="field-hint">Used to evaluate scheduled closures.</div>
                    </div>

                    <div className="form-group" style={{ flex: 1, minWidth: 320 }}>
                      <label>Customer message</label>
                      <input
                        value={messageValue}
                        onChange={(e) => updateOrderingClosedHours({ message: e.target.value })}
                        disabled={!enabled}
                        placeholder="We’re currently closed. Please come back during open hours."
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: 18, marginTop: 18 }}>
                    <section style={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 14, padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <h3 style={{ margin: 0 }}>Weekly recurring closures</h3>
                        <button type="button" className="btn-secondary" onClick={addWeeklyClosedRule} disabled={!enabled}>
                          Add
                        </button>
                      </div>

                      {weekly.length === 0 ? (
                        <div className="field-hint" style={{ marginTop: 10 }}>No weekly rules.</div>
                      ) : (
                        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                          {weekly.map((row) => (
                            <div
                              key={String(row?.id || '')}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 160px 140px 140px 1fr auto',
                                gap: 10,
                                alignItems: 'center',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(row?.enabled ?? true)}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { enabled: Boolean(e.target.checked) })}
                                disabled={!enabled}
                                aria-label="Enable weekly rule"
                              />
                              <select
                                value={String(row?.weekday || 'MON')}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { weekday: e.target.value })}
                                disabled={!enabled}
                              >
                                <option value="ALL">All days</option>
                                <option value="MON">Mon</option>
                                <option value="TUE">Tue</option>
                                <option value="WED">Wed</option>
                                <option value="THU">Thu</option>
                                <option value="FRI">Fri</option>
                                <option value="SAT">Sat</option>
                                <option value="SUN">Sun</option>
                              </select>
                              <input
                                type="time"
                                value={String(row?.start_hhmm || '09:00')}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { start_hhmm: e.target.value })}
                                disabled={!enabled}
                              />
                              <input
                                type="time"
                                value={String(row?.end_hhmm || '17:00')}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { end_hhmm: e.target.value })}
                                disabled={!enabled}
                              />
                              <input
                                value={String(row?.label || '')}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { label: e.target.value })}
                                disabled={!enabled}
                                placeholder="Label (optional)"
                              />
                              <button type="button" className="btn-secondary" onClick={() => deleteWeeklyClosedRule(row?.id)} disabled={!enabled}>
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="field-hint" style={{ marginTop: 10 }}>
                        Cross-midnight is supported (end time can be earlier than start time).
                      </div>
                    </section>

                    <section style={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 14, padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <h3 style={{ margin: 0 }}>One-off closures</h3>
                        <button type="button" className="btn-secondary" onClick={addOneOffClosedRule} disabled={!enabled}>
                          Add
                        </button>
                      </div>

                      {oneOff.length === 0 ? (
                        <div className="field-hint" style={{ marginTop: 10 }}>No one-off rules.</div>
                      ) : (
                        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                          {oneOff.map((row) => (
                            <div
                              key={String(row?.id || '')}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 240px 240px 1fr auto',
                                gap: 10,
                                alignItems: 'center',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(row?.enabled ?? true)}
                                onChange={(e) => updateOneOffClosedRule(row?.id, { enabled: Boolean(e.target.checked) })}
                                disabled={!enabled}
                                aria-label="Enable one-off rule"
                              />
                              <input
                                type="datetime-local"
                                value={String(row?.start_iso || '')}
                                onChange={(e) => updateOneOffClosedRule(row?.id, { start_iso: e.target.value })}
                                disabled={!enabled}
                              />
                              <input
                                type="datetime-local"
                                value={String(row?.end_iso || '')}
                                onChange={(e) => updateOneOffClosedRule(row?.id, { end_iso: e.target.value })}
                                disabled={!enabled}
                              />
                              <input
                                value={String(row?.label || '')}
                                onChange={(e) => updateOneOffClosedRule(row?.id, { label: e.target.value })}
                                disabled={!enabled}
                                placeholder="Label (optional)"
                              />
                              <button type="button" className="btn-secondary" onClick={() => deleteOneOffClosedRule(row?.id)} disabled={!enabled}>
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                </section>
              </details>
            );
          })()}

          {(() => {
            const mode = String(form?.storeMode ?? '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const enabled = Boolean(serviceWindows?.enabled);
            const tzValue = String(serviceWindows?.timezone || 'America/New_York');
            const windows = Array.isArray(serviceWindows?.windows) ? serviceWindows.windows : [];

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Service windows (POS)</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updateServiceWindows({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable service windows
                  </label>

                  <div className="form-row" style={{ gap: 14, alignItems: 'end', marginTop: 6 }}>
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Timezone</label>
                      <select value={tzValue} onChange={(e) => updateServiceWindows({ timezone: e.target.value })} disabled={!enabled}>
                        <option value="America/New_York">Eastern (America/New_York)</option>
                        <option value="America/Chicago">Central (America/Chicago)</option>
                        <option value="America/Denver">Mountain (America/Denver)</option>
                        <option value="America/Phoenix">Arizona (America/Phoenix)</option>
                        <option value="America/Los_Angeles">Pacific (America/Los_Angeles)</option>
                        <option value="America/Anchorage">Alaska (America/Anchorage)</option>
                        <option value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</option>
                        <option value="Asia/Kolkata">India (Asia/Kolkata, IST)</option>
                        <option value="Australia/Sydney">Sydney (Australia/Sydney, AEST/AEDT)</option>
                        <option value="Australia/Melbourne">Melbourne (Australia/Melbourne)</option>
                        <option value="Australia/Brisbane">Brisbane (Australia/Brisbane, no DST)</option>
                        <option value="Australia/Adelaide">Adelaide (Australia/Adelaide)</option>
                        <option value="Australia/Perth">Perth (Australia/Perth, AWST)</option>
                        <option value="Etc/GMT">GMT (Etc/GMT)</option>
                      </select>
                      <div className="field-hint">Used to determine Breakfast/Lunch/Dinner windows.</div>
                    </div>
                  </div>

                  <section style={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 14, padding: 14, marginTop: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <h3 style={{ margin: 0 }}>Windows</h3>
                      <button type="button" className="btn-secondary" onClick={addServiceWindowRule} disabled={!enabled}>
                        Add
                      </button>
                    </div>

                    {windows.length === 0 ? (
                      <div className="field-hint" style={{ marginTop: 10 }}>No windows configured.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                        {windows.map((row) => {
                          const id = String(row?.id || '');
                          const rowDays = Array.isArray(row?.days) ? row.days : [];
                          const setDays = (days) => updateServiceWindowRule(id, { days });
                          const toggleDay = (day) => {
                            const d = String(day || '').trim().toUpperCase();
                            if (!d) return;
                            if (d === 'ALL') {
                              setDays(['ALL']);
                              return;
                            }
                            const current = rowDays.includes('ALL') ? [] : rowDays;
                            const next = current.includes(d) ? current.filter((x) => x !== d) : [...current, d];
                            setDays(next.length ? next : ['ALL']);
                          };

                          return (
                            <div key={id} style={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 14, padding: 12 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 140px 140px auto', gap: 10, alignItems: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(row?.enabled ?? true)}
                                  onChange={(e) => updateServiceWindowRule(id, { enabled: Boolean(e.target.checked) })}
                                  disabled={!enabled}
                                  aria-label="Enable window"
                                />
                                <input
                                  value={String(row?.name || '')}
                                  onChange={(e) => updateServiceWindowRule(id, { name: e.target.value })}
                                  disabled={!enabled}
                                  placeholder="e.g. Breakfast"
                                />
                                <input
                                  type="time"
                                  value={String(row?.start_hhmm || '09:00')}
                                  onChange={(e) => updateServiceWindowRule(id, { start_hhmm: e.target.value })}
                                  disabled={!enabled}
                                />
                                <input
                                  type="time"
                                  value={String(row?.end_hhmm || '17:00')}
                                  onChange={(e) => updateServiceWindowRule(id, { end_hhmm: e.target.value })}
                                  disabled={!enabled}
                                />
                                <button type="button" className="btn-secondary" onClick={() => deleteServiceWindowRule(id)} disabled={!enabled}>
                                  Delete
                                </button>
                              </div>

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                                {['ALL', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d) => {
                                  const selected = d === 'ALL' ? rowDays.includes('ALL') || rowDays.length === 0 : (!rowDays.includes('ALL') && rowDays.includes(d));
                                  return (
                                    <label key={`${id}-${d}`} className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: 0, opacity: enabled ? 1 : 0.6 }}>
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => toggleDay(d)}
                                        disabled={!enabled}
                                      />
                                      {d === 'ALL' ? 'All days' : d}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="field-hint" style={{ marginTop: 10 }}>Cross-midnight is supported (end time can be earlier than start time).</div>
                  </section>
                </section>
              </details>
            );
          })()}

          {(() => {
            const mode = String(form?.storeMode ?? '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const enabled = Boolean(posDeliveryLinks?.enabled);
            const vendors = Array.isArray(posDeliveryLinks?.vendors) ? posDeliveryLinks.vendors : [];

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>POS Delivery (External links)</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updatePosDeliveryLinks({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable Delivery button / external providers
                  </label>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    <div style={{ fontWeight: 800 }}>Delivery providers</div>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={addDeliveryVendor}
                      disabled={!enabled}
                    >
                      Add
                    </button>
                  </div>

                  <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                    {vendors.length ? vendors.map((row) => {
                      const id = row?.id;
                      const preview = String(row?.imageUrl || '') || (typeof row?.imageUpload?.data === 'string' ? row.imageUpload.data : '');
                      return (
                        <div
                          key={String(id || '')}
                          style={{
                            border: '1px solid rgba(148, 163, 184, 0.35)',
                            borderRadius: 14,
                            padding: 12,
                            display: 'grid',
                            gap: 10,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                            <div style={{ fontWeight: 800 }}>Provider</div>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => deleteDeliveryVendor(id)}
                              disabled={!enabled}
                            >
                              Delete
                            </button>
                          </div>

                          <div className="form-row" style={{ gap: 12 }}>
                            <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
                              <label>Name</label>
                              <input
                                value={String(row?.name || '')}
                                onChange={(e) => updateDeliveryVendor(id, { name: e.target.value })}
                                disabled={!enabled}
                                placeholder="e.g. DoorDash"
                              />
                            </div>
                            <div className="form-group" style={{ flex: 2, minWidth: 320 }}>
                              <label>Restaurant URL</label>
                              <input
                                value={String(row?.url || '')}
                                onChange={(e) => updateDeliveryVendor(id, { url: e.target.value })}
                                disabled={!enabled}
                                placeholder="https://..."
                              />
                            </div>
                          </div>

                          <div className="form-row" style={{ gap: 12, alignItems: 'center' }}>
                            <div className="form-group" style={{ minWidth: 260 }}>
                              <label>Logo (optional)</label>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                onChange={(e) => handleDeliveryVendorImageUpload(id, e.target.files?.[0])}
                                disabled={!enabled}
                              />
                            </div>
                            {preview ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                <img
                                  src={preview}
                                  alt="Vendor logo preview"
                                  style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'cover', border: '1px solid rgba(148, 163, 184, 0.45)' }}
                                />
                                <div style={{ color: 'rgba(15, 23, 42, 0.7)', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {String(row?.imageUrl || '') ? 'Saved logo' : 'New upload (will save on Save)'}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="field-hint">No providers added yet.</div>
                    )}
                  </div>
                </section>
              </details>
            );
          })()}

          {/* Internal Delivery Settings */}
          {(() => {
            const mode = String(form?.storeMode || '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const delivery = form?.delivery || {};
            const enabled = Boolean(delivery.enabled);
            const feeCents = Number(delivery.fee_cents) || 0;
            const minimumCents = Number(delivery.minimum_order_cents) || 0;
            
            // Radius restriction settings
            const radiusRestriction = delivery.radius_restriction || {};
            const radiusEnabled = Boolean(radiusRestriction.enabled);
            const baseZipCode = String(radiusRestriction.base_zip_code || '').trim();
            const maxRadiusMiles = Number(radiusRestriction.max_radius_miles) || 10.0;
            const maxRadiusKm = Number(radiusRestriction.max_radius_km) || 5.0;
            // India (INR) stores configure a 6-digit PIN + km radius; every other
            // store keeps the US 5-digit ZIP + miles UI, so nothing changes for them.
            const isInrStore = String(form?.currency || 'USD').trim().toUpperCase() === 'INR';
            // Australia (AUD) stores configure a 4-digit postcode + km radius.
            // Used only by the radius block; courier editors still key on isInrStore.
            const isAudStore = String(form?.currency || 'USD').trim().toUpperCase() === 'AUD';
            const radiusUsesKm = isInrStore || isAudStore;
            const radiusPostalLabel = isInrStore ? 'PIN code' : isAudStore ? 'postcode' : 'ZIP code';
            // Currency-aware labels/hints so the money fields reflect the tenant's
            // selected currency (₹/INR for India) instead of a hardcoded "$"/USD.
            const curCode = normalizeCurrency(form?.currency);
            const curSym = currencySymbol(curCode);

            // Uber Direct on-demand courier (nested under internal delivery)
            const uberDirect = (delivery.uber_direct && typeof delivery.uber_direct === 'object') ? delivery.uber_direct : {};
            const udEnabled = Boolean(uberDirect.enabled);
            const udMock = Boolean(uberDirect.mock);
            const udEnvironment = uberDirect.environment === 'production' ? 'production' : 'sandbox';
            const udClientId = String(uberDirect.client_id || '').trim();
            const udCustomerId = String(uberDirect.customer_id || '').trim();
            const udHasSecret = Boolean(uberDirect.has_secret);
            const udHasWebhookSecret = Boolean(uberDirect.has_webhook_secret);
            const udPricingRule = (uberDirect.pricing_rule && typeof uberDirect.pricing_rule === 'object') ? uberDirect.pricing_rule : {};
            const udPricingMode = ['pass_through', 'flat', 'markup', 'subsidized'].includes(udPricingRule.mode) ? udPricingRule.mode : 'pass_through';
            const udPickup = (uberDirect.pickup_address && typeof uberDirect.pickup_address === 'object') ? uberDirect.pickup_address : {};

            // Borzo India courier (nested under internal delivery). Borzo is the
            // India equivalent of Uber Direct, which does not operate there, so
            // the editors are mutually exclusive: Borzo for INR tenants, Uber
            // Direct for everyone else. An admin never sees both at once.
            const borzo = (delivery.borzo && typeof delivery.borzo === 'object') ? delivery.borzo : {};
            const bzEnabled = Boolean(borzo.enabled);
            const bzMock = Boolean(borzo.mock);
            const bzEnvironment = borzo.environment === 'production' ? 'production' : 'sandbox';
            // Stored-credential indicators. The API never returns the real
            // values, only these flags plus a mask sentinel.
            const bzHasToken = Boolean(borzo.has_token);
            const bzHasWebhookSecret = Boolean(borzo.has_webhook_secret);
            const bzPricingRule = (borzo.pricing_rule && typeof borzo.pricing_rule === 'object') ? borzo.pricing_rule : {};
            const bzPricingMode = ['pass_through', 'flat', 'markup', 'subsidized'].includes(bzPricingRule.mode) ? bzPricingRule.mode : 'pass_through';
            const bzPickup = (borzo.pickup_address && typeof borzo.pickup_address === 'object') ? borzo.pickup_address : {};
            const typedBorzoToken = String(borzoTokenInput || '').trim();
            // A token is "usable" if one is already stored or the admin typed a
            // new one in this session.
            const bzHasUsableToken = bzHasToken || Boolean(typedBorzoToken);

            const updateDelivery = (patch) => {
              setForm((prev) => ({
                ...prev,
                delivery: {
                  enabled: false,
                  fee_cents: 0,
                  minimum_order_cents: 0,
                  radius_restriction: {
                    enabled: false,
                    base_zip_code: '',
                    max_radius_miles: 10.0,
                  },
                  ...((prev.delivery && typeof prev.delivery === 'object') ? prev.delivery : {}),
                  ...(patch || {}),
                },
              }));
            };
            
            const updateRadiusRestriction = (patch) => {
              updateDelivery({
                radius_restriction: {
                  enabled: false,
                  base_zip_code: '',
                  max_radius_miles: 10.0,
                  ...((delivery.radius_restriction && typeof delivery.radius_restriction === 'object') ? delivery.radius_restriction : {}),
                  ...(patch || {}),
                },
              });
            };

            const updateUberDirect = (patch) => {
              updateDelivery({
                uber_direct: {
                  enabled: false,
                  environment: 'sandbox',
                  client_id: '',
                  customer_id: '',
                  pricing_rule: {
                    mode: 'pass_through',
                    flat_cents: 0,
                    markup_cents: 0,
                    markup_percent: 0,
                    subsidized_customer_cents: 0,
                  },
                  ...((delivery.uber_direct && typeof delivery.uber_direct === 'object') ? delivery.uber_direct : {}),
                  ...(patch || {}),
                },
              });
            };

            const updateUberDirectPickup = (patch) => {
              updateUberDirect({
                pickup_address: {
                  name: '',
                  phone: '',
                  street: '',
                  apt_unit: '',
                  city: '',
                  state: '',
                  zip: '',
                  ...((uberDirect.pickup_address && typeof uberDirect.pickup_address === 'object') ? uberDirect.pickup_address : {}),
                  ...(patch || {}),
                },
              });
            };

            const updateUberDirectPricing = (patch) => {
              updateUberDirect({
                pricing_rule: {
                  mode: 'pass_through',
                  flat_cents: 0,
                  markup_cents: 0,
                  markup_percent: 0,
                  subsidized_customer_cents: 0,
                  ...((uberDirect.pricing_rule && typeof uberDirect.pricing_rule === 'object') ? uberDirect.pricing_rule : {}),
                  ...(patch || {}),
                },
              });
            };

            const updateBorzo = (patch) => {
              updateDelivery({
                borzo: {
                  enabled: false,
                  mock: false,
                  environment: 'sandbox',
                  pricing_rule: {
                    mode: 'pass_through',
                    flat_cents: 0,
                    markup_cents: 0,
                    markup_percent: 0,
                    subsidized_customer_cents: 0,
                  },
                  ...((delivery.borzo && typeof delivery.borzo === 'object') ? delivery.borzo : {}),
                  ...(patch || {}),
                },
              });
            };

            const updateBorzoPickup = (patch) => {
              updateBorzo({
                pickup_address: {
                  name: '',
                  phone: '',
                  street: '',
                  apt_unit: '',
                  city: '',
                  state: '',
                  zip: '',
                  ...((borzo.pickup_address && typeof borzo.pickup_address === 'object') ? borzo.pickup_address : {}),
                  ...(patch || {}),
                },
              });
            };

            const updateBorzoPricing = (patch) => {
              updateBorzo({
                pricing_rule: {
                  mode: 'pass_through',
                  flat_cents: 0,
                  markup_cents: 0,
                  markup_percent: 0,
                  subsidized_customer_cents: 0,
                  ...((borzo.pricing_rule && typeof borzo.pricing_rule === 'object') ? borzo.pricing_rule : {}),
                  ...(patch || {}),
                },
              });
            };

            // Borzo connection test. Mirrors the Uber Eats / DoorDash handlers:
            // POSTs to the admin connection-test route with a bearer token and
            // renders only the provider's success/failure message. The typed
            // token is sent only when the admin entered a new one this session,
            // and no credential value is ever rendered back.
            const handleBorzoTestConnection = async () => {
              if (!bzMock && !bzHasUsableToken) {
                const msg = 'Provide the Borzo auth token (or enable mock mode) before testing.';
                setError(msg);
                setBorzoTestResult({ ok: false, message: msg });
                return;
              }
              setStatusMessage('Testing Borzo connection...');
              setError('');
              setBorzoTestResult({ ok: null, message: 'Testing Borzo connection...' });
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const body = {
                  environment: bzEnvironment,
                  mock: bzMock,
                };
                // Only include the token if the admin typed a new one this
                // session; otherwise the backend uses the stored token.
                if (typedBorzoToken) body.auth_token = typedBorzoToken;

                const response = await fetch(`${API_BASE_URL}/admin/borzo/connection/test`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(body),
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok && data.success) {
                  const msg = data.message || 'Borzo connection successful!';
                  setStatusMessage(msg);
                  setBorzoTestResult({ ok: true, message: msg });
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  const msg = data.error || data.message || 'Borzo connection test failed';
                  setError(msg);
                  setStatusMessage('');
                  setBorzoTestResult({ ok: false, message: msg });
                }
              } catch (err) {
                console.error('Borzo connection test error:', err);
                const msg = `Borzo connection test failed: ${err.message}`;
                setError(msg);
                setStatusMessage('');
                setBorzoTestResult({ ok: false, message: msg });
              }
            };

            // Address autocomplete is a top-level setting (independent of delivery state).
            // Admin only toggles `enabled`; the Geoapify key is delivered out-of-band.
            const addressAutocomplete = form?.addressAutocomplete || {};
            const autocompleteEnabled = Boolean(addressAutocomplete.enabled);
            const updateAddressAutocomplete = (patch) => {
              setForm((prev) => ({
                ...prev,
                addressAutocomplete: {
                  provider: 'geoapify',
                  enabled: false,
                  ...((prev.addressAutocomplete && typeof prev.addressAutocomplete === 'object') ? prev.addressAutocomplete : {}),
                  ...(patch || {}),
                },
              }));
            };

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Internal Delivery</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updateDelivery({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable internal delivery option
                  </label>

                  <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                    When enabled, customers can choose delivery at checkout and provide a delivery address. This is separate from external delivery provider links (DoorDash, Uber Eats, etc.).
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Delivery Fee ({curCode})</label>
                      <PriceInput
                        value={feeCents}
                        onChange={(cents) => updateDelivery({ fee_cents: cents })}
                        disabled={!enabled}
                        placeholder="5.00"
                      />
                      <div className="field-hint">Flat fee added to delivery orders (e.g., {curSym}5.00)</div>
                    </div>

                    <div className="form-group">
                      <label>Minimum Order ({curCode})</label>
                      <PriceInput
                        value={minimumCents}
                        onChange={(cents) => updateDelivery({ minimum_order_cents: cents })}
                        disabled={!enabled}
                        placeholder="10.00"
                      />
                      <div className="field-hint">Minimum subtotal required for delivery (e.g., {curSym}10.00). Set to 0 for no minimum.</div>
                    </div>
                  </div>

                  {/* Radius Restriction Configuration */}
                  {enabled && (
                    <div style={{ marginTop: 20, padding: 16, background: 'rgba(99, 102, 241, 0.05)', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <input
                          type="checkbox"
                          checked={radiusEnabled}
                          onChange={(e) => updateRadiusRestriction({ enabled: Boolean(e.target.checked) })}
                        />
                        <span style={{ fontWeight: 600 }}>Restrict delivery by distance</span>
                      </label>

                      <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                        When enabled, delivery addresses will be validated against a maximum distance from your store location. Orders outside the radius will be rejected with a helpful message.
                      </div>

                      {radiusEnabled && (
                        <div className="form-row" style={{ gap: 14 }}>
                          <div className="form-group">
                            <label>{isInrStore ? 'Store PIN Code' : isAudStore ? 'Store Postcode' : 'Store ZIP Code'}</label>
                            <input
                              type="text"
                              value={baseZipCode}
                              onChange={(e) => {
                                const value = isInrStore
                                  ? e.target.value.replace(/\D/g, '').slice(0, 6)
                                  : isAudStore
                                    ? e.target.value.replace(/\D/g, '').slice(0, 4)
                                    : e.target.value.replace(/[^0-9-]/g, '').slice(0, 10);
                                updateRadiusRestriction({ base_zip_code: value });
                              }}
                              placeholder={isInrStore ? '600001' : isAudStore ? '2000' : '12345'}
                              maxLength={isInrStore ? 6 : isAudStore ? 4 : 10}
                              pattern={isInrStore ? '[0-9]{6}' : isAudStore ? '[0-9]{4}' : '[0-9]{5}(-[0-9]{4})?'}
                            />
                            <div className="field-hint">
                              {isInrStore
                                ? "Your store's 6-digit PIN code (e.g., 600001)"
                                : isAudStore
                                  ? "Your store's 4-digit postcode (e.g., 2000)"
                                  : "Your store's ZIP code (5 digits, e.g., 12345)"}
                            </div>
                          </div>

                          {radiusUsesKm ? (
                            <div className="form-group">
                              <label>Maximum Delivery Distance (km)</label>
                              <input
                                type="number"
                                value={maxRadiusKm}
                                onChange={(e) => {
                                  const value = Math.max(0.1, Math.min(100, parseFloat(e.target.value) || 5.0));
                                  updateRadiusRestriction({ max_radius_km: value });
                                }}
                                min="0.1"
                                max="100"
                                step="0.5"
                                placeholder="5.0"
                              />
                              <div className="field-hint">Maximum distance in km (0.1 to 100 km)</div>
                            </div>
                          ) : (
                            <div className="form-group">
                              <label>Maximum Delivery Distance (miles)</label>
                              <input
                                type="number"
                                value={maxRadiusMiles}
                                onChange={(e) => {
                                  const value = Math.max(0.1, Math.min(100, parseFloat(e.target.value) || 10.0));
                                  updateRadiusRestriction({ max_radius_miles: value });
                                }}
                                min="0.1"
                                max="100"
                                step="0.1"
                                placeholder="10.0"
                              />
                              <div className="field-hint">Maximum distance in miles (0.1 to 100 miles)</div>
                            </div>
                          )}
                        </div>
                      )}

                      {radiusEnabled && baseZipCode && (radiusUsesKm ? maxRadiusKm > 0 : maxRadiusMiles > 0) && (
                        <div style={{ marginTop: 12, padding: 10, background: 'rgba(34, 197, 94, 0.08)', borderRadius: 6, border: '1px solid rgba(34, 197, 94, 0.25)' }}>
                          <div style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                            <strong>Delivery Area:</strong> {radiusUsesKm
                              ? `Within ${maxRadiusKm} km of ${radiusPostalLabel} ${baseZipCode}`
                              : `Within ${maxRadiusMiles} miles of ZIP code ${baseZipCode}`}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: 'rgba(15, 23, 42, 0.6)', marginTop: 4 }}>
                            {radiusUsesKm
                              ? `Customers outside this area will see: "This address is X km from our location. We only deliver within ${maxRadiusKm} km."`
                              : `Customers outside this area will see: "This address is X miles from our location. We only deliver within ${maxRadiusMiles} miles."`}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Uber Direct on-demand courier (nested under internal delivery).
                      Hidden for INR tenants — Uber Direct does not operate in
                      India, so those tenants get the Borzo editor below instead. */}
                  {enabled && !isInrStore && (
                    <div style={{ marginTop: 20, padding: 16, background: 'rgba(16, 185, 129, 0.05)', borderRadius: 8, border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <input
                          type="checkbox"
                          checked={udEnabled}
                          onChange={(e) => updateUberDirect({ enabled: Boolean(e.target.checked) })}
                        />
                        <span style={{ fontWeight: 600 }}>Enable Uber Direct courier delivery</span>
                      </label>

                      <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                        When enabled, delivery orders on your storefront get a live Uber Direct courier quote at checkout and a driver is dispatched automatically after payment (no commission on food, pay per delivery). When disabled, internal delivery uses your flat delivery fee exactly as before.
                      </div>

                      {udEnabled && (
                        <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                          <input
                            type="checkbox"
                            checked={udMock}
                            onChange={(e) => updateUberDirect({ mock: Boolean(e.target.checked) })}
                          />
                          <span style={{ fontWeight: 600 }}>Simulate courier (mock — no Uber account needed)</span>
                        </label>
                      )}

                      {udEnabled && udMock && (
                        <div className="field-hint" style={{ marginBottom: 12, color: '#137484' }}>
                          Mock mode is ON: checkout shows a simulated courier quote and orders get a fake delivery/tracking — no live Uber call, no charges. Credentials below are optional while mocking. Turn this off to use the real Uber Direct API.
                        </div>
                      )}

                      {udEnabled && !udMock && (
                        <>
                          <div className="form-row" style={{ gap: 14 }}>
                            <div className="form-group">
                              <label>Environment</label>
                              <select
                                value={udEnvironment}
                                onChange={(e) => updateUberDirect({ environment: e.target.value === 'production' ? 'production' : 'sandbox' })}
                              >
                                <option value="sandbox">Sandbox (testing, no real deliveries)</option>
                                <option value="production">Production (live couriers)</option>
                              </select>
                              <div className="field-hint">Use Sandbox until you're ready to dispatch real deliveries.</div>
                            </div>

                            <div className="form-group">
                              <label>Customer / Organization ID</label>
                              <input
                                type="text"
                                value={udCustomerId}
                                onChange={(e) => updateUberDirect({ customer_id: e.target.value })}
                                placeholder="cus_..."
                              />
                              <div className="field-hint">Your Uber Direct organization ID (from the Uber developer dashboard).</div>
                            </div>
                          </div>

                          <div className="form-row" style={{ gap: 14 }}>
                            <div className="form-group">
                              <label>Client ID</label>
                              <input
                                type="text"
                                value={udClientId}
                                onChange={(e) => updateUberDirect({ client_id: e.target.value })}
                                placeholder="Uber Direct client ID"
                              />
                            </div>

                            <div className="form-group">
                              <label>Client Secret</label>
                              <input
                                type="password"
                                value={uberDirectSecretInput}
                                onChange={(e) => setUberDirectSecretInput(e.target.value)}
                                placeholder={udHasSecret ? '•••••••• (secret saved — leave blank to keep)' : 'Enter Uber Direct client secret'}
                              />
                              <div className="field-hint">Write-only. Leave blank to keep the currently stored secret.</div>
                            </div>
                          </div>

                          <div className="form-row" style={{ gap: 14 }}>
                            <div className="form-group">
                              <label>Webhook Signing Secret</label>
                              <input
                                type="password"
                                value={uberDirectWebhookSecretInput}
                                onChange={(e) => setUberDirectWebhookSecretInput(e.target.value)}
                                placeholder={udHasWebhookSecret ? '•••••••• (saved — leave blank to keep)' : 'Enter Uber Direct webhook signing secret'}
                              />
                              <div className="field-hint">
                                From your Uber Direct webhook config. Used to verify delivery status updates. Point the Uber webhook at <code>/public/delivery/uber-direct/webhook</code>.
                              </div>
                            </div>
                          </div>

                          <div style={{ marginTop: 10, paddingTop: 12, borderTop: '1px dashed rgba(16, 185, 129, 0.35)' }}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>Courier Pickup Address (private)</div>
                            <div className="field-hint" style={{ marginBottom: 12 }}>
                              The real address the courier picks up from. Sent only to Uber — never shown on your public storefront. Leave blank to use your store location's address instead.
                            </div>
                            <div className="form-row" style={{ gap: 14 }}>
                              <div className="form-group">
                                <label>Pickup Location Name</label>
                                <input
                                  type="text"
                                  value={String(udPickup.name || '')}
                                  onChange={(e) => updateUberDirectPickup({ name: e.target.value })}
                                  placeholder="e.g., EZSellProducts Kitchen"
                                />
                              </div>
                              <div className="form-group">
                                <label>Pickup Phone</label>
                                <input
                                  type="text"
                                  value={String(udPickup.phone || '')}
                                  onChange={(e) => updateUberDirectPickup({ phone: e.target.value })}
                                  placeholder="+13235550000"
                                />
                              </div>
                            </div>
                            <div className="form-row" style={{ gap: 14 }}>
                              <div className="form-group">
                                <label>Street Address</label>
                                <input
                                  type="text"
                                  value={String(udPickup.street || '')}
                                  onChange={(e) => updateUberDirectPickup({ street: e.target.value })}
                                  placeholder="123 Main St"
                                />
                              </div>
                              <div className="form-group">
                                <label>Apt / Suite (optional)</label>
                                <input
                                  type="text"
                                  value={String(udPickup.apt_unit || '')}
                                  onChange={(e) => updateUberDirectPickup({ apt_unit: e.target.value })}
                                  placeholder="Suite 200"
                                />
                              </div>
                            </div>
                            <div className="form-row" style={{ gap: 14 }}>
                              <div className="form-group">
                                <label>City</label>
                                <input
                                  type="text"
                                  value={String(udPickup.city || '')}
                                  onChange={(e) => updateUberDirectPickup({ city: e.target.value })}
                                  placeholder="Nazareth"
                                />
                              </div>
                              <div className="form-group">
                                <label>State</label>
                                <input
                                  type="text"
                                  value={String(udPickup.state || '')}
                                  onChange={(e) => updateUberDirectPickup({ state: e.target.value.toUpperCase().slice(0, 2) })}
                                  placeholder="PA"
                                  maxLength="2"
                                />
                              </div>
                              <div className="form-group">
                                <label>ZIP Code</label>
                                <input
                                  type="text"
                                  value={String(udPickup.zip || '')}
                                  onChange={(e) => updateUberDirectPickup({ zip: e.target.value.replace(/[^0-9-]/g, '').slice(0, 10) })}
                                  placeholder="18064"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="form-row" style={{ gap: 14 }}>
                            <div className="form-group">
                              <label>Customer Delivery Pricing</label>
                              <select
                                value={udPricingMode}
                                onChange={(e) => updateUberDirectPricing({ mode: e.target.value })}
                              >
                                <option value="pass_through">Pass through (customer pays the courier fee)</option>
                                <option value="flat">Flat fee (customer always pays a fixed amount)</option>
                                <option value="markup">Markup (courier fee + extra)</option>
                                <option value="subsidized">Subsidized (customer pays fixed, you absorb the rest)</option>
                              </select>
                              <div className="field-hint">How the live courier fee is turned into the price the customer sees.</div>
                            </div>

                            {udPricingMode === 'flat' && (
                              <div className="form-group">
                                <label>Flat Delivery Fee ({curCode})</label>
                                <PriceInput
                                  value={Number(udPricingRule.flat_cents) || 0}
                                  onChange={(cents) => updateUberDirectPricing({ flat_cents: cents })}
                                  placeholder="4.99"
                                />
                              </div>
                            )}

                            {udPricingMode === 'markup' && (
                              <div className="form-group">
                                <label>Markup Added to Courier Fee ({curCode})</label>
                                <PriceInput
                                  value={Number(udPricingRule.markup_cents) || 0}
                                  onChange={(cents) => updateUberDirectPricing({ markup_cents: cents })}
                                  placeholder="2.00"
                                />
                                <div className="field-hint">Added on top of the quoted courier fee.</div>
                              </div>
                            )}

                            {udPricingMode === 'subsidized' && (
                              <div className="form-group">
                                <label>Customer Pays ({curCode})</label>
                                <PriceInput
                                  value={Number(udPricingRule.subsidized_customer_cents) || 0}
                                  onChange={(cents) => updateUberDirectPricing({ subsidized_customer_cents: cents })}
                                  placeholder="2.99"
                                />
                                <div className="field-hint">You absorb the difference between this and the courier fee.</div>
                              </div>
                            )}
                          </div>

                          <div className="field-hint" style={{ marginTop: 4 }}>
                            Note: customers never choose the courier — they just pick Delivery. Uber Direct is white-label and stays invisible to them.
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Borzo India courier (nested under internal delivery).
                      Shown only for INR tenants, so the Uber Direct editor above
                      and this one are never visible at the same time. */}
                  {enabled && isInrStore && (
                    <div style={{ marginTop: 20, padding: 16, background: 'rgba(16, 185, 129, 0.05)', borderRadius: 8, border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <input
                          type="checkbox"
                          checked={bzEnabled}
                          onChange={(e) => updateBorzo({ enabled: Boolean(e.target.checked) })}
                        />
                        <span style={{ fontWeight: 600 }}>Enable Borzo courier delivery</span>
                      </label>

                      <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                        When enabled, delivery orders on your storefront get a live Borzo rider quote at checkout and a rider is dispatched automatically after payment. When disabled, internal delivery uses your flat delivery fee exactly as before. Borzo covers 23 Indian cities; addresses outside its serviceable area fall back to your flat fee.
                      </div>

                      {bzEnabled && (
                        <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                          <input
                            type="checkbox"
                            checked={bzMock}
                            onChange={(e) => updateBorzo({ mock: Boolean(e.target.checked) })}
                          />
                          <span style={{ fontWeight: 600 }}>Simulate courier (mock — no Borzo account needed)</span>
                        </label>
                      )}

                      {bzEnabled && bzMock && (
                        <div className="field-hint" style={{ marginBottom: 12, color: '#137484' }}>
                          Mock mode is ON: checkout shows a simulated rider quote and orders get a fake delivery/tracking — no live Borzo call, no charges. The credentials below are optional while mocking. Turn this off to use the real Borzo API.
                        </div>
                      )}

                      {bzEnabled && (
                        <>
                          <div className="form-row" style={{ gap: 14 }}>
                            <div className="form-group">
                              <label>Environment</label>
                              <select
                                value={bzEnvironment}
                                onChange={(e) => updateBorzo({ environment: e.target.value === 'production' ? 'production' : 'sandbox' })}
                              >
                                <option value="sandbox">Sandbox (testing, no real deliveries)</option>
                                <option value="production">Production (live riders)</option>
                              </select>
                              <div className="field-hint">Use Sandbox until you're ready to dispatch real deliveries.</div>
                            </div>

                            <div className="form-group">
                              <label>Auth Token</label>
                              <input
                                type="password"
                                value={borzoTokenInput}
                                onChange={(e) => setBorzoTokenInput(e.target.value)}
                                placeholder={bzHasToken ? '•••••••• (token saved — leave blank to keep)' : 'Enter Borzo API auth token'}
                              />
                              <div className="field-hint">
                                {bzHasToken
                                  ? 'A token is stored. Write-only — leave blank to keep the currently stored token.'
                                  : 'From your Borzo dashboard. Write-only: it is never displayed again after saving.'}
                              </div>
                            </div>
                          </div>

                          <div className="form-row" style={{ gap: 14 }}>
                            <div className="form-group">
                              <label>Webhook Signing Secret</label>
                              <input
                                type="password"
                                value={borzoWebhookSecretInput}
                                onChange={(e) => setBorzoWebhookSecretInput(e.target.value)}
                                placeholder={bzHasWebhookSecret ? '•••••••• (saved — leave blank to keep)' : 'Enter Borzo webhook signing secret'}
                              />
                              <div className="field-hint">
                                {bzHasWebhookSecret ? 'A secret is stored. ' : ''}
                                Used to verify Borzo status callbacks. Point the Borzo callback at <code>/public/delivery/borzo/webhook</code>. Required in Production.
                              </div>
                            </div>
                          </div>

                          <div style={{ marginTop: 4, marginBottom: 4 }}>
                            <button
                              type="button"
                              onClick={handleBorzoTestConnection}
                              disabled={!bzMock && !bzHasUsableToken}
                              style={{
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: '1px solid #4c1d9580',
                                background: '#4c1d951a',
                                color: '#4c1d95',
                                cursor: (!bzMock && !bzHasUsableToken) ? 'not-allowed' : 'pointer',
                                opacity: (!bzMock && !bzHasUsableToken) ? 0.5 : 1,
                              }}
                            >
                              Test Borzo Connection
                            </button>
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              Checks that Borzo accepts your credentials. Nothing is dispatched and no credential is displayed.
                            </div>
                          </div>

                          {borzoTestResult && (
                            <div
                              style={{
                                marginTop: 8,
                                marginBottom: 8,
                                padding: 12,
                                borderRadius: 8,
                                background: borzoTestResult.ok === true
                                  ? 'rgba(34, 197, 94, 0.10)'
                                  : borzoTestResult.ok === false
                                    ? 'rgba(239, 68, 68, 0.08)'
                                    : 'rgba(100, 116, 139, 0.10)',
                                border: borzoTestResult.ok === true
                                  ? '1px solid rgba(34, 197, 94, 0.30)'
                                  : borzoTestResult.ok === false
                                    ? '1px solid rgba(239, 68, 68, 0.25)'
                                    : '1px solid rgba(100, 116, 139, 0.25)',
                                color: borzoTestResult.ok === true
                                  ? '#15803d'
                                  : borzoTestResult.ok === false
                                    ? '#dc2626'
                                    : 'rgba(15, 23, 42, 0.8)',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                              }}
                            >
                              {borzoTestResult.ok === true ? '✓ ' : borzoTestResult.ok === false ? '⚠ ' : '… '}
                              {borzoTestResult.message}
                            </div>
                          )}

                          <div style={{ marginTop: 10, paddingTop: 12, borderTop: '1px dashed rgba(16, 185, 129, 0.35)' }}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>Rider Pickup Address (private)</div>
                            <div className="field-hint" style={{ marginBottom: 12 }}>
                              The real address the rider picks up from. Sent only to Borzo — never shown on your public storefront. Leave blank to use your store location's address instead.
                            </div>
                            <div className="form-row" style={{ gap: 14 }}>
                              <div className="form-group">
                                <label>Pickup Location Name</label>
                                <input
                                  type="text"
                                  value={String(bzPickup.name || '')}
                                  onChange={(e) => updateBorzoPickup({ name: e.target.value })}
                                  placeholder="e.g., EZSellProducts Kitchen"
                                />
                              </div>
                              <div className="form-group">
                                <label>Pickup Phone</label>
                                <input
                                  type="text"
                                  value={String(bzPickup.phone || '')}
                                  onChange={(e) => updateBorzoPickup({ phone: e.target.value })}
                                  placeholder="+919876543210"
                                />
                                <div className="field-hint">Indian mobile number. A bare 10-digit number is assumed to be +91.</div>
                              </div>
                            </div>
                            <div className="form-row" style={{ gap: 14 }}>
                              <div className="form-group">
                                <label>Street Address</label>
                                <input
                                  type="text"
                                  value={String(bzPickup.street || '')}
                                  onChange={(e) => updateBorzoPickup({ street: e.target.value })}
                                  placeholder="12 Anna Salai"
                                />
                              </div>
                              <div className="form-group">
                                <label>Apt / Unit (optional)</label>
                                <input
                                  type="text"
                                  value={String(bzPickup.apt_unit || '')}
                                  onChange={(e) => updateBorzoPickup({ apt_unit: e.target.value })}
                                  placeholder="Shop 4"
                                />
                              </div>
                            </div>
                            <div className="form-row" style={{ gap: 14 }}>
                              <div className="form-group">
                                <label>City</label>
                                <input
                                  type="text"
                                  value={String(bzPickup.city || '')}
                                  onChange={(e) => updateBorzoPickup({ city: e.target.value })}
                                  placeholder="Chennai"
                                />
                              </div>
                              <div className="form-group">
                                <label>State</label>
                                {/* Free text: Indian state names are full words,
                                    so this is never uppercased or capped at 2 chars. */}
                                <input
                                  type="text"
                                  value={String(bzPickup.state || '')}
                                  onChange={(e) => updateBorzoPickup({ state: e.target.value })}
                                  placeholder="Tamil Nadu"
                                />
                                <div className="field-hint">Full state name (e.g., Tamil Nadu).</div>
                              </div>
                              <div className="form-group">
                                <label>PIN code</label>
                                <input
                                  type="text"
                                  value={String(bzPickup.zip || '')}
                                  onChange={(e) => updateBorzoPickup({ zip: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                                  placeholder="600002"
                                  maxLength={6}
                                  pattern="[0-9]{6}"
                                />
                                <div className="field-hint">6-digit PIN code (e.g., 600002).</div>
                              </div>
                            </div>
                          </div>

                          <div className="form-row" style={{ gap: 14 }}>
                            <div className="form-group">
                              <label>Customer Delivery Pricing</label>
                              <select
                                value={bzPricingMode}
                                onChange={(e) => updateBorzoPricing({ mode: e.target.value })}
                              >
                                <option value="pass_through">Pass through (customer pays the rider fee)</option>
                                <option value="flat">Flat fee (customer always pays a fixed amount)</option>
                                <option value="markup">Markup (rider fee + extra)</option>
                                <option value="subsidized">Subsidized (customer pays fixed, you absorb the rest)</option>
                              </select>
                              <div className="field-hint">How the live rider fee is turned into the price the customer sees.</div>
                            </div>

                            {bzPricingMode === 'flat' && (
                              <div className="form-group">
                                <label>Flat Delivery Fee ({curCode})</label>
                                <PriceInput
                                  value={Number(bzPricingRule.flat_cents) || 0}
                                  onChange={(cents) => updateBorzoPricing({ flat_cents: cents })}
                                  placeholder="49.00"
                                />
                                <div className="field-hint">Customer always pays this (e.g., {curSym}49.00).</div>
                              </div>
                            )}

                            {bzPricingMode === 'markup' && (
                              <div className="form-group">
                                <label>Markup Added to Rider Fee ({curCode})</label>
                                <PriceInput
                                  value={Number(bzPricingRule.markup_cents) || 0}
                                  onChange={(cents) => updateBorzoPricing({ markup_cents: cents })}
                                  placeholder="20.00"
                                />
                                <div className="field-hint">Added on top of the quoted rider fee (e.g., {curSym}20.00).</div>
                              </div>
                            )}

                            {bzPricingMode === 'subsidized' && (
                              <div className="form-group">
                                <label>Customer Pays ({curCode})</label>
                                <PriceInput
                                  value={Number(bzPricingRule.subsidized_customer_cents) || 0}
                                  onChange={(cents) => updateBorzoPricing({ subsidized_customer_cents: cents })}
                                  placeholder="29.00"
                                />
                                <div className="field-hint">You absorb the difference between this ({curSym}) and the rider fee.</div>
                              </div>
                            )}
                          </div>

                          <div className="field-hint" style={{ marginTop: 4 }}>
                            Note: customers never choose the courier — they just pick Delivery. Borzo stays invisible to them.
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Address Autocomplete toggle (independent of delivery enablement) */}
                  <div style={{ marginTop: 20, padding: 16, background: 'rgba(99, 102, 241, 0.05)', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                    <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <input
                        type="checkbox"
                        checked={autocompleteEnabled}
                        onChange={(e) => updateAddressAutocomplete({ enabled: Boolean(e.target.checked) })}
                      />
                      <span style={{ fontWeight: 600 }}>Enable address autocomplete</span>
                    </label>

                    {/* Country-aware: useGeoapifyConfig constrains suggestions to the
                        store's country ("in" for INR stores, "us" otherwise), so
                        hardcoding "US" here misdescribed what an India store gets. */}
                    <div className="field-hint" style={{ color: 'rgba(15, 23, 42, 0.7)' }}>
                      When enabled, the delivery Street Address field suggests verified {isInrStore ? 'Indian' : isAudStore ? 'Australian' : 'US'} addresses as the customer types (powered by Geoapify). Requires the Geoapify key to be configured for this tenant.
                    </div>
                  </div>

                  {enabled && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(23, 162, 184, 0.08)', borderRadius: 8, border: '1px solid rgba(23, 162, 184, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#137484' }}>Preview</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Customers will see: <strong>"Delivery {feeCents > 0 ? `(+${formatMinor(feeCents, form?.currency)})` : '(Free)'}"</strong>
                        {minimumCents > 0 && (
                          <div style={{ marginTop: 4 }}>
                            Minimum order: <strong>{formatMinor(minimumCents, form?.currency)}</strong>
                          </div>
                        )}
                        {radiusEnabled && baseZipCode && (
                          <div style={{ marginTop: 4 }}>
                            {/* Currency-aware, matching the Delivery Area block above.
                                This previously always rendered `maxRadiusMiles` + "miles",
                                so an INR store showed the unused US default ("Within 10
                                miles of 600016") while the enforced value was the km one. */}
                            Delivery area: <strong>{radiusUsesKm
                              ? `Within ${maxRadiusKm} km of ${radiusPostalLabel} ${baseZipCode}`
                              : `Within ${maxRadiusMiles} miles of ${baseZipCode}`}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              </details>
            );
          })()}

          {/* Delivery Integrations enablement section (Uber Eats, Otter, ...) */}
          {(() => {
            const di = (form?.delivery_integrations && typeof form.delivery_integrations === 'object')
              ? form.delivery_integrations
              : {};
            const ue = (di.ubereats && typeof di.ubereats === 'object') ? di.ubereats : {};

            const ueEnabled = Boolean(ue.enabled);
            const ueEnvironment = ue.environment === 'production' ? 'production' : 'sandbox';
            const ueClientId = String(ue.client_id || '').trim();
            const ueStoreId = String(ue.store_id || '').trim();
            const ueHasSecret = Boolean(ue.has_secret);
            const ueLastSync = ue.last_menu_sync_timestamp;
            const typedSecret = String(ubereatsSecretInput || '').trim();
            // A secret is "available" if one is already stored or the admin
            // typed a new one in this session.
            const ueHasUsableSecret = ueHasSecret || Boolean(typedSecret);

            // DoorDash provider (mirrors Uber Eats; JWT credential model).
            const dd = (di.doordash && typeof di.doordash === 'object') ? di.doordash : {};
            const ddEnabled = Boolean(dd.enabled);
            const ddEnvironment = dd.environment === 'production' ? 'production' : 'sandbox';
            const ddDeveloperId = String(dd.developer_id || '').trim();
            const ddKeyId = String(dd.key_id || '').trim();
            const ddStoreId = String(dd.store_id || '').trim();
            const ddHasSecret = Boolean(dd.has_secret);
            const ddLastSync = dd.last_menu_sync_timestamp;
            const typedDoordashSecret = String(doordashSecretInput || '').trim();
            // A signing secret is "available" if one is already stored or the
            // admin typed a new one in this session.
            const ddHasUsableSecret = ddHasSecret || Boolean(typedDoordashSecret);

            // Otter enablement toggle is relocated here. The detailed Otter
            // config (fields, menu sync) remains in its own section below.
            // `form.otter.enabled` stays the source of truth so toggling in
            // either place stays consistent.
            const otterEnabled = Boolean(form?.otter?.enabled ?? di?.otter?.enabled);

            const updateUberEats = (patch) => {
              setForm((prev) => {
                const prevDi = (prev.delivery_integrations && typeof prev.delivery_integrations === 'object')
                  ? prev.delivery_integrations
                  : {};
                const prevUe = (prevDi.ubereats && typeof prevDi.ubereats === 'object') ? prevDi.ubereats : {};
                return {
                  ...prev,
                  delivery_integrations: {
                    ...prevDi,
                    ubereats: {
                      ...fallbackDefaults.delivery_integrations.ubereats,
                      ...prevUe,
                      ...(patch || {}),
                    },
                  },
                };
              });
            };

            const updateDeliveryOtterEnabled = (next) => {
              const enabledVal = Boolean(next);
              setForm((prev) => {
                const prevDi = (prev.delivery_integrations && typeof prev.delivery_integrations === 'object')
                  ? prev.delivery_integrations
                  : {};
                return {
                  ...prev,
                  // Keep the relocated toggle and the existing top-level Otter
                  // block in sync so existing Otter behavior is preserved.
                  otter: {
                    ...fallbackDefaults.otter,
                    ...((prev.otter && typeof prev.otter === 'object') ? prev.otter : {}),
                    enabled: enabledVal,
                  },
                  delivery_integrations: {
                    ...prevDi,
                    otter: {
                      ...((prevDi.otter && typeof prevDi.otter === 'object') ? prevDi.otter : {}),
                      enabled: enabledVal,
                    },
                  },
                };
              });
            };

            const handleUberEatsTestConnection = async () => {
              if (!ueClientId || !ueStoreId || !ueHasUsableSecret) {
                setError('Please provide client ID, client secret, and store ID before testing the Uber Eats connection');
                setUeTestResult({ ok: false, message: 'Provide client ID, client secret, and store ID before testing.' });
                return;
              }
              setStatusMessage('Testing Uber Eats connection...');
              setError('');
              setUeTestResult({ ok: null, message: 'Testing Uber Eats connection...' });
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const body = {
                  environment: ueEnvironment,
                  client_id: ueClientId,
                  store_id: ueStoreId,
                };
                // Only include the secret if the admin typed a new one this
                // session; otherwise the backend uses the stored secret.
                if (typedSecret) body.client_secret = typedSecret;

                const response = await fetch(`${API_BASE_URL}/admin/ubereats/connection/test`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(body),
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok && data.success) {
                  const msg = data.message || 'Uber Eats connection successful!';
                  setStatusMessage(msg);
                  setUeTestResult({ ok: true, message: msg });
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  const msg = data.error || data.message || 'Uber Eats connection test failed';
                  setError(msg);
                  setStatusMessage('');
                  setUeTestResult({ ok: false, message: msg });
                }
              } catch (err) {
                console.error('Uber Eats connection test error:', err);
                const msg = `Uber Eats connection test failed: ${err.message}`;
                setError(msg);
                setStatusMessage('');
                setUeTestResult({ ok: false, message: msg });
              }
            };

            const handleUberEatsMenuSync = async () => {
              if (!ueEnabled) {
                setError('Please enable Uber Eats integration first');
                return;
              }
              setStatusMessage('Syncing menu with Uber Eats...');
              setError('');
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();

                // Optional selective sync: parse the comma-separated Product IDs
                // input. When empty, omit product_ids so the backend syncs the
                // full catalog (existing behavior).
                const selectedProductIds = String(ueSyncProductIds || '')
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean);
                const requestBody = selectedProductIds.length
                  ? { product_ids: selectedProductIds }
                  : {};

                const response = await fetch(`${API_BASE_URL}/admin/ubereats/menu/sync`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(requestBody),
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok) {
                  const itemsCount = data.items_count || 0;
                  const categoriesCount = data.categories_count || 0;
                  setStatusMessage(`Uber Eats menu synced! ${itemsCount} items, ${categoriesCount} categories`);
                  updateUberEats({ last_menu_sync_timestamp: data.last_menu_sync_timestamp || Date.now() });
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  setError(data.error || data.details || 'Uber Eats menu sync failed');
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('Uber Eats menu sync error:', err);
                setError(`Uber Eats menu sync failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const updateDoorDash = (patch) => {
              setForm((prev) => {
                const prevDi = (prev.delivery_integrations && typeof prev.delivery_integrations === 'object')
                  ? prev.delivery_integrations
                  : {};
                const prevDd = (prevDi.doordash && typeof prevDi.doordash === 'object') ? prevDi.doordash : {};
                return {
                  ...prev,
                  delivery_integrations: {
                    ...prevDi,
                    doordash: {
                      ...fallbackDefaults.delivery_integrations.doordash,
                      ...prevDd,
                      ...(patch || {}),
                    },
                  },
                };
              });
            };

            const handleDoorDashTestConnection = async () => {
              if (!ddDeveloperId || !ddKeyId || !ddStoreId || !ddHasUsableSecret) {
                setError('Please provide developer ID, key ID, signing secret, and store ID before testing the DoorDash connection');
                return;
              }
              setStatusMessage('Testing DoorDash connection...');
              setError('');
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const body = {
                  environment: ddEnvironment,
                  developer_id: ddDeveloperId,
                  key_id: ddKeyId,
                  store_id: ddStoreId,
                };
                // Only include the secret if the admin typed a new one this
                // session; otherwise the backend uses the stored secret.
                if (typedDoordashSecret) body.signing_secret = typedDoordashSecret;

                const response = await fetch(`${API_BASE_URL}/admin/doordash/connection/test`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(body),
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok && data.success) {
                  setStatusMessage(data.message || 'DoorDash connection successful!');
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  setError(data.error || data.message || 'DoorDash connection test failed');
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('DoorDash connection test error:', err);
                setError(`DoorDash connection test failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const handleDoorDashMenuSync = async () => {
              if (!ddEnabled) {
                setError('Please enable DoorDash integration first');
                return;
              }
              setStatusMessage('Syncing menu with DoorDash...');
              setError('');
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const response = await fetch(`${API_BASE_URL}/admin/doordash/menu/sync`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok) {
                  const itemsCount = data.items_count || 0;
                  const categoriesCount = data.categories_count || 0;
                  setStatusMessage(`DoorDash menu synced! ${itemsCount} items, ${categoriesCount} categories`);
                  updateDoorDash({ last_menu_sync_timestamp: data.last_menu_sync_timestamp || Date.now() });
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  setError(data.error || data.details || 'DoorDash menu sync failed');
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('DoorDash menu sync error:', err);
                setError(`DoorDash menu sync failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const providerCardStyle = {
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              border: '1px solid rgba(148, 163, 184, 0.35)',
              marginTop: 14,
            };
            const buttonStyle = (disabled, color) => ({
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${color}80`,
              background: `${color}1a`,
              color,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            });

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Delivery Integrations</summary>

                <div className="field-hint" style={{ marginTop: 10, marginBottom: 4, color: 'rgba(15, 23, 42, 0.7)' }}>
                  Enable or disable each delivery integration independently. All integrations are off by default. Disabling an integration preserves its saved credentials.
                </div>

                {/* Uber Eats provider */}
                <section style={providerCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>🚗 Uber Eats</h3>
                    <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={ueEnabled}
                        onChange={(e) => updateUberEats({ enabled: Boolean(e.target.checked) })}
                      />
                      {ueEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>

                  <div className="field-hint" style={{ marginTop: 8, marginBottom: 12, color: 'rgba(15, 23, 42, 0.7)' }}>
                    Direct integration with the Uber Eats Marketplace API. Orders flow into your admin order queue. Use the sandbox environment for testing before going live.
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Environment</label>
                      <select
                        value={ueEnvironment}
                        onChange={(e) => updateUberEats({ environment: e.target.value === 'production' ? 'production' : 'sandbox' })}
                        disabled={!ueEnabled}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
                      >
                        <option value="sandbox">Sandbox (testing)</option>
                        <option value="production">Production (live)</option>
                      </select>
                      <div className="field-hint">Sandbox routes to test-api.uber.com; Production routes to api.uber.com.</div>
                    </div>

                    <div className="form-group">
                      <label>Store ID *</label>
                      <input
                        type="text"
                        value={ueStoreId}
                        onChange={(e) => updateUberEats({ store_id: e.target.value })}
                        disabled={!ueEnabled}
                        placeholder="Enter Uber Eats store ID"
                        maxLength="100"
                      />
                      <div className="field-hint">Your Uber Eats store identifier.</div>
                    </div>
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Client ID *</label>
                      <input
                        type="text"
                        value={ueClientId}
                        onChange={(e) => updateUberEats({ client_id: e.target.value })}
                        disabled={!ueEnabled}
                        placeholder="Enter Uber Eats client ID"
                        maxLength="200"
                      />
                      <div className="field-hint">OAuth client ID from your Uber developer app.</div>
                    </div>

                    <div className="form-group">
                      <label>Client Secret *</label>
                      <input
                        type="password"
                        value={ubereatsSecretInput}
                        onChange={(e) => setUbereatsSecretInput(e.target.value)}
                        disabled={!ueEnabled}
                        placeholder={ueHasSecret ? '•••••••• (secret saved — leave blank to keep)' : 'Enter Uber Eats client secret'}
                        maxLength="500"
                        autoComplete="new-password"
                      />
                      <div className="field-hint">
                        {ueHasSecret
                          ? 'A secret is already saved. Leave blank to keep it, or type a new value to replace it.'
                          : 'OAuth client secret. Stored securely and never displayed after saving.'}
                      </div>
                    </div>
                  </div>

                  {ueEnabled && (
                    <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={handleUberEatsTestConnection}
                        disabled={!ueClientId || !ueStoreId || !ueHasUsableSecret}
                        style={buttonStyle(!ueClientId || !ueStoreId || !ueHasUsableSecret, '#4c1d95')}
                      >
                        Test Connection
                      </button>
                      <button
                        type="button"
                        onClick={handleUberEatsMenuSync}
                        style={buttonStyle(false, '#15803d')}
                      >
                        Sync Menu Now
                      </button>
                      {ueLastSync && (
                        <span style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)' }}>
                          Last menu sync: {new Date(ueLastSync).toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}

                  {ueEnabled && (
                    <div style={{ marginTop: 10 }}>
                      <label
                        htmlFor="ueSyncProductIds"
                        style={{ display: 'block', fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.7)', marginBottom: 4 }}
                      >
                        Product IDs to sync (optional)
                      </label>
                      <input
                        id="ueSyncProductIds"
                        type="text"
                        value={ueSyncProductIds}
                        onChange={(e) => setUeSyncProductIds(e.target.value)}
                        placeholder="Comma-separated Product IDs. Leave blank to sync the entire menu."
                        style={{ width: '100%', maxWidth: 520, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(15, 23, 42, 0.2)' }}
                      />
                      <div style={{ fontSize: '0.8rem', color: 'rgba(15, 23, 42, 0.55)', marginTop: 4 }}>
                        Leave empty to sync all products. Enter specific Product IDs (comma-separated) to sync only those.
                      </div>
                    </div>
                  )}

                  {ueEnabled && ueTestResult && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 8,
                        background: ueTestResult.ok === true
                          ? 'rgba(34, 197, 94, 0.10)'
                          : ueTestResult.ok === false
                            ? 'rgba(239, 68, 68, 0.08)'
                            : 'rgba(100, 116, 139, 0.10)',
                        border: ueTestResult.ok === true
                          ? '1px solid rgba(34, 197, 94, 0.30)'
                          : ueTestResult.ok === false
                            ? '1px solid rgba(239, 68, 68, 0.25)'
                            : '1px solid rgba(100, 116, 139, 0.25)',
                        color: ueTestResult.ok === true
                          ? '#15803d'
                          : ueTestResult.ok === false
                            ? '#dc2626'
                            : 'rgba(15, 23, 42, 0.8)',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                      }}
                    >
                      {ueTestResult.ok === true ? '✓ ' : ueTestResult.ok === false ? '⚠ ' : '… '}
                      {ueTestResult.message}
                    </div>
                  )}

                  {ueEnabled && (!ueClientId || !ueStoreId || !ueHasUsableSecret) && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#dc2626' }}>⚠ Configuration Incomplete</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Provide client ID, client secret, and store ID to complete the Uber Eats integration.
                      </div>
                    </div>
                  )}
                </section>

                {/* DoorDash provider */}
                <section style={providerCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>🛵 DoorDash</h3>
                    <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={ddEnabled}
                        onChange={(e) => updateDoorDash({ enabled: Boolean(e.target.checked) })}
                      />
                      {ddEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>

                  <div className="field-hint" style={{ marginTop: 8, marginBottom: 12, color: 'rgba(15, 23, 42, 0.7)' }}>
                    Direct integration with the DoorDash Marketplace API. Orders flow into your admin order queue. Use the sandbox environment for testing before going live.
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Environment</label>
                      <select
                        value={ddEnvironment}
                        onChange={(e) => updateDoorDash({ environment: e.target.value === 'production' ? 'production' : 'sandbox' })}
                        disabled={!ddEnabled}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
                      >
                        <option value="sandbox">Sandbox (testing)</option>
                        <option value="production">Production (live)</option>
                      </select>
                      <div className="field-hint">Sandbox routes to the DoorDash sandbox; Production routes to the live DoorDash API.</div>
                    </div>

                    <div className="form-group">
                      <label>Store ID *</label>
                      <input
                        type="text"
                        value={ddStoreId}
                        onChange={(e) => updateDoorDash({ store_id: e.target.value })}
                        disabled={!ddEnabled}
                        placeholder="Enter DoorDash store ID"
                        maxLength="100"
                      />
                      <div className="field-hint">Your DoorDash store identifier.</div>
                    </div>
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Developer ID *</label>
                      <input
                        type="text"
                        value={ddDeveloperId}
                        onChange={(e) => updateDoorDash({ developer_id: e.target.value })}
                        disabled={!ddEnabled}
                        placeholder="Enter DoorDash developer ID"
                        maxLength="200"
                      />
                      <div className="field-hint">Developer ID from your DoorDash Developer Portal credentials.</div>
                    </div>

                    <div className="form-group">
                      <label>Key ID *</label>
                      <input
                        type="text"
                        value={ddKeyId}
                        onChange={(e) => updateDoorDash({ key_id: e.target.value })}
                        disabled={!ddEnabled}
                        placeholder="Enter DoorDash key ID"
                        maxLength="200"
                      />
                      <div className="field-hint">Key ID used to sign the request JWT.</div>
                    </div>
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Signing Secret *</label>
                      <input
                        type="password"
                        value={doordashSecretInput}
                        onChange={(e) => setDoordashSecretInput(e.target.value)}
                        disabled={!ddEnabled}
                        placeholder={ddHasSecret ? '•••••••• (secret saved — leave blank to keep)' : 'Enter DoorDash signing secret'}
                        maxLength="500"
                        autoComplete="new-password"
                      />
                      <div className="field-hint">
                        {ddHasSecret
                          ? 'A secret is already saved. Leave blank to keep it, or type a new value to replace it.'
                          : 'JWT signing secret. Stored securely and never displayed after saving.'}
                      </div>
                    </div>
                  </div>

                  {ddEnabled && (
                    <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={handleDoorDashTestConnection}
                        disabled={!ddDeveloperId || !ddKeyId || !ddStoreId || !ddHasUsableSecret}
                        style={buttonStyle(!ddDeveloperId || !ddKeyId || !ddStoreId || !ddHasUsableSecret, '#4c1d95')}
                      >
                        Test Connection
                      </button>
                      <button
                        type="button"
                        onClick={handleDoorDashMenuSync}
                        style={buttonStyle(false, '#15803d')}
                      >
                        Sync Menu Now
                      </button>
                      {ddLastSync && (
                        <span style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)' }}>
                          Last menu sync: {new Date(ddLastSync).toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}

                  {ddEnabled && (!ddDeveloperId || !ddKeyId || !ddStoreId || !ddHasUsableSecret) && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#dc2626' }}>⚠ Configuration Incomplete</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Provide developer ID, key ID, signing secret, and store ID to complete the DoorDash integration.
                      </div>
                    </div>
                  )}
                </section>

                {/* Otter provider — enablement toggle only (detailed config below) */}
                <section style={providerCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>🦦 Otter</h3>
                    <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={otterEnabled}
                        onChange={(e) => updateDeliveryOtterEnabled(e.target.checked)}
                      />
                      {otterEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                  <div className="field-hint" style={{ marginTop: 8, color: 'rgba(15, 23, 42, 0.7)' }}>
                    Aggregates orders from multiple delivery platforms. Configure Otter credentials and menu sync in the Otter Delivery Integration section below.
                  </div>
                </section>
              </details>
            );
          })()}

          {/* Otter Delivery Integration Settings */}
          {(() => {
            const mode = String(form?.storeMode || '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const otter = form?.otter || {};
            const enabled = Boolean(otter.enabled);
            const apiKey = String(otter.api_key || '').trim();
            const apiSecret = String(otter.api_secret || '').trim();
            const storeId = String(otter.store_id || '').trim();
            const webhookUrl = String(otter.webhook_url || '').trim();
            const menuSyncEnabled = Boolean(otter.menu_sync_enabled);
            const menuSyncMode = String(otter.menu_sync_mode || 'manual').trim();
            const lastSyncTimestamp = otter.last_menu_sync_timestamp;

            const updateOtter = (patch) => {
              setForm((prev) => ({
                ...prev,
                otter: {
                  enabled: false,
                  api_key: '',
                  api_secret: '',
                  store_id: '',
                  webhook_url: '',
                  menu_sync_enabled: false,
                  menu_sync_mode: 'manual',
                  last_menu_sync_timestamp: null,
                  ...((prev.otter && typeof prev.otter === 'object') ? prev.otter : {}),
                  ...(patch || {}),
                },
              }));
            };

            const handleTestConnection = async () => {
              if (!apiKey || !apiSecret || !storeId) {
                setError('Please provide API key, API secret, and store ID before testing connection');
                return;
              }
              
              setStatusMessage('Testing Otter connection...');
              setError('');
              
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                
                const response = await fetch(`${API_BASE_URL}/admin/otter/connection/test`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    api_key: apiKey,
                    api_secret: apiSecret,
                    store_id: storeId,
                  }),
                });
                
                const data = await response.json();
                
                if (response.ok && data.success) {
                  setStatusMessage(data.message || 'Connection successful!');
                  // Clear success message after 5 seconds
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  const errorMsg = data.error || 'Connection test failed';
                  setError(errorMsg);
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('Connection test error:', err);
                setError(`Connection test failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const handleMenuSync = async () => {
              if (!enabled) {
                setError('Please enable Otter integration first');
                return;
              }
              
              setStatusMessage('Syncing menu with Otter...');
              setError('');
              
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                
                const response = await fetch(`${API_BASE_URL}/admin/otter/menu/sync`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                });
                
                const data = await response.json();
                
                if (response.ok) {
                  const itemsCount = data.items_count || 0;
                  const categoriesCount = data.categories_count || 0;
                  const duration = data.total_duration_seconds || 0;
                  
                  setStatusMessage(`Menu synced successfully! ${itemsCount} items, ${categoriesCount} categories (${duration}s)`);
                  
                  // Update last sync timestamp
                  updateOtter({ last_menu_sync_timestamp: Date.now() });
                  
                  // Clear success message after 5 seconds
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  const errorMsg = data.error || data.details || 'Menu sync failed';
                  setError(errorMsg);
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('Menu sync error:', err);
                setError(`Menu sync failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const openOtterDashboard = () => {
              if (storeId) {
                window.open(`https://dashboard.otter.com/store/${storeId}`, '_blank');
              } else {
                setError('Please provide store ID to open Otter dashboard');
              }
            };

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Otter Delivery Integration</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updateOtter({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable Otter integration
                  </label>

                  <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                    Otter consolidates orders from multiple delivery platforms (DoorDash, Uber Eats, GrubHub, etc.) into your admin queue. Customers continue using their preferred delivery apps.
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>API Key *</label>
                      <input
                        type="text"
                        value={apiKey}
                        onChange={(e) => updateOtter({ api_key: e.target.value })}
                        disabled={!enabled}
                        placeholder="Enter Otter API key"
                        maxLength="200"
                      />
                      <div className="field-hint">Your Otter API key for authentication</div>
                    </div>

                    <div className="form-group">
                      <label>API Secret *</label>
                      <input
                        type="password"
                        value={apiSecret}
                        onChange={(e) => updateOtter({ api_secret: e.target.value })}
                        disabled={!enabled}
                        placeholder="Enter Otter API secret"
                        maxLength="200"
                      />
                      <div className="field-hint">Your Otter API secret for webhook signature verification</div>
                    </div>
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Store ID *</label>
                      <input
                        type="text"
                        value={storeId}
                        onChange={(e) => updateOtter({ store_id: e.target.value })}
                        disabled={!enabled}
                        placeholder="Enter Otter store ID"
                        maxLength="100"
                      />
                      <div className="field-hint">Your unique Otter store identifier</div>
                    </div>

                    <div className="form-group">
                      <label>Webhook URL (Reference)</label>
                      <input
                        type="text"
                        value={webhookUrl}
                        onChange={(e) => updateOtter({ webhook_url: e.target.value })}
                        disabled={!enabled}
                        placeholder="https://api.yourdomain.com/marketplace/otter/webhook"
                        maxLength="500"
                      />
                      <div className="field-hint">Configure this URL in your Otter dashboard</div>
                    </div>
                  </div>

                  {enabled && (
                    <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                      <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={!apiKey || !apiSecret || !storeId}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 6,
                          border: '1px solid rgba(99, 102, 241, 0.5)',
                          background: 'rgba(99, 102, 241, 0.1)',
                          color: '#4c1d95',
                          cursor: (!apiKey || !apiSecret || !storeId) ? 'not-allowed' : 'pointer',
                          opacity: (!apiKey || !apiSecret || !storeId) ? 0.5 : 1,
                        }}
                      >
                        Test Connection
                      </button>
                      <button
                        type="button"
                        onClick={openOtterDashboard}
                        disabled={!storeId}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 6,
                          border: '1px solid rgba(99, 102, 241, 0.5)',
                          background: 'rgba(99, 102, 241, 0.1)',
                          color: '#4c1d95',
                          cursor: !storeId ? 'not-allowed' : 'pointer',
                          opacity: !storeId ? 0.5 : 1,
                        }}
                      >
                        Open Otter Dashboard
                      </button>
                    </div>
                  )}

                  {/* Menu Sync Configuration */}
                  {enabled && (
                    <div style={{ marginTop: 20, padding: 16, background: 'rgba(99, 102, 241, 0.05)', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <input
                          type="checkbox"
                          checked={menuSyncEnabled}
                          onChange={(e) => updateOtter({ menu_sync_enabled: Boolean(e.target.checked) })}
                        />
                        <span style={{ fontWeight: 600 }}>Enable menu synchronization</span>
                      </label>

                      <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                        Automatically sync your menu data with Otter so delivery platforms display current items and prices.
                      </div>

                      {menuSyncEnabled && (
                        <>
                          <div className="form-group" style={{ marginBottom: 16 }}>
                            <label>Sync Mode</label>
                            <select
                              value={menuSyncMode}
                              onChange={(e) => updateOtter({ menu_sync_mode: e.target.value })}
                              style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
                            >
                              <option value="manual">Manual - Sync when you click the button</option>
                              <option value="automatic">Automatic - Sync every 5 minutes</option>
                            </select>
                            <div className="field-hint">Choose how often to sync your menu with Otter</div>
                          </div>

                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <button
                              type="button"
                              onClick={handleMenuSync}
                              style={{
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: '1px solid rgba(34, 197, 94, 0.5)',
                                background: 'rgba(34, 197, 94, 0.1)',
                                color: '#15803d',
                                cursor: 'pointer',
                              }}
                            >
                              Sync Menu Now
                            </button>
                            {lastSyncTimestamp && (
                              <span style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)' }}>
                                Last synced: {new Date(lastSyncTimestamp).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {enabled && apiKey && apiSecret && storeId && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(34, 197, 94, 0.08)', borderRadius: 8, border: '1px solid rgba(34, 197, 94, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#15803d' }}>✓ Configuration Complete</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Otter orders from DoorDash, Uber Eats, GrubHub, and other platforms will appear in your admin order queue.
                      </div>
                    </div>
                  )}

                  {enabled && (!apiKey || !apiSecret || !storeId) && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#dc2626' }}>⚠ Configuration Incomplete</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Please provide API key, API secret, and store ID to complete the integration.
                      </div>
                    </div>
                  )}

                  {/* Otter Analytics Section */}
                  {enabled && apiKey && apiSecret && storeId && (
                    <OtterAnalyticsSection tenantId={form?.tenant_id} />
                  )}
                </section>
              </details>
            );
          })()}

          <details open>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Shipping rates</summary>

          <div style={{ display: 'grid', gap: 18, marginTop: 14 }}>
            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Standard shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(standard.enabled)}
                  onChange={(e) => updateShipping('standard', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={standard.label || ''}
                    onChange={(e) => updateShipping('standard', { label: e.target.value })}
                    placeholder="Standard Shipping"
                  />
                </div>
                <div className="form-group">
                  <label>Price (USD)</label>
                  <input
                    value={centsToDollars(standard.amount_cents)}
                    onChange={(e) => updateShipping('standard', { amount_cents: dollarsToCents(e.target.value) ?? standard.amount_cents })}
                    placeholder="4.99"
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={standard.min_days ?? 3}
                    onChange={(e) => updateShipping('standard', { min_days: Number(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={standard.max_days ?? 5}
                    onChange={(e) => updateShipping('standard', { max_days: Number(e.target.value) })}
                  />
                </div>
              </div>
            </section>

            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Express shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(express.enabled)}
                  onChange={(e) => updateShipping('express', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={express.label || ''}
                    onChange={(e) => updateShipping('express', { label: e.target.value })}
                    placeholder="Express Shipping"
                  />
                </div>
                <div className="form-group">
                  <label>Price (USD)</label>
                  <input
                    value={centsToDollars(express.amount_cents)}
                    onChange={(e) => updateShipping('express', { amount_cents: dollarsToCents(e.target.value) ?? express.amount_cents })}
                    placeholder="9.99"
                    disabled={!express.enabled}
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={express.min_days ?? 1}
                    onChange={(e) => updateShipping('express', { min_days: Number(e.target.value) })}
                    disabled={!express.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={express.max_days ?? 2}
                    onChange={(e) => updateShipping('express', { max_days: Number(e.target.value) })}
                    disabled={!express.enabled}
                  />
                </div>
              </div>
            </section>

            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Free shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(free.enabled)}
                  onChange={(e) => updateShipping('free', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={free.label || ''}
                    onChange={(e) => updateShipping('free', { label: e.target.value })}
                    placeholder="Free Shipping"
                    disabled={!free.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Free over (USD)</label>
                  <input
                    value={centsToDollars(free.threshold_cents)}
                    onChange={(e) => updateShipping('free', { threshold_cents: dollarsToCents(e.target.value) ?? free.threshold_cents })}
                    placeholder="20.00"
                    disabled={!free.enabled}
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={free.min_days ?? 5}
                    onChange={(e) => updateShipping('free', { min_days: Number(e.target.value) })}
                    disabled={!free.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={free.max_days ?? 8}
                    onChange={(e) => updateShipping('free', { max_days: Number(e.target.value) })}
                    disabled={!free.enabled}
                  />
                </div>
              </div>
            </section>
          </div>
          </details>

          {(() => {
            const mode = String(form?.storeMode ?? '').trim().toUpperCase();
            if (mode === 'POS') return null;
            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Taxes</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form?.tax?.stripe_automatic_tax_enabled)}
                      onChange={(e) => updateTax({ stripe_automatic_tax_enabled: e.target.checked })}
                    />
                    Enable automatic tax
                  </label>
                  <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem' }}>
                    Requires tax configuration in your payment dashboard (registrations, tax settings).
                  </p>
                </section>
              </details>
            );
          })()}

          <details open style={{ marginTop: 22 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Optional Sections</summary>
            <div style={{ display: 'grid', gap: 18, marginTop: 14 }}>
              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Product features</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.crochet_features?.enabled)}
                    onChange={(e) => updateOptionalCrochetFeatures({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.crochet_features?.title || 'Product features')}
                      onChange={(e) => updateOptionalCrochetFeatures({ title: e.target.value })}
                      placeholder="Product features"
                      disabled={!form?.optional_sections?.crochet_features?.enabled}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>HTML content</label>
                    <textarea
                      rows={7}
                      value={String(form?.optional_sections?.crochet_features?.html || '')}
                      onChange={(e) => updateOptionalCrochetFeatures({ html: e.target.value })}
                      placeholder="Add HTML-enabled feature bullets"
                      disabled={!form?.optional_sections?.crochet_features?.enabled}
                      style={{ width: '100%' }}
                    />
                    <div className="field-hint">Rendered on the demo app home page above Events.</div>
                  </div>
                </div>
              </section>

              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Events</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.events?.enabled)}
                    onChange={(e) => updateOptionalEvents({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.events?.title || 'Events')}
                      onChange={(e) => updateOptionalEvents({ title: e.target.value })}
                      placeholder="Events"
                      disabled={!form?.optional_sections?.events?.enabled}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>HTML content</label>
                    <textarea
                      rows={6}
                      value={String(form?.optional_sections?.events?.html || '')}
                      onChange={(e) => updateOptionalEvents({ html: e.target.value })}
                      placeholder="Paste HTML-enabled events feed content"
                      disabled={!form?.optional_sections?.events?.enabled}
                      style={{ width: '100%' }}
                    />
                    <div className="field-hint">Rendered on the demo app home page below the hero.</div>
                  </div>
                </div>
              </section>

              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Reviews</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.reviews?.enabled)}
                    onChange={(e) => updateOptionalReviews({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.reviews?.title || 'Reviews')}
                      onChange={(e) => updateOptionalReviews({ title: e.target.value })}
                      placeholder="Reviews"
                      disabled={!form?.optional_sections?.reviews?.enabled}
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: 260 }}>
                    <label>Approval mode</label>
                    <select
                      value={Boolean(form?.optional_sections?.reviews?.auto_approve) ? 'auto' : 'admin'}
                      onChange={(e) => updateOptionalReviews({ auto_approve: e.target.value === 'auto' })}
                      disabled={!form?.optional_sections?.reviews?.enabled}
                    >
                      <option value="admin">Admin approval required</option>
                      <option value="auto">Auto-approve after verification</option>
                    </select>
                    <div className="field-hint">Guests must confirm via email (double opt-in). Logged-in users skip email verification.</div>
                  </div>
                </div>
              </section>
            </div>
          </details>

          <div className="general-actions-bar">
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-secondary" onClick={fetchSettings} disabled={saving}>
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
